// CockpitBatimentPage - Le cockpit complet pour piloter ton entreprise du bâtiment
// Version fidèle à l'image de référence validée

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Mic, MapPin, Bot, BarChart3, Shield, Trophy, Headphones, 
  CheckCircle, Play, Rocket, Users, FileText, 
  Calendar, Briefcase, Share2, Receipt, ShoppingCart, FolderOpen,
  TrendingUp, Star, Home, HardHat, Megaphone, ChevronRight, 
  MessageSquare, Settings, LogOut, Store, Plus, Send, Mail, Inbox,
  XCircle, X, Check, ChevronDown, UserPlus, Camera, CheckSquare,
  Heart, MessageCircle, Eye, Image, AtSign, User, ArrowRight,
  Zap, Clock, Bell, Volume2, PiggyBank, Navigation, AlertTriangle,
  Euro, Target, Download, Building, RefreshCw, Cloud, Smile,
  ListChecks, Archive, MailOpen, Activity
} from 'lucide-react';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';
import EspaceProSectionsNav, { EspaceProSectionsNavSpacer } from '../../components/layout/EspaceProSectionsNav';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES - Exactement comme l'image de référence
// ═══════════════════════════════════════════════════════════════════════════════
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@600;700;800;900&display=swap');
    
    .cockpit-hero {
      position: relative;
      overflow: hidden;
      padding: 60px 40px 32px;
      font-family: 'DM Sans', sans-serif;
      color: #1a2a4a;
      background: 
        radial-gradient(ellipse 80% 60% at 65% 40%, rgba(29,78,137,0.5) 0%, transparent 55%),
        radial-gradient(circle at 20% 80%, rgba(255,122,0,0.08) 0%, transparent 40%),
        linear-gradient(165deg, #072B57 0%, #103A6D 35%, #1D4E89 70%, #072B57 100%);
    }
    
    /* Glow orange subtil derrière le cockpit */
    .cockpit-hero::before {
      content: '';
      position: absolute;
      right: 5%;
      top: 55%;
      transform: translateY(-50%);
      width: 700px;
      height: 700px;
      background: radial-gradient(
        circle,
        rgba(249, 115, 22, 0.10) 0%,
        rgba(249, 115, 22, 0.04) 35%,
        rgba(249, 115, 22, 0.02) 55%,
        transparent 75%
      );
      filter: blur(100px);
      z-index: 0;
      pointer-events: none;
    }
    
    /* Texture légère */
    .cockpit-hero::after {
      content: '';
      position: absolute;
      inset: 0;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E");
      opacity: 0.03;
      z-index: 0;
      pointer-events: none;
    }
    
    .cockpit-container {
      max-width: 1440px;
      margin: 0 auto;
      position: relative;
      z-index: 1;
    }
    
    /* Header avec badge */
    .hero-header {
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      margin-bottom: 20px;
      padding-top: 12px;
    }
    
    .hero-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: rgba(249, 115, 22, 0.15);
      border: 1px solid rgba(249, 115, 22, 0.3);
      color: #F97316;
      border-radius: 999px;
      padding: 10px 20px;
      font-weight: 600;
      font-size: 13px;
    }
    
    .hero-badge::before {
      content: '';
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #F97316;
    }
    
    /* Main Grid */
    .hero-main {
      display: grid;
      grid-template-columns: 420px 1fr;
      gap: 48px;
      align-items: flex-start;
      padding-top: 12px;
    }
    
    /* Left Content */
    .hero-content h1 {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: clamp(40px, 5vw, 68px);
      font-weight: 800;
      line-height: 1.1;
      letter-spacing: -0.03em;
      color: #FFFFFF;
      margin: 0 0 20px 0;
    }
    
    .hero-content h1 .line1 {
      display: block;
      white-space: nowrap;
      color: #FFFFFF;
    }
    
    .hero-content h1 span:not(.line1) {
      display: block;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700;
      font-size: clamp(24px, 3vw, 40px);
      color: #F97316;
      margin-top: 16px;
      white-space: nowrap;
    }
    
    .hero-subtitle {
      font-size: clamp(15px, 1.8vw, 18px);
      line-height: 1.7;
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: 28px;
    }
    
    .hero-subtitle strong {
      color: #FFFFFF;
      font-weight: 700;
    }
    
    .hero-highlight {
      display: inline-block;
      background: rgba(249, 115, 22, 0.15);
      border: 1px solid rgba(249, 115, 22, 0.3);
      border-radius: 12px;
      padding: 14px 20px;
      font-size: 16px;
      font-weight: 700;
      color: #FFFFFF;
      margin-bottom: 24px;
      backdrop-filter: blur(8px);
    }
    
    .hero-points {
      display: flex;
      flex-direction: column;
      gap: 18px;
      margin-bottom: 40px;
    }
    
    .hero-point {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 17px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
    }
    
    .hero-point svg {
      color: #F97316;
      flex-shrink: 0;
    }
    
    /* Boutons CTA - Style Boutique */
    .hero-actions {
      display: flex;
      gap: 16px;
      align-items: center;
      margin-top: 24px;
      flex-wrap: wrap;
    }
    
    .cta-primary {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #22C55E;
      color: white;
      border: none;
      border-radius: 12px;
      padding: 16px 28px;
      font-size: 15px;
      font-weight: 700;
      text-decoration: none;
      box-shadow: 0 8px 24px rgba(34,197,94,0.35);
      transition: all 0.2s ease;
      white-space: nowrap;
    }
    
    .cta-primary:hover {
      background: #16A34A;
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(34,197,94,0.42);
      color: white;
    }
    
    .cta-secondary {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #F97316;
      color: white;
      border: none;
      border-radius: 12px;
      padding: 16px 28px;
      font-size: 15px;
      font-weight: 700;
      text-decoration: none;
      box-shadow: 0 8px 24px rgba(249,115,22,0.35);
      transition: all 0.2s ease;
      white-space: nowrap;
    }
    
    .cta-secondary:hover {
      background: #E8650A;
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(249,115,22,0.42);
      color: white;
    }
    
    /* ═══════════════════════════════════════════════════════════════════════════════
       COCKPIT PREVIEW - Ajusté
    ═══════════════════════════════════════════════════════════════════════════════ */
    .cockpit-preview-wrapper {
      padding: 10px 20px;
      margin-top: 20px;
    }
    
    .cockpit-preview {
      display: grid;
      grid-template-columns: 180px 1fr;
      background: white;
      border-radius: 24px;
      overflow: hidden;
      box-shadow: 0 25px 80px rgba(26,42,74,0.12);
      border: 1px solid rgba(37,99,235,0.1);
      transform: scale(0.88);
      transform-origin: top right;
    }
    
    .cockpit-sidebar {
      background: linear-gradient(180deg, #0f1d2f 0%, #162a45 100%);
      padding: 14px 10px;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    
    .cockpit-logo {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px;
      background: rgba(255,255,255,0.05);
      border-radius: 10px;
      margin-bottom: 6px;
    }
    
    .cockpit-logo-icon {
      width: 32px;
      height: 32px;
      background: linear-gradient(135deg, #f97316, #ea580c);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    
    .cockpit-logo-text {
      color: white;
    }
    
    .cockpit-logo-text strong {
      display: block;
      font-size: 13px;
      font-weight: 800;
      white-space: nowrap;
    }
    
    .cockpit-logo-text span {
      display: block;
      font-size: 9px;
      opacity: 0.6;
      white-space: nowrap;
    }
    
    .new-conversation-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 8px 10px;
      background: linear-gradient(135deg, #f97316, #ea580c);
      border-radius: 8px;
      color: white;
      font-size: 11px;
      font-weight: 700;
      margin-bottom: 10px;
      white-space: nowrap;
    }
    
    .sidebar-section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 10px;
      color: rgba(255,255,255,0.9);
      font-size: 11px;
      font-weight: 600;
    }
    
    .sidebar-divider {
      padding: 6px 10px 3px;
      color: rgba(255,255,255,0.4);
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }
    
    .sidebar-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 10px;
      border-radius: 6px;
      color: rgba(255,255,255,0.7);
      font-size: 11px;
      font-weight: 600;
      transition: all 0.2s;
      white-space: nowrap;
    }
    
    .sidebar-item:hover {
      background: rgba(255,255,255,0.05);
    }
    
    .sidebar-item.active {
      background: rgba(249, 115, 22, 0.15);
      color: #f97316;
    }
    
    .sidebar-item svg {
      width: 14px;
      height: 14px;
      opacity: 0.75;
      flex-shrink: 0;
    }
    
    .sidebar-item.active svg {
      opacity: 1;
      color: #f97316;
    }
    
    .sidebar-item-chevron {
      margin-left: auto;
      opacity: 0.5;
    }
    
    .sidebar-bottom {
      margin-top: auto;
      padding-top: 10px;
      border-top: 1px solid rgba(255,255,255,0.08);
    }
    
    .sidebar-bottom-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      color: rgba(255,255,255,0.5);
      font-size: 10px;
      font-weight: 600;
      white-space: nowrap;
    }
    
    .sidebar-logout {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      color: #ef4444;
      font-size: 10px;
      font-weight: 600;
      margin-top: 4px;
    }
    
    .assistant-icon {
      width: 40px;
      height: 40px;
      background: rgba(59,130,246,0.2);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .assistant-icon svg {
      color: #60a5fa;
    }
    
    .assistant-text {
      color: white;
    }
    
    .assistant-text strong {
      display: block;
      font-size: 14px;
      font-weight: 700;
    }
    
    .assistant-text span {
      font-size: 12px;
      color: #4ade80;
    }
    
    .assistant-dot {
      width: 8px;
      height: 8px;
      background: #4ade80;
      border-radius: 50%;
      margin-left: auto;
    }
    
    /* Dashboard */
    .cockpit-dashboard {
      padding: 20px;
      background: #fafbfc;
    }
    
    .dash-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 16px;
    }
    
    .dash-header h3 {
      font-size: 20px;
      font-weight: 800;
      color: #1a2a4a;
      margin: 0;
    }
    
    .dash-header p {
      font-size: 12px;
      color: rgba(26,42,74,0.6);
      margin: 3px 0 0;
    }
    
    .period-selector {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      background: white;
      border: 1px solid rgba(0,0,0,0.08);
      border-radius: 10px;
      font-size: 11px;
      font-weight: 600;
      color: #1a2a4a;
    }
    
    .stats-row {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
      margin-bottom: 16px;
    }
    
    .stat-card {
      background: white;
      border-radius: 12px;
      padding: 14px;
      border: 1px solid rgba(0,0,0,0.04);
      box-shadow: 0 2px 12px rgba(0,0,0,0.03);
    }
    
    .stat-card-label {
      font-size: 10px;
      color: rgba(26,42,74,0.6);
      font-weight: 600;
      margin-bottom: 6px;
      white-space: nowrap;
    }
    
    .stat-card-value {
      font-size: 20px;
      font-weight: 800;
      color: #1a2a4a;
      margin-bottom: 6px;
      white-space: nowrap;
    }
    
    .stat-card-chart {
      height: 28px;
      display: flex;
      align-items: flex-end;
      gap: 2px;
    }
    
    .stat-bar {
      flex: 1;
      background: #e0f2fe;
      border-radius: 2px;
    }
    
    .stat-trend {
      font-size: 11px;
      font-weight: 700;
    }
    
    .stat-trend.positive { color: #16a34a; }
    .stat-trend.negative { color: #dc2626; }
    
    .dash-panels {
      display: grid;
      grid-template-columns: 1fr 1fr 1.1fr;
      gap: 16px;
    }
    
    .panel {
      background: white;
      border-radius: 16px;
      padding: 18px;
      border: 1px solid rgba(0,0,0,0.04);
      box-shadow: 0 2px 12px rgba(0,0,0,0.03);
    }
    
    .panel h4 {
      font-size: 15px;
      font-weight: 800;
      color: #1a2a4a;
      margin: 0 0 14px;
    }
    
    .chantier-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      border-bottom: 1px solid rgba(0,0,0,0.04);
    }
    
    .chantier-item:last-child {
      border-bottom: none;
    }
    
    .chantier-thumb {
      width: 36px;
      height: 36px;
      background: linear-gradient(135deg, #e0f2fe, #bfdbfe);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    
    .chantier-thumb svg {
      width: 16px;
      height: 16px;
      color: #3b82f6;
    }
    
    .chantier-info {
      flex: 1;
      min-width: 0;
    }
    
    .chantier-name {
      font-size: 11px;
      font-weight: 700;
      color: #1a2a4a;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .chantier-location {
      font-size: 9px;
      color: rgba(26,42,74,0.5);
    }
    
    .chantier-status {
      padding: 3px 8px;
      border-radius: 12px;
      font-size: 9px;
      font-weight: 700;
      white-space: nowrap;
      flex-shrink: 0;
    }
    
    .chantier-status.en-cours {
      background: #dcfce7;
      color: #15803d;
    }
    
    .chantier-status.planning {
      background: #fef3c7;
      color: #b45309;
    }
    
    .chantier-status.termine {
      background: #e0e7ff;
      color: #4338ca;
    }
    
    .activity-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 10px 0;
      border-bottom: 1px solid rgba(0,0,0,0.04);
    }
    
    .activity-item:last-child {
      border-bottom: none;
    }
    
    .activity-dot {
      width: 8px;
      height: 8px;
      background: #3b82f6;
      border-radius: 50%;
      margin-top: 6px;
    }
    
    .activity-content {
      flex: 1;
    }
    
    .activity-title {
      font-size: 13px;
      font-weight: 700;
      color: #1a2a4a;
    }
    
    .activity-meta {
      font-size: 12px;
      color: rgba(26,42,74,0.5);
    }
    
    .panel-link {
      display: block;
      margin-top: 12px;
      font-size: 13px;
      font-weight: 700;
      color: #2563eb;
    }
    
    /* Assistant Panel - Style réel du cockpit */
    .assistant-panel {
      background: linear-gradient(180deg, #fef7f0 0%, #fdf4ec 100%);
      border-radius: 14px;
      padding: 14px 12px 12px;
      text-align: center;
      border: 2px solid #2563eb;
      box-shadow: 0 4px 20px rgba(37,99,235,0.15);
      display: flex;
      flex-direction: column;
      min-height: 280px;
    }
    
    .assistant-mic-btn {
      width: 48px;
      height: 48px;
      background: #1e3a5f;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 10px;
    }
    
    .assistant-mic-btn svg {
      color: white;
    }
    
    .assistant-greeting {
      font-size: 14px;
      font-weight: 800;
      color: #1a2a4a;
      margin: 0 0 3px;
    }
    
    .assistant-subtitle {
      font-size: 9px;
      color: rgba(26,42,74,0.5);
      margin-bottom: 10px;
      line-height: 1.4;
    }
    
    .actions-divider {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }
    
    .actions-divider::before,
    .actions-divider::after {
      content: '';
      flex: 1;
      height: 1px;
      background: rgba(26,42,74,0.1);
    }
    
    .actions-divider span {
      font-size: 8px;
      color: rgba(26,42,74,0.4);
      font-weight: 600;
      white-space: nowrap;
    }
    
    .actions-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px;
      margin-bottom: 12px;
    }
    
    .action-card {
      padding: 10px 8px;
      border-radius: 10px;
      text-align: center;
      border: 1px solid transparent;
    }
    
    .action-card.peach { background: #fef3e8; border-color: rgba(249,115,22,0.15); }
    .action-card.green { background: #ecfdf5; border-color: rgba(16,185,129,0.15); }
    .action-card.blue { background: #eff6ff; border-color: rgba(59,130,246,0.15); }
    .action-card.pink { background: #fdf2f8; border-color: rgba(236,72,153,0.15); }
    
    .action-card-icon {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 6px;
    }
    
    .action-card-icon.peach { background: #fed7aa; }
    .action-card-icon.green { background: #a7f3d0; }
    .action-card-icon.blue { background: #bfdbfe; }
    .action-card-icon.pink { background: #fbcfe8; }
    
    .action-card-icon svg {
      width: 14px;
      height: 14px;
    }
    
    .action-card-title {
      font-size: 9px;
      font-weight: 700;
      color: #1a2a4a;
    }
    
    .chat-input-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      background: white;
      border-radius: 8px;
      padding: 8px 10px;
      border: 1px solid rgba(0,0,0,0.1);
      margin-top: auto;
    }
    
    .chat-input-field {
      flex: 1;
      font-size: 10px;
      color: rgba(26,42,74,0.4);
      border: none;
      background: transparent;
    }
    
    .chat-send-btn {
      width: 26px;
      height: 26px;
      background: linear-gradient(135deg, #f97316, #ea580c);
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    
    .chat-send-btn svg {
      color: white;
      width: 12px;
      height: 12px;
    }
    
    /* ═══════════════════════════════════════════════════════════════════════════════
       FEATURE STRIP
    ═══════════════════════════════════════════════════════════════════════════════ */
    .feature-strip {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 20px;
      margin-top: 20px;
      padding: 24px 28px;
      background: white;
      border-radius: 20px;
      box-shadow: 0 8px 40px rgba(0,0,0,0.06);
    }
    
    .feature-item {
      display: flex;
      gap: 14px;
      align-items: flex-start;
    }
    
    .feature-icon {
      width: 48px;
      height: 48px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    
    .feature-icon.blue { background: linear-gradient(135deg, #dbeafe, #bfdbfe); }
    .feature-icon.green { background: linear-gradient(135deg, #dcfce7, #bbf7d0); }
    .feature-icon.purple { background: linear-gradient(135deg, #ede9fe, #ddd6fe); }
    .feature-icon.orange { background: linear-gradient(135deg, #ffedd5, #fed7aa); }
    .feature-icon.pink { background: linear-gradient(135deg, #fce7f3, #fbcfe8); }
    
    .feature-icon svg {
      width: 24px;
      height: 24px;
    }
    
    .feature-icon.blue svg { color: #2563eb; }
    .feature-icon.green svg { color: #16a34a; }
    .feature-icon.purple svg { color: #7c3aed; }
    .feature-icon.orange svg { color: #ea580c; }
    .feature-icon.pink svg { color: #db2777; }
    
    .feature-text strong {
      display: block;
      font-size: 15px;
      font-weight: 800;
      color: #1a2a4a;
      margin-bottom: 4px;
    }
    
    .feature-text span {
      font-size: 13px;
      color: rgba(26,42,74,0.6);
      line-height: 1.4;
    }
    
    /* ═══════════════════════════════════════════════════════════════════════════════
       TRUST STRIP
    ═══════════════════════════════════════════════════════════════════════════════ */
    .trust-strip {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 32px;
      margin-top: 24px;
      padding: 28px 40px;
      background: linear-gradient(135deg, #1e3a5f 0%, #1e3a5f 100%);
      border-radius: 20px;
      color: white;
    }
    
    .trust-item {
      display: flex;
      gap: 16px;
      align-items: flex-start;
    }
    
    .trust-icon {
      width: 48px;
      height: 48px;
      border-radius: 14px;
      background: rgba(255,255,255,0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    
    .trust-icon svg {
      width: 24px;
      height: 24px;
      color: #fbbf24;
    }
    
    .trust-text strong {
      display: block;
      font-size: 15px;
      font-weight: 800;
      margin-bottom: 4px;
    }
    
    .trust-text span {
      font-size: 13px;
      opacity: 0.75;
      line-height: 1.4;
    }
    
    /* ═══════════════════════════════════════════════════════════════════════════════
       RESPONSIVE
    ═══════════════════════════════════════════════════════════════════════════════ */
    @media (max-width: 1280px) {
      .hero-main {
        grid-template-columns: 1fr;
        gap: 32px;
      }
      
      .hero-content {
        text-align: center;
      }
      
      .hero-points {
        align-items: center;
      }
      
      .hero-actions {
        justify-content: center;
      }
      
      .cockpit-preview {
        max-width: 900px;
        margin: 0 auto;
      }
      
      .trust-badges {
        display: none;
      }
    }
    
    @media (max-width: 1024px) {
      .feature-strip {
        grid-template-columns: repeat(3, 1fr);
      }
      
      .trust-strip {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    
    @media (max-width: 768px) {
      .cockpit-hero {
        padding: 24px 16px;
      }
      
      .hero-content h1 {
        font-size: 40px;
      }
      
      .cockpit-preview {
        grid-template-columns: 1fr;
      }
      
      .cockpit-sidebar {
        flex-direction: row;
        flex-wrap: wrap;
        padding: 16px;
      }
      
      .cockpit-logo {
        width: 100%;
        margin-bottom: 8px;
      }
      
      .sidebar-item {
        flex: 1 1 45%;
      }
      
      .sidebar-assistant {
        width: 100%;
        margin-top: 12px;
      }
      
      .stats-row {
        grid-template-columns: repeat(2, 1fr);
      }
      
      .dash-panels {
        grid-template-columns: 1fr;
      }
      
      .feature-strip {
        grid-template-columns: 1fr;
        gap: 20px;
      }
      
      .trust-strip {
        grid-template-columns: 1fr;
        gap: 20px;
      }
      
      .hero-actions {
        flex-direction: column;
      }
      
      .cta-primary, .cta-secondary {
        width: 100%;
      }
    }
    
    /* ═══════════════════════════════════════════════════════════════════════════════
       OVERRIDES FINAUX - Boutons allongés + Feature strip remontée
    ═══════════════════════════════════════════════════════════════════════════════ */
    
    /* === BOUTONS DESKTOP === */
    @media (min-width: 769px) {
      .hero-actions {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        gap: 16px !important;
        flex-wrap: nowrap !important;
        margin-top: 24px !important;
        margin-bottom: 100px !important;
        position: relative !important;
        z-index: 15 !important;
      }
      
      .hero-actions .cta-primary {
        min-width: 220px !important;
        padding: 12px 20px !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        border-radius: 14px !important;
      }
      
      .hero-actions .cta-primary .cta-primary-text strong {
        white-space: nowrap !important;
      }
      
      .hero-actions .cta-secondary {
        min-width: 160px !important;
        padding: 12px 20px !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        border-radius: 14px !important;
      }
      
      .hero-actions .cta-secondary .cta-secondary-text strong {
        white-space: nowrap !important;
      }
    }
    
    /* === COCKPIT PREVIEW - Alignement avec les boutons === */
    .cockpit-preview-wrapper {
      margin-bottom: 40px !important;
      position: relative !important;
      margin-top: -80px !important;
    }
    
    /* === HALO SUBTIL DERRIERE LE COCKPIT === */
    .cockpit-preview-wrapper::before {
      content: "" !important;
      position: absolute !important;
      inset: -30px !important;
      background: radial-gradient(circle at center, rgba(59, 130, 246, 0.12), transparent 65%) !important;
      z-index: 0 !important;
      pointer-events: none !important;
      border-radius: 50px !important;
    }
    
    /* === COCKPIT PREMIUM STYLING - Carte flottante === */
    .cockpit-preview {
      position: relative !important;
      z-index: 1 !important;
      border-radius: 24px !important;
      overflow: hidden !important;
      box-shadow: 
        0 25px 60px rgba(30, 58, 95, 0.15),
        0 10px 30px rgba(30, 58, 95, 0.08) !important;
    }
    
    /* === FEATURE STRIP - Remontée === */
    .feature-strip {
      margin-top: -80px !important;
      position: relative !important;
      z-index: 10 !important;
      background: linear-gradient(180deg, #FFF8F4 0%, #FFFFFF 100%) !important;
      border: 1px solid rgba(249, 115, 22, 0.12) !important;
      border-radius: 20px !important;
      padding: 24px 28px !important;
      box-shadow: 0 20px 40px rgba(0,0,0,0.08) !important;
    }
    
    /* ═══════════════════════════════════════════════════════════════════════════════
       BLOC 2 - POURQUOI LE COCKPIT
    ═══════════════════════════════════════════════════════════════════════════════ */
    .cockpit-block-2 {
      background:
        radial-gradient(circle at 20% 20%, rgba(249, 115, 22, 0.08), transparent 28%),
        linear-gradient(180deg, #FFF8F4 0%, #FFFFFF 100%);
      padding: 90px 24px;
      font-family: 'DM Sans', sans-serif;
      color: #1E3A5F;
    }

    .cb2-container {
      max-width: 1480px;
      margin: 0 auto;
      display: grid;
      grid-template-columns: 0.78fr 1.22fr;
      gap: 54px;
      align-items: flex-start;
    }

    .cb2-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 22px;
      border-radius: 999px;
      border: 1px solid rgba(249, 115, 22, 0.22);
      background: rgba(249, 115, 22, 0.08);
      color: #E8650A;
      font-weight: 900;
      font-size: 14px;
      letter-spacing: 0.02em;
      margin-bottom: 28px;
    }

    .cb2-title {
      margin: 0;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: clamp(36px, 4vw, 60px);
      line-height: 1.08;
      letter-spacing: -0.045em;
      font-weight: 900;
      color: #1E3A5F;
    }

    .cb2-title span {
      color: #F97316;
    }

    .cb2-text {
      max-width: 620px;
      margin: 28px 0 24px;
      font-size: 19px;
      line-height: 1.55;
      color: rgba(30, 58, 95, 0.78);
    }

    .cb2-text strong {
      color: #1E3A5F;
      font-weight: 900;
    }

    .cb2-callout {
      max-width: 620px;
      display: flex;
      gap: 18px;
      align-items: center;
      padding: 22px;
      border: 1px solid rgba(249, 115, 22, 0.22);
      border-radius: 22px;
      background: rgba(255, 255, 255, 0.74);
      box-shadow: 0 18px 55px rgba(30, 58, 95, 0.08);
      margin-bottom: 28px;
    }

    .cb2-callout-icon {
      width: 58px;
      height: 58px;
      border-radius: 18px;
      background: rgba(249, 115, 22, 0.12);
      color: #F97316;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 26px;
      flex-shrink: 0;
    }

    .cb2-callout p {
      margin: 0 0 6px;
      color: rgba(30, 58, 95, 0.72);
      line-height: 1.45;
      font-size: 15px;
    }

    .cb2-callout strong {
      color: #E8650A;
      font-weight: 900;
    }

    .cb2-stats {
      max-width: 620px;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      background: #FFFFFF;
      border-radius: 24px;
      border: 1px solid rgba(30, 58, 95, 0.08);
      box-shadow: 0 22px 70px rgba(30, 58, 95, 0.09);
      overflow: hidden;
    }

    .cb2-stats > div {
      padding: 26px 14px;
      text-align: center;
      border-right: 1px solid rgba(30, 58, 95, 0.09);
    }

    .cb2-stats > div:last-child {
      border-right: none;
    }

    .cb2-stats strong {
      display: block;
      color: #F97316;
      font-size: 26px;
      line-height: 1.05;
      font-weight: 900;
      margin-bottom: 10px;
    }

    .cb2-stats span {
      display: block;
      font-size: 13px;
      line-height: 1.45;
      color: rgba(30, 58, 95, 0.75);
      font-weight: 700;
    }

    .cb2-right {
      background: rgba(255,255,255,0.86);
      border: 1px solid rgba(30, 58, 95, 0.08);
      border-radius: 32px;
      box-shadow: 0 28px 90px rgba(30, 58, 95, 0.12);
      padding: 34px;
    }

    .cb2-right-header {
      margin-bottom: 26px;
    }

    .cb2-right-header h3 {
      margin: 0 0 8px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 28px;
      line-height: 1.16;
      font-weight: 900;
      letter-spacing: -0.03em;
      color: #1E3A5F;
    }

    .cb2-right-header p {
      margin: 0;
      font-size: 16px;
      color: rgba(30, 58, 95, 0.68);
    }

    .cb2-features-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 18px;
    }

    .cb2-card {
      background: #FFFFFF;
      border: 1px solid rgba(30, 58, 95, 0.08);
      border-radius: 22px;
      padding: 22px;
      min-height: 265px;
      box-shadow: 0 14px 40px rgba(30, 58, 95, 0.07);
    }

    .cb2-card-icon {
      width: 52px;
      height: 52px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 23px;
      margin-bottom: 14px;
    }

    .cb2-card-icon.blue { background: rgba(59,130,246,0.12); }
    .cb2-card-icon.green { background: rgba(34,197,94,0.12); }
    .cb2-card-icon.orange { background: rgba(249,115,22,0.12); }
    .cb2-card-icon.yellow { background: rgba(245,158,11,0.12); }
    .cb2-card-icon.red { background: rgba(239,68,68,0.10); }
    .cb2-card-icon.purple { background: rgba(124,58,237,0.12); }

    .cb2-card h4 {
      margin: 0 0 8px;
      color: #1E3A5F;
      font-size: 17px;
      font-weight: 900;
    }

    .cb2-card > p {
      margin: 0 0 16px;
      font-size: 13px;
      line-height: 1.45;
      color: rgba(30, 58, 95, 0.66);
    }

    .cb2-mini-ui {
      border: 1px solid rgba(30, 58, 95, 0.08);
      border-radius: 16px;
      padding: 14px;
      background: #FAFCFF;
    }

    .cb2-ui-title {
      font-size: 12px;
      font-weight: 900;
      color: #1E3A5F;
      margin-bottom: 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .cb2-ui-title span {
      color: #22C55E;
      font-size: 10px;
    }

    .cb2-voice {
      border: 1px solid rgba(249, 115, 22, 0.22);
      background: rgba(249, 115, 22, 0.06);
      color: #1E3A5F;
      border-radius: 12px;
      padding: 10px;
      font-size: 12px;
      margin-bottom: 10px;
    }

    .cb2-price {
      font-size: 18px;
      font-weight: 900;
      color: #1E3A5F;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .cb2-price span {
      font-size: 10px;
      color: #22C55E;
      background: rgba(34,197,94,0.12);
      padding: 4px 8px;
      border-radius: 999px;
      font-weight: 800;
    }

    .cb2-mini-ui ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .cb2-mini-ui li {
      font-size: 12px;
      color: rgba(30, 58, 95, 0.78);
      padding: 5px 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .cb2-mini-ui li::before {
      content: "✓";
      color: #22C55E;
      font-weight: 900;
    }

    .cb2-progress {
      height: 8px;
      background: rgba(30,58,95,0.10);
      border-radius: 99px;
      overflow: hidden;
      margin: 10px 0 12px;
    }

    .cb2-progress i {
      display: block;
      width: 68%;
      height: 100%;
      background: #F97316;
      border-radius: 99px;
    }

    .cb2-thumbs {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 6px;
    }

    .cb2-thumbs span {
      height: 36px;
      border-radius: 8px;
      background: linear-gradient(135deg, #e8edf5, #cbd5e1);
    }

    .cb2-team-row,
    .cb2-reminder {
      display: flex;
      justify-content: space-between;
      border-bottom: 1px solid rgba(30,58,95,0.07);
      padding: 8px 0;
      font-size: 12px;
      color: #1E3A5F;
    }

    .cb2-team-row:last-child,
    .cb2-reminder:last-child {
      border-bottom: none;
    }

    .cb2-team-row strong {
      color: #22C55E;
    }

    .cb2-reminder em {
      font-style: normal;
      color: #E8650A;
      font-weight: 800;
      font-size: 11px;
    }

    .cb2-ai-bubble {
      background: linear-gradient(135deg, #F97316, #E8650A);
      color: white;
      border-radius: 14px 14px 4px 14px;
      padding: 12px;
      font-size: 12px;
      font-weight: 800;
      margin-bottom: 10px;
    }

    .cb2-ai-answer {
      background: #FAFCFF;
      border: 1px solid rgba(30,58,95,0.08);
      border-radius: 14px;
      padding: 12px;
      font-size: 12px;
      line-height: 1.55;
      color: rgba(30,58,95,0.82);
    }

    .cb2-bottom {
      margin-top: 22px;
      padding: 20px;
      border-radius: 20px;
      background: rgba(249,115,22,0.07);
      border: 1px solid rgba(249,115,22,0.18);
      display: flex;
      justify-content: space-between;
      gap: 18px;
      align-items: center;
    }

    .cb2-bottom strong {
      display: block;
      font-size: 15px;
      color: #1E3A5F;
      font-weight: 900;
    }

    .cb2-bottom > div span {
      color: rgba(30,58,95,0.68);
      font-size: 14px;
    }

    .cb2-bottom a {
      flex-shrink: 0;
      background: linear-gradient(135deg, #F97316, #E8650A);
      color: white;
      text-decoration: none;
      padding: 15px 22px;
      border-radius: 14px;
      font-weight: 900;
      font-size: 14px;
      box-shadow: 0 16px 40px rgba(249, 115, 22, 0.22);
      transition: transform 0.2s, box-shadow 0.2s;
    }

    .cb2-bottom a:hover {
      transform: translateY(-2px);
      box-shadow: 0 20px 50px rgba(249, 115, 22, 0.28);
    }

    @media (max-width: 1180px) {
      .cb2-container {
        grid-template-columns: 1fr;
      }

      .cb2-left,
      .cb2-text,
      .cb2-callout,
      .cb2-stats {
        max-width: none;
      }
    }

    @media (max-width: 860px) {
      .cockpit-block-2 {
        padding: 70px 16px;
      }

      .cb2-features-grid {
        grid-template-columns: 1fr;
      }

      .cb2-stats {
        grid-template-columns: repeat(2, 1fr);
      }

      .cb2-stats > div:nth-child(2) {
        border-right: none;
      }

      .cb2-bottom {
        flex-direction: column;
        align-items: flex-start;
      }

      .cb2-bottom a {
        width: 100%;
        text-align: center;
      }
    }

    @media (max-width: 480px) {
      .cb2-title {
        font-size: 32px;
      }

      .cb2-text {
        font-size: 16px;
      }

      .cb2-stats {
        grid-template-columns: 1fr;
      }

      .cb2-stats > div {
        border-right: none;
        border-bottom: 1px solid rgba(30, 58, 95, 0.09);
      }

      .cb2-stats > div:last-child {
        border-bottom: none;
      }
    }

    /* ═══════════════════════════════════════════════════════════════════════════════
       CB2-BOTTOM-LEFT - Bloc unifié en bas à gauche
    ═══════════════════════════════════════════════════════════════════════════════ */
    .cb2-bottom-left {
      margin-top: 28px;
      padding: 20px;
      border-radius: 20px;
      background: rgba(249,115,22,0.07);
      border: 1px solid rgba(249,115,22,0.18);
      display: flex;
      flex-direction: column;
      gap: 12px;
      animation: cb2FadeSlideIn 0.6s ease-out;
    }

    @keyframes cb2FadeSlideIn {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .cb2-bottom-left:hover {
      border-color: rgba(249,115,22,0.35);
      box-shadow: 0 12px 35px rgba(249, 115, 22, 0.12);
      transition: all 0.3s ease;
    }

    .cb2-bottom-left strong {
      display: block;
      font-size: 15px;
      color: #1E3A5F;
      font-weight: 900;
    }

    .cb2-bottom-left > div span {
      display: block;
      color: rgba(30,58,95,0.68);
      font-size: 14px;
      margin-top: 4px;
    }

    .cb2-bottom-left a {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: linear-gradient(135deg, #F97316, #E8650A);
      color: white;
      text-decoration: none;
      padding: 14px 22px;
      border-radius: 14px;
      font-weight: 900;
      font-size: 14px;
      box-shadow: 0 16px 40px rgba(249, 115, 22, 0.22);
      transition: transform 0.2s, box-shadow 0.2s;
      width: fit-content;
    }

    .cb2-bottom-left a:hover {
      transform: translateY(-2px);
      box-shadow: 0 20px 50px rgba(249, 115, 22, 0.28);
    }

    @media (max-width: 860px) {
      .cb2-bottom-left a {
        width: 100%;
        justify-content: center;
        text-align: center;
      }
    }

    /* ═══════════════════════════════════════════════════════════════════════════════
       FOUNDER BANNER - Bandeau premium fondateur
    ═══════════════════════════════════════════════════════════════════════════════ */
    .founder-banner {
      width: 100%;
      padding: 50px 0;
      background: linear-gradient(135deg, #0f2a44 0%, #1e3a5f 50%, #0f2a44 100%);
      border-radius: 28px;
      margin: 30px auto;
      max-width: 1400px;
      position: relative;
      overflow: hidden;
    }

    .founder-banner::before {
      content: '';
      position: absolute;
      right: 80px;
      bottom: 20px;
      width: 120px;
      height: 60px;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Cpath d='M10 40 Q30 10 50 30 T90 20' stroke='rgba(255,255,255,0.08)' stroke-width='2' fill='none'/%3E%3C/svg%3E") no-repeat center;
      background-size: contain;
      opacity: 0.6;
    }

    .founder-container {
      max-width: 1200px;
      margin: 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 60px;
      padding: 0 50px;
    }

    .founder-left {
      flex-shrink: 0;
      position: relative;
      margin-right: 20px;
    }

    .founder-photo {
      width: 180px;
      height: 180px;
      border-radius: 50%;
      object-fit: cover;
      object-position: center top;
      border: 4px solid rgba(255,255,255,0.2);
      box-shadow: 0 20px 50px rgba(0,0,0,0.3);
    }

    .founder-trophy {
      position: absolute;
      top: -5px;
      right: -15px;
      width: 50px;
      height: 50px;
      background: rgba(15, 42, 68, 0.9);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid rgba(249, 115, 22, 0.3);
    }

    .founder-trophy svg {
      color: #F97316;
      width: 28px;
      height: 28px;
    }

    .founder-center {
      flex: 1;
      color: white;
    }

    .founder-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 32px;
      font-weight: 800;
      line-height: 1.2;
      margin-bottom: 20px;
      letter-spacing: -0.02em;
      color: #FFFFFF;
    }

    .founder-name {
      font-size: 20px;
      font-weight: 800;
      color: #F97316;
      margin-bottom: 8px;
    }

    .founder-sub {
      font-size: 16px;
      color: rgba(255,255,255,0.75);
      line-height: 1.6;
    }

    .founder-right {
      flex-shrink: 0;
    }

    .founder-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      border: 2px solid #F97316;
      color: #F97316;
      background: transparent;
      padding: 16px 28px;
      border-radius: 14px;
      text-decoration: none;
      font-weight: 700;
      font-size: 15px;
      transition: all 0.25s ease;
      white-space: nowrap;
    }

    .founder-btn:hover {
      background: #F97316;
      color: white;
      transform: translateY(-2px);
      box-shadow: 0 12px 30px rgba(249, 115, 22, 0.35);
    }

    @media (max-width: 1024px) {
      .founder-container {
        flex-direction: column;
        text-align: center;
        gap: 30px;
        padding: 0 30px;
      }

      .founder-title {
        font-size: 26px;
      }

      .founder-photo {
        width: 120px;
        height: 120px;
      }

      .founder-trophy {
        right: -10px;
      }
    }

    @media (max-width: 640px) {
      .founder-banner {
        border-radius: 20px;
        padding: 40px 0;
        margin: 40px 16px;
      }

      .founder-title {
        font-size: 22px;
      }

      .founder-btn {
        width: 100%;
        justify-content: center;
      }
    }

    /* ═══════════════════════════════════════════════════════════════════════════════
       COLLABORATION SECTION - Section premium Collaboration
    ═══════════════════════════════════════════════════════════════════════════════ */
    .collab-section {
      padding: 120px 24px;
      background: linear-gradient(180deg, #FAFBFC 0%, #F8FAFC 100%);
    }

    .collab-container {
      max-width: 1400px;
      margin: 0 auto;
      display: flex;
      gap: 60px;
      align-items: flex-start;
    }

    .collab-left {
      flex: 0 0 420px;
      max-width: 420px;
    }

    .collab-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #FFF4ED;
      color: #F97316;
      padding: 8px 16px;
      border-radius: 999px;
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 24px;
    }

    .collab-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 42px;
      font-weight: 800;
      color: #1E293B;
      line-height: 1.15;
      margin-bottom: 8px;
    }

    .collab-subtitle {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 42px;
      font-weight: 800;
      color: #F97316;
      line-height: 1.15;
      margin-bottom: 24px;
    }

    .collab-desc {
      font-size: 16px;
      line-height: 1.7;
      color: #64748B;
      margin-bottom: 32px;
    }

    .collab-desc strong {
      color: #1E293B;
      font-weight: 600;
    }

    /* Comparison Cards */
    .collab-comparison {
      display: flex;
      gap: 16px;
      position: relative;
    }

    .collab-comparison::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 40px;
      height: 40px;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'%3E%3Cpath stroke='%2364748B' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M7 17L17 7M17 7H7M17 7v10'/%3E%3C/svg%3E") no-repeat center;
      background-size: 28px;
      z-index: 2;
    }

    .collab-card {
      flex: 1;
      background: white;
      border-radius: 16px;
      padding: 20px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.04);
      border: 1px solid rgba(0,0,0,0.04);
    }

    .collab-card-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 15px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 14px;
    }

    .collab-card-title.avant svg {
      color: #EF4444;
    }

    .collab-card-title.avec svg {
      color: #22C55E;
    }

    .collab-card ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .collab-card li {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: #64748B;
      padding: 6px 0;
    }

    .collab-card li svg {
      flex-shrink: 0;
    }

    .collab-card.avant li svg {
      color: #EF4444;
    }

    .collab-card.avec li svg {
      color: #22C55E;
    }

    /* Right Column - Mockup Interface */
    .collab-right {
      flex: 1;
      min-width: 0;
    }

    .collab-mockup {
      background: white;
      border-radius: 20px;
      box-shadow: 0 25px 80px rgba(0,0,0,0.08);
      overflow: hidden;
      border: 1px solid rgba(0,0,0,0.06);
    }

    .collab-mockup-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid #F1F5F9;
    }

    .collab-mockup-title {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 15px;
      font-weight: 700;
      color: #1E293B;
    }

    .collab-mockup-title svg {
      color: #64748B;
    }

    .collab-header-right {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .collab-avatars {
      display: flex;
      align-items: center;
    }

    .collab-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 2px solid white;
      margin-left: -8px;
      object-fit: cover;
      background: linear-gradient(135deg, #F97316, #FB923C);
    }

    .collab-avatar:first-child {
      margin-left: 0;
    }

    .collab-avatar-more {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 2px solid white;
      margin-left: -8px;
      background: #F1F5F9;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 600;
      color: #64748B;
    }

    .collab-invite-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      background: #F97316;
      color: white;
      padding: 8px 14px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      border: none;
      cursor: pointer;
    }

    /* Tabs */
    .collab-tabs {
      display: flex;
      gap: 4px;
      padding: 0 20px;
      border-bottom: 1px solid #F1F5F9;
    }

    .collab-tab {
      padding: 12px 16px;
      font-size: 13px;
      font-weight: 500;
      color: #64748B;
      border-bottom: 2px solid transparent;
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .collab-tab:hover {
      color: #1E293B;
    }

    .collab-tab.active {
      color: #F97316;
      border-bottom-color: #F97316;
      font-weight: 600;
    }

    /* Main Content Area */
    .collab-main {
      display: flex;
    }

    /* Sidebar Left */
    .collab-sidebar-left {
      width: 200px;
      border-right: 1px solid #F1F5F9;
      padding: 16px;
      flex-shrink: 0;
    }

    .collab-sidebar-section {
      margin-bottom: 20px;
    }

    .collab-sidebar-title {
      font-size: 11px;
      font-weight: 700;
      color: #94A3B8;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .collab-feed-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border-radius: 8px;
      font-size: 13px;
      color: #64748B;
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .collab-feed-item:hover {
      background: #F8FAFC;
    }

    .collab-feed-item.active {
      background: #FFF7ED;
      color: #F97316;
      font-weight: 600;
    }

    .collab-feed-item svg {
      width: 16px;
      height: 16px;
    }

    .collab-feed-dot {
      width: 8px;
      height: 8px;
      border-radius: 2px;
    }

    .collab-feed-dot.orange { background: #F97316; }
    .collab-feed-dot.blue { background: #3B82F6; }
    .collab-feed-dot.green { background: #22C55E; }
    .collab-feed-dot.purple { background: #8B5CF6; }

    /* Feed Center */
    .collab-feed-center {
      flex: 1;
      min-width: 0;
      padding: 16px;
      max-height: 500px;
      overflow-y: auto;
    }

    .collab-input-box {
      background: #F8FAFC;
      border-radius: 12px;
      padding: 14px;
      margin-bottom: 16px;
    }

    .collab-input-placeholder {
      display: flex;
      align-items: center;
      gap: 10px;
      color: #94A3B8;
      font-size: 13px;
      margin-bottom: 12px;
    }

    .collab-input-placeholder img {
      width: 28px;
      height: 28px;
      border-radius: 50%;
    }

    .collab-input-actions {
      display: flex;
      gap: 8px;
      padding-left: 38px;
    }

    .collab-input-action {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #64748B;
      padding: 6px 10px;
      border-radius: 6px;
      background: white;
      border: 1px solid #E2E8F0;
      cursor: pointer;
    }

    .collab-input-action svg {
      width: 14px;
      height: 14px;
    }

    /* Posts */
    .collab-post {
      background: white;
      border: 1px solid #F1F5F9;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 12px;
    }

    .collab-post-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 12px;
    }

    .collab-post-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      object-fit: cover;
    }

    .collab-post-info {
      flex: 1;
    }

    .collab-post-name {
      font-size: 13px;
      font-weight: 600;
      color: #1E293B;
    }

    .collab-post-role {
      font-size: 12px;
      color: #94A3B8;
    }

    .collab-post-time {
      font-size: 11px;
      color: #94A3B8;
    }

    .collab-post-content {
      font-size: 13px;
      color: #475569;
      line-height: 1.5;
      margin-bottom: 12px;
    }

    .collab-post-images {
      display: flex;
      gap: 8px;
      margin-bottom: 12px;
    }

    .collab-post-img {
      width: 80px;
      height: 60px;
      border-radius: 8px;
      object-fit: cover;
    }

    .collab-post-reactions {
      display: flex;
      align-items: center;
      gap: 16px;
      font-size: 12px;
      color: #94A3B8;
    }

    .collab-post-reactions span {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    /* Task Post */
    .collab-task {
      background: #FFFBEB;
      border: 1px solid #FEF3C7;
      border-radius: 12px;
      padding: 14px;
      margin-bottom: 12px;
    }

    .collab-task-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .collab-task-badge {
      display: flex;
      align-items: center;
      gap: 4px;
      background: #FEF3C7;
      color: #B45309;
      padding: 4px 10px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 600;
    }

    .collab-task-time {
      font-size: 11px;
      color: #94A3B8;
    }

    .collab-task-title {
      font-size: 13px;
      font-weight: 600;
      color: #1E293B;
      margin-bottom: 6px;
    }

    .collab-task-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 12px;
      color: #64748B;
    }

    .collab-task-deadline {
      color: #EF4444;
      font-weight: 600;
    }

    /* Event Post */
    .collab-event {
      background: #F0FDF4;
      border: 1px solid #BBF7D0;
      border-radius: 12px;
      padding: 14px;
      margin-bottom: 12px;
    }

    .collab-event-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .collab-event-badge {
      display: flex;
      align-items: center;
      gap: 4px;
      background: #BBF7D0;
      color: #166534;
      padding: 4px 10px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 600;
    }

    .collab-event-title {
      font-size: 13px;
      font-weight: 600;
      color: #1E293B;
      margin-bottom: 4px;
    }

    .collab-event-date {
      font-size: 12px;
      color: #64748B;
    }

    .collab-event-btn {
      display: inline-block;
      margin-top: 8px;
      background: #22C55E;
      color: white;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
    }

    /* Comment */
    .collab-comment {
      background: white;
      border: 1px solid #F1F5F9;
      border-radius: 12px;
      padding: 14px;
      margin-bottom: 12px;
    }

    .collab-comment-badge {
      display: inline-block;
      background: #DBEAFE;
      color: #1D4ED8;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      margin-left: 8px;
    }

    .collab-fournisseur-badge {
      display: inline-block;
      background: #F3E8FF;
      color: #7C3AED;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      margin-left: 8px;
    }

    /* Sidebar Right */
    .collab-sidebar-right {
      width: 220px;
      border-left: 1px solid #F1F5F9;
      padding: 16px;
      flex-shrink: 0;
    }

    .collab-about-box {
      background: #F8FAFC;
      border-radius: 10px;
      padding: 12px;
      margin-bottom: 16px;
    }

    .collab-about-title {
      font-size: 12px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 6px;
    }

    .collab-about-text {
      font-size: 11px;
      color: #64748B;
      line-height: 1.4;
    }

    .collab-members-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }

    .collab-members-title {
      font-size: 12px;
      font-weight: 700;
      color: #1E293B;
    }

    .collab-members-link {
      font-size: 11px;
      color: #F97316;
      font-weight: 500;
    }

    .collab-members-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 10px;
    }

    .collab-member-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
    }

    .collab-manage-btn {
      width: 100%;
      padding: 8px;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      background: white;
      font-size: 12px;
      color: #64748B;
      cursor: pointer;
      margin-bottom: 16px;
    }

    .collab-files-list {
      margin-bottom: 16px;
    }

    .collab-file-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      border-bottom: 1px solid #F1F5F9;
    }

    .collab-file-item:last-child {
      border-bottom: none;
    }

    .collab-file-icon {
      width: 28px;
      height: 28px;
      border-radius: 6px;
      background: #FEE2E2;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .collab-file-icon.pdf { background: #FEE2E2; color: #DC2626; }
    .collab-file-icon.img { background: #DBEAFE; color: #2563EB; }

    .collab-file-info {
      flex: 1;
      min-width: 0;
    }

    .collab-file-name {
      font-size: 11px;
      font-weight: 600;
      color: #1E293B;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .collab-file-meta {
      font-size: 10px;
      color: #94A3B8;
    }

    .collab-see-all-btn {
      width: 100%;
      padding: 8px;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      background: white;
      font-size: 12px;
      color: #64748B;
      cursor: pointer;
      margin-bottom: 16px;
    }

    .collab-next-event {
      background: #FFF7ED;
      border-radius: 10px;
      padding: 12px;
    }

    .collab-next-event-title {
      font-size: 12px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 8px;
    }

    .collab-next-event-item {
      display: flex;
      align-items: flex-start;
      gap: 8px;
    }

    .collab-next-event-icon {
      width: 28px;
      height: 28px;
      border-radius: 6px;
      background: #FFEDD5;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #F97316;
      flex-shrink: 0;
    }

    .collab-next-event-name {
      font-size: 12px;
      font-weight: 600;
      color: #1E293B;
    }

    .collab-next-event-date {
      font-size: 11px;
      color: #64748B;
    }

    /* Features Bar - PLEINE LARGEUR */
    .collab-features-bar {
      background: #FFF7ED;
      border-radius: 20px;
      padding: 32px 60px;
      margin-top: 60px;
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 32px;
      width: 100%;
      max-width: 1400px;
      margin-left: auto;
      margin-right: auto;
    }

    .collab-feature {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 10px;
    }

    .collab-feature-icon {
      width: 48px;
      height: 48px;
      border-radius: 14px;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #F97316;
      flex-shrink: 0;
      box-shadow: 0 4px 12px rgba(249, 115, 22, 0.12);
    }

    .collab-feature-text {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .collab-feature-text h4 {
      font-size: 14px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 4px;
      white-space: nowrap;
    }

    .collab-feature-text p {
      font-size: 12px;
      color: #64748B;
      line-height: 1.4;
      max-width: 130px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    /* Responsive */
    @media (max-width: 1200px) {
      .collab-container {
        flex-direction: column;
      }

      .collab-left {
        flex: none;
        max-width: 100%;
      }

      .collab-sidebar-left,
      .collab-sidebar-right {
        display: none;
      }

      .collab-features-bar {
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
      }
    }

    @media (max-width: 768px) {
      .collab-section {
        padding: 60px 16px;
      }

      .collab-title,
      .collab-subtitle {
        font-size: 28px;
      }

      .collab-comparison {
        flex-direction: column;
      }

      .collab-comparison::after {
        display: none;
      }

      .collab-features-bar {
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;
        padding: 20px;
      }
    }

    @media (max-width: 480px) {
      .collab-features-bar {
        grid-template-columns: 1fr;
      }
    }

    /* ═══════════════════════════════════════════════════════════════════════════════
       SYSTÈME INTELLIGENT - Section réseau connecté (REFAIT)
    ═══════════════════════════════════════════════════════════════════════════════ */
    .system-section {
      padding: 60px 24px 80px;
      background: #F8FAFC;
    }

    .system-container {
      max-width: 1400px;
      margin: 0 auto;
    }

    .system-main {
      display: grid;
      grid-template-columns: 280px 1fr 220px;
      gap: 40px;
      align-items: start;
    }

    /* LEFT COLUMN - Title + Side Blocks */
    .system-left {
      padding-top: 0;
    }

    .system-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #FFF4ED;
      color: #F97316;
      padding: 8px 16px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 20px;
    }

    .system-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 38px;
      font-weight: 800;
      color: #1E293B;
      line-height: 1.1;
      margin-bottom: 16px;
    }

    .system-desc {
      font-size: 16px;
      color: #64748B;
      line-height: 1.6;
      margin-bottom: 50px;
    }

    .system-side-blocks {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .system-side-block {
      display: flex;
      align-items: flex-start;
      gap: 14px;
    }

    .system-side-icon {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .system-side-icon.blue { background: #DBEAFE; color: #3B82F6; }
    .system-side-icon.green { background: #D1FAE5; color: #10B981; }
    .system-side-icon.purple { background: #EDE9FE; color: #8B5CF6; }
    .system-side-icon.orange { background: #FFEDD5; color: #F97316; }
    .system-side-icon.teal { background: #CCFBF1; color: #14B8A6; }

    .system-side-text h4 {
      font-size: 15px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 4px;
    }

    .system-side-text p {
      font-size: 13px;
      color: #64748B;
      line-height: 1.5;
    }

    /* CENTER - Network Visualization */
    .system-center {
      position: relative;
      min-height: 680px;
    }

    /* SVG Connections */
    .system-svg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
      pointer-events: none;
    }

    .system-svg path {
      fill: none;
      stroke: #CBD5E1;
      stroke-width: 1.5;
    }

    /* Cockpit Central */
    .system-cockpit {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 260px;
      background: linear-gradient(145deg, #0F172A 0%, #1E3A5F 100%);
      border-radius: 20px;
      padding: 24px 20px;
      color: white;
      text-align: center;
      box-shadow: 0 25px 60px rgba(15, 23, 42, 0.3);
      z-index: 10;
    }

    .system-cockpit-icon {
      width: 44px;
      height: 44px;
      background: rgba(255,255,255,0.1);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 10px;
    }

    .system-cockpit h3 {
      font-size: 18px;
      font-weight: 700;
      margin-bottom: 6px;
    }

    .system-cockpit-badge {
      display: inline-block;
      background: rgba(249, 115, 22, 0.25);
      color: #FB923C;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 600;
      margin-bottom: 10px;
    }

    .system-cockpit-subtitle {
      font-size: 12px;
      color: rgba(255,255,255,0.7);
      margin-bottom: 16px;
    }

    .system-cockpit-stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 6px;
      margin-bottom: 14px;
    }

    .system-cockpit-stat {
      text-align: center;
    }

    .system-cockpit-stat-label {
      font-size: 9px;
      color: rgba(255,255,255,0.5);
      margin-bottom: 2px;
    }

    .system-cockpit-stat-value {
      font-size: 18px;
      font-weight: 700;
    }

    .system-cockpit-stat-change {
      font-size: 9px;
      color: #4ADE80;
    }

    .system-cockpit-sync {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      font-size: 10px;
      color: rgba(255,255,255,0.6);
      padding-top: 10px;
      border-top: 1px solid rgba(255,255,255,0.1);
    }

    .system-cockpit-sync-dot {
      width: 6px;
      height: 6px;
      background: #4ADE80;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }

    /* Module Cards */
    .system-module {
      position: absolute;
      background: white;
      border-radius: 16px;
      padding: 16px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.06);
      border: 1px solid #F1F5F9;
      z-index: 5;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .system-module:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    }

    .system-module-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 6px;
    }

    .system-module-icon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .system-module-icon.teal { background: #CCFBF1; color: #14B8A6; }
    .system-module-icon.blue { background: #DBEAFE; color: #3B82F6; }
    .system-module-icon.indigo { background: #E0E7FF; color: #6366F1; }
    .system-module-icon.purple { background: #F3E8FF; color: #A855F7; }
    .system-module-icon.orange { background: #FFEDD5; color: #F97316; }
    .system-module-icon.green { background: #DCFCE7; color: #22C55E; }
    .system-module-icon.yellow { background: #FEF9C3; color: #CA8A04; }
    .system-module-icon.rose { background: #FFE4E6; color: #F43F5E; }

    .system-module h4 {
      font-size: 14px;
      font-weight: 700;
      color: #1E293B;
    }

    .system-module-desc {
      font-size: 11px;
      color: #64748B;
      line-height: 1.4;
      margin-bottom: 10px;
    }

    /* Module Specific Widths */
    .system-module.client { width: 160px; top: 0; left: 50%; transform: translateX(-50%); }
    .system-module.devis { width: 160px; top: 100px; left: 60px; }
    .system-module.equipe { width: 170px; top: 100px; right: 60px; }
    .system-module.assistants { width: 160px; top: 280px; left: 0; }
    .system-module.super-assistant { width: 175px; top: 280px; right: 0; }
    .system-module.chantiers { width: 165px; bottom: 80px; left: 80px; }
    .system-module.paiement { width: 165px; bottom: 0; left: 50%; transform: translateX(-50%); }
    .system-module.marketing { width: 175px; bottom: 80px; right: 60px; }

    /* Module Avatars */
    .system-module-avatars {
      display: flex;
      align-items: center;
    }

    .system-module-avatar {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 2px solid white;
      margin-left: -6px;
      object-fit: cover;
    }

    .system-module-avatar:first-child { margin-left: 0; }

    .system-module-badge {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #14B8A6;
      color: white;
      font-size: 10px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: -6px;
    }

    /* Module Stats */
    .system-module-stats {
      display: flex;
      gap: 16px;
      margin-top: 8px;
    }

    .system-module-stat-label {
      font-size: 10px;
      color: #94A3B8;
    }

    .system-module-stat-value {
      font-size: 14px;
      font-weight: 700;
      color: #1E293B;
    }

    /* Module List */
    .system-module-list {
      font-size: 11px;
      color: #64748B;
    }

    .system-module-list-item {
      padding: 3px 0;
    }

    .system-module-list-item.muted { color: #94A3B8; }

    /* Module Image */
    .system-module-img {
      width: 100%;
      height: 50px;
      border-radius: 8px;
      object-fit: cover;
      margin: 6px 0;
    }

    .system-module-progress {
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #64748B;
    }

    .system-module-progress strong { color: #1E293B; }

    /* Module Amount */
    .system-module-amount {
      font-size: 18px;
      font-weight: 700;
      color: #1E293B;
      margin-top: 6px;
    }

    .system-module-amount span {
      font-size: 12px;
      color: #22C55E;
      margin-left: 6px;
    }

    .system-module-meta {
      font-size: 10px;
      color: #94A3B8;
      margin-top: 4px;
    }

    /* RIGHT COLUMN - Side Blocks */
    .system-right {
      padding-top: 100px;
    }

    /* Bottom Banner */
    .system-banner {
      background: white;
      border-radius: 20px;
      padding: 24px 40px;
      margin-top: 50px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-shadow: 0 4px 20px rgba(0,0,0,0.04);
    }

    .system-banner-left {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .system-banner-icon {
      width: 48px;
      height: 48px;
      background: #DBEAFE;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #3B82F6;
    }

    .system-banner-title {
      font-size: 17px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 2px;
    }

    .system-banner-desc {
      font-size: 13px;
      color: #64748B;
    }

    .system-banner-stats {
      display: flex;
      gap: 40px;
    }

    .system-banner-stat {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .system-banner-stat svg {
      color: #3B82F6;
    }

    .system-banner-stat-text h5 {
      font-size: 14px;
      font-weight: 700;
      color: #1E293B;
    }

    .system-banner-stat-text p {
      font-size: 12px;
      color: #64748B;
    }

    @media (max-width: 1200px) {
      .system-main {
        grid-template-columns: 1fr;
        gap: 30px;
      }

      .system-left, .system-right {
        display: none;
      }

      .system-center {
        min-height: 500px;
      }

      .system-banner {
        flex-direction: column;
        gap: 20px;
        text-align: center;
      }

      .system-banner-stats {
        flex-wrap: wrap;
        justify-content: center;
      }
    }

    /* ═══════════════════════════════════════════════════════════════════════════════
       COMMANDE VOCALE - Section premium
    ═══════════════════════════════════════════════════════════════════════════════ */
    .voice-section {
      padding: 100px 24px 80px;
      background: #FFFFFF;
    }

    .voice-container {
      max-width: 1400px;
      margin: 0 auto;
    }

    /* Process Steps Bar */
    .voice-steps {
      display: flex;
      justify-content: center;
      gap: 60px;
      margin-bottom: 60px;
    }

    .voice-step {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }

    .voice-step-icon {
      width: 56px;
      height: 56px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .voice-step-icon.blue { background: #DBEAFE; color: #3B82F6; }
    .voice-step-icon.purple { background: #EDE9FE; color: #8B5CF6; }
    .voice-step-icon.teal { background: #CCFBF1; color: #14B8A6; }
    .voice-step-icon.orange { background: #FFEDD5; color: #F97316; }
    .voice-step-icon.green { background: #DCFCE7; color: #22C55E; }

    .voice-step-label {
      font-size: 13px;
      font-weight: 600;
      color: #64748B;
      text-align: center;
    }

    /* Main Layout */
    .voice-main {
      display: grid;
      grid-template-columns: 380px 140px 1fr;
      gap: 30px;
      align-items: start;
    }

    /* Left Column */
    .voice-left {
      padding-right: 20px;
    }

    .voice-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #FFF4ED;
      color: #F97316;
      padding: 8px 16px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 20px;
    }

    .voice-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 42px;
      font-weight: 800;
      color: #1E293B;
      line-height: 1.1;
      margin-bottom: 6px;
    }

    .voice-title-orange {
      color: #F97316;
    }

    .voice-subtitle {
      font-size: 18px;
      font-weight: 600;
      color: #1E293B;
      margin-bottom: 8px;
      margin-top: 16px;
    }

    .voice-subtitle-light {
      font-weight: 700;
    }

    .voice-desc {
      font-size: 15px;
      color: #64748B;
      line-height: 1.7;
      margin-bottom: 30px;
    }

    /* Voice Command Card */
    .voice-card {
      background: linear-gradient(145deg, #0F172A 0%, #1E3A5F 100%);
      border-radius: 20px;
      padding: 20px;
      color: white;
      box-shadow: 0 25px 60px rgba(15, 23, 42, 0.25);
      position: relative;
    }

    .voice-card-content {
      display: flex;
      gap: 16px;
      align-items: flex-start;
    }

    .voice-card-avatar {
      width: 80px;
      height: 80px;
      border-radius: 16px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .voice-card-text {
      flex: 1;
    }

    .voice-card-quote {
      color: #F97316;
      font-size: 28px;
      line-height: 1;
      margin-bottom: 8px;
    }

    .voice-card-message {
      font-size: 15px;
      line-height: 1.5;
      color: rgba(255,255,255,0.95);
      font-weight: 500;
    }

    .voice-card-wave {
      display: flex;
      align-items: center;
      gap: 3px;
      margin-top: 16px;
      height: 24px;
    }

    .voice-card-wave span {
      width: 3px;
      background: rgba(255,255,255,0.4);
      border-radius: 2px;
      animation: wave 1s ease-in-out infinite;
    }

    .voice-card-wave span:nth-child(1) { height: 8px; animation-delay: 0s; }
    .voice-card-wave span:nth-child(2) { height: 16px; animation-delay: 0.1s; }
    .voice-card-wave span:nth-child(3) { height: 12px; animation-delay: 0.2s; }
    .voice-card-wave span:nth-child(4) { height: 20px; animation-delay: 0.3s; }
    .voice-card-wave span:nth-child(5) { height: 14px; animation-delay: 0.4s; }
    .voice-card-wave span:nth-child(6) { height: 18px; animation-delay: 0.5s; }
    .voice-card-wave span:nth-child(7) { height: 10px; animation-delay: 0.6s; }
    .voice-card-wave span:nth-child(8) { height: 16px; animation-delay: 0.7s; }
    .voice-card-wave span:nth-child(9) { height: 8px; animation-delay: 0.8s; }
    .voice-card-wave span:nth-child(10) { height: 14px; animation-delay: 0.9s; }

    @keyframes wave {
      0%, 100% { transform: scaleY(1); }
      50% { transform: scaleY(0.5); }
    }

    .voice-card-mic {
      position: absolute;
      bottom: 20px;
      right: 20px;
      width: 48px;
      height: 48px;
      background: #F97316;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      box-shadow: 0 8px 20px rgba(249, 115, 22, 0.4);
    }

    /* Super Assistant Listening */
    .voice-listening {
      background: #F8FAFC;
      border-radius: 14px;
      padding: 14px 18px;
      margin-top: 16px;
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .voice-listening-icon {
      width: 36px;
      height: 36px;
      background: white;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #8B5CF6;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    }

    .voice-listening-text h5 {
      font-size: 14px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 2px;
    }

    .voice-listening-text p {
      font-size: 12px;
      color: #64748B;
    }

    .voice-listening-wave {
      display: flex;
      align-items: center;
      gap: 2px;
      margin-left: auto;
      height: 20px;
    }

    .voice-listening-wave span {
      width: 3px;
      background: #F97316;
      border-radius: 2px;
      animation: wave 0.8s ease-in-out infinite;
    }

    .voice-listening-wave span:nth-child(1) { height: 6px; animation-delay: 0s; }
    .voice-listening-wave span:nth-child(2) { height: 12px; animation-delay: 0.1s; }
    .voice-listening-wave span:nth-child(3) { height: 8px; animation-delay: 0.2s; }
    .voice-listening-wave span:nth-child(4) { height: 16px; animation-delay: 0.3s; }
    .voice-listening-wave span:nth-child(5) { height: 10px; animation-delay: 0.4s; }
    .voice-listening-wave span:nth-child(6) { height: 14px; animation-delay: 0.5s; }
    .voice-listening-wave span:nth-child(7) { height: 8px; animation-delay: 0.6s; }
    .voice-listening-wave span:nth-child(8) { height: 12px; animation-delay: 0.7s; }

    /* Center Flow */
    .voice-center {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding-top: 180px;
    }

    .voice-flow {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .voice-flow-arrow {
      color: #CBD5E1;
      font-size: 20px;
    }

    .voice-ai-icon {
      width: 80px;
      height: 80px;
      background: linear-gradient(145deg, #DBEAFE 0%, #EDE9FE 100%);
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
    }

    .voice-ai-icon img {
      width: 50px;
      height: 50px;
    }

    .voice-ai-badge {
      position: absolute;
      bottom: -4px;
      right: -4px;
      background: #F97316;
      color: white;
      font-size: 10px;
      font-weight: 700;
      padding: 2px 6px;
      border-radius: 6px;
    }

    .voice-ai-labels {
      text-align: center;
      margin-top: 12px;
    }

    .voice-ai-labels p {
      font-size: 13px;
      color: #64748B;
      line-height: 1.6;
    }

    .voice-ai-check {
      width: 32px;
      height: 32px;
      background: #F0FDF4;
      border: 2px solid #22C55E;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #22C55E;
      margin: 16px auto 0;
    }

    /* Right Column - Results */
    .voice-right {
      background: white;
      border-radius: 20px;
      padding: 24px;
      box-shadow: 0 20px 50px rgba(0,0,0,0.06);
      border: 1px solid #F1F5F9;
    }

    .voice-right-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;
    }

    .voice-right-title {
      font-size: 16px;
      font-weight: 700;
      color: #1E293B;
    }

    .voice-right-badge {
      display: flex;
      align-items: center;
      gap: 6px;
      background: #F0FDF4;
      color: #22C55E;
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 13px;
      font-weight: 600;
    }

    /* Result Blocks */
    .voice-result {
      background: #F8FAFC;
      border-radius: 14px;
      padding: 16px;
      margin-bottom: 12px;
    }

    .voice-result:last-child {
      margin-bottom: 0;
    }

    .voice-result-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 10px;
    }

    .voice-result-icon {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .voice-result-icon.orange { background: #FFEDD5; color: #F97316; }
    .voice-result-icon.blue { background: #DBEAFE; color: #3B82F6; }
    .voice-result-icon.purple { background: #EDE9FE; color: #8B5CF6; }
    .voice-result-icon.teal { background: #CCFBF1; color: #14B8A6; }
    .voice-result-icon.green { background: #DCFCE7; color: #22C55E; }

    .voice-result-title {
      font-size: 14px;
      font-weight: 700;
      color: #1E293B;
    }

    .voice-result-content {
      font-size: 13px;
      color: #64748B;
      line-height: 1.5;
    }

    .voice-result-meta {
      font-size: 12px;
      color: #94A3B8;
      margin-top: 6px;
    }

    .voice-result-tasks {
      margin-top: 8px;
    }

    .voice-result-task {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: #475569;
      padding: 4px 0;
    }

    .voice-result-task::before {
      content: '•';
      color: #F97316;
    }

    .voice-result-avatars {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 8px;
    }

    .voice-result-avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 2px solid white;
      margin-left: -8px;
      object-fit: cover;
    }

    .voice-result-avatar:first-child {
      margin-left: 0;
    }

    .voice-result-avatar-more {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: #F97316;
      color: white;
      font-size: 10px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: -8px;
    }

    .voice-result-sent {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      background: #F0FDF4;
      color: #22C55E;
      padding: 4px 10px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 600;
      margin-top: 8px;
    }

    .voice-result-photos {
      display: flex;
      gap: 8px;
      margin-top: 8px;
    }

    .voice-result-photo {
      width: 60px;
      height: 45px;
      border-radius: 8px;
      object-fit: cover;
    }

    .voice-result-photo-more {
      width: 60px;
      height: 45px;
      border-radius: 8px;
      background: #E2E8F0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 600;
      color: #64748B;
    }

    /* Features Bar */
    .voice-features {
      display: flex;
      justify-content: space-between;
      gap: 20px;
      margin-top: 50px;
      padding-top: 30px;
      border-top: 1px solid #F1F5F9;
    }

    .voice-feature {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .voice-feature-icon {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .voice-feature-icon.orange { background: #FFEDD5; color: #F97316; }
    .voice-feature-icon.blue { background: #DBEAFE; color: #3B82F6; }
    .voice-feature-icon.teal { background: #CCFBF1; color: #14B8A6; }
    .voice-feature-icon.green { background: #DCFCE7; color: #22C55E; }
    .voice-feature-icon.purple { background: #EDE9FE; color: #8B5CF6; }

    .voice-feature-text h5 {
      font-size: 13px;
      font-weight: 700;
      color: #1E293B;
    }

    .voice-feature-text p {
      font-size: 11px;
      color: #64748B;
    }

    /* Bottom Banner */
    .voice-banner {
      background: #F8FAFC;
      border-radius: 20px;
      padding: 24px 40px;
      margin-top: 50px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .voice-banner-left {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .voice-banner-icon {
      width: 48px;
      height: 48px;
      background: #FFEDD5;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #F97316;
    }

    .voice-banner-title {
      font-size: 17px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 2px;
    }

    .voice-banner-desc {
      font-size: 13px;
      color: #64748B;
    }

    .voice-banner-stats {
      display: flex;
      gap: 40px;
    }

    .voice-banner-stat {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .voice-banner-stat-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .voice-banner-stat-icon.blue { background: #DBEAFE; color: #3B82F6; }
    .voice-banner-stat-icon.green { background: #DCFCE7; color: #22C55E; }
    .voice-banner-stat-icon.purple { background: #EDE9FE; color: #8B5CF6; }

    .voice-banner-stat-text h5 {
      font-size: 14px;
      font-weight: 700;
      color: #1E293B;
    }

    .voice-banner-stat-text p {
      font-size: 12px;
      color: #64748B;
    }

    @media (max-width: 1200px) {
      .voice-main {
        grid-template-columns: 1fr;
        gap: 40px;
      }

      .voice-center {
        padding-top: 0;
        flex-direction: row;
        justify-content: center;
      }

      .voice-steps {
        flex-wrap: wrap;
        gap: 30px;
      }

      .voice-features {
        flex-wrap: wrap;
        gap: 16px;
      }

      .voice-banner {
        flex-direction: column;
        gap: 24px;
        text-align: center;
      }

      .voice-banner-stats {
        flex-wrap: wrap;
        justify-content: center;
      }
    }

    /* ═══════════════════════════════════════════════════════════════════════════════
       PORTAIL CLIENT - Section premium
    ═══════════════════════════════════════════════════════════════════════════════ */
    .portal-section {
      padding: 100px 24px 80px;
      background: #F9FAFB;
    }

    .portal-container {
      max-width: 1400px;
      margin: 0 auto;
    }

    .portal-main {
      display: grid;
      grid-template-columns: 380px 1fr;
      gap: 60px;
      align-items: start;
    }

    /* Left Column */
    .portal-left {
      position: sticky;
      top: 140px;
    }

    .portal-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #FFF4ED;
      color: #F97316;
      padding: 8px 16px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 24px;
    }

    .portal-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 40px;
      font-weight: 800;
      color: #1E293B;
      line-height: 1.1;
      margin-bottom: 6px;
    }

    .portal-title-orange {
      color: #F97316;
    }

    .portal-desc {
      font-size: 16px;
      color: #64748B;
      line-height: 1.7;
      margin-bottom: 32px;
      margin-top: 20px;
    }

    /* Benefit Cards */
    .portal-benefits {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .portal-benefit {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      background: white;
      border-radius: 14px;
      padding: 16px;
      border: 1px solid #F1F5F9;
      transition: all 0.2s ease;
    }

    .portal-benefit:hover {
      box-shadow: 0 8px 24px rgba(0,0,0,0.06);
      border-color: #E2E8F0;
    }

    .portal-benefit-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .portal-benefit-icon.teal { background: #CCFBF1; color: #14B8A6; }
    .portal-benefit-icon.green { background: #DCFCE7; color: #22C55E; }
    .portal-benefit-icon.yellow { background: #FEF9C3; color: #CA8A04; }
    .portal-benefit-icon.orange { background: #FFEDD5; color: #F97316; }

    .portal-benefit-text h4 {
      font-size: 15px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 4px;
    }

    .portal-benefit-text p {
      font-size: 13px;
      color: #64748B;
      line-height: 1.4;
    }

    /* Right Column - Portal Mockup */
    .portal-right {
      background: white;
      border-radius: 20px;
      box-shadow: 0 25px 60px rgba(0,0,0,0.08);
      overflow: hidden;
      border: 1px solid #E2E8F0;
    }

    /* Portal Header */
    .portal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 24px;
      border-bottom: 1px solid #F1F5F9;
    }

    .portal-header-left {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 16px;
      font-weight: 700;
      color: #1E293B;
    }

    .portal-header-left svg {
      color: #64748B;
    }

    .portal-header-right {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .portal-header-icons {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .portal-header-icon {
      position: relative;
      color: #64748B;
    }

    .portal-header-icon-badge {
      position: absolute;
      top: -4px;
      right: -4px;
      width: 16px;
      height: 16px;
      background: #EF4444;
      color: white;
      font-size: 10px;
      font-weight: 700;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .portal-header-user {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .portal-header-user-info {
      text-align: right;
    }

    .portal-header-user-greeting {
      font-size: 11px;
      color: #94A3B8;
    }

    .portal-header-user-name {
      font-size: 14px;
      font-weight: 600;
      color: #1E293B;
    }

    .portal-header-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    }

    /* Portal Body */
    .portal-body {
      display: flex;
    }

    /* Portal Sidebar */
    .portal-sidebar {
      width: 160px;
      border-right: 1px solid #F1F5F9;
      padding: 16px 12px;
      flex-shrink: 0;
    }

    .portal-nav-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border-radius: 10px;
      font-size: 13px;
      color: #64748B;
      cursor: pointer;
      transition: all 0.15s ease;
      margin-bottom: 4px;
    }

    .portal-nav-item:hover {
      background: #F8FAFC;
      color: #1E293B;
    }

    .portal-nav-item.active {
      background: #EFF6FF;
      color: #3B82F6;
      font-weight: 600;
    }

    .portal-nav-item svg {
      width: 18px;
      height: 18px;
    }

    /* Portal Content */
    .portal-content {
      flex: 1;
      padding: 20px;
      min-width: 0;
    }

    /* Project Card */
    .portal-project {
      display: flex;
      gap: 16px;
      margin-bottom: 20px;
    }

    .portal-project-img {
      width: 100px;
      height: 80px;
      border-radius: 12px;
      object-fit: cover;
    }

    .portal-project-info h3 {
      font-size: 18px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 6px;
    }

    .portal-project-address {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #64748B;
      margin-bottom: 8px;
    }

    .portal-project-meta {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .portal-project-badge {
      background: #DCFCE7;
      color: #22C55E;
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 600;
    }

    .portal-project-date {
      font-size: 11px;
      color: #94A3B8;
    }

    /* Portal Grid */
    .portal-grid {
      display: grid;
      grid-template-columns: 1fr 200px;
      gap: 16px;
    }

    .portal-grid-left {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .portal-grid-right {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    /* Portal Box */
    .portal-box {
      background: #F8FAFC;
      border-radius: 14px;
      padding: 16px;
    }

    .portal-box-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .portal-box-title {
      font-size: 13px;
      font-weight: 700;
      color: #1E293B;
    }

    .portal-box-link {
      font-size: 12px;
      color: #F97316;
      font-weight: 500;
    }

    /* Timeline */
    .portal-timeline {
      display: flex;
      justify-content: space-between;
      position: relative;
      padding: 0 10px;
    }

    .portal-timeline::before {
      content: '';
      position: absolute;
      top: 12px;
      left: 20px;
      right: 20px;
      height: 3px;
      background: #E2E8F0;
      z-index: 1;
    }

    .portal-timeline-step {
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      z-index: 2;
    }

    .portal-timeline-dot {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 6px;
      font-size: 11px;
      font-weight: 700;
    }

    .portal-timeline-dot.done {
      background: #22C55E;
      color: white;
    }

    .portal-timeline-dot.current {
      background: #3B82F6;
      color: white;
    }

    .portal-timeline-dot.pending {
      background: #E2E8F0;
      color: #94A3B8;
    }

    .portal-timeline-label {
      font-size: 11px;
      font-weight: 600;
      color: #1E293B;
      text-align: center;
    }

    .portal-timeline-status {
      font-size: 10px;
      color: #94A3B8;
      text-align: center;
    }

    .portal-timeline-status.done { color: #22C55E; }
    .portal-timeline-status.current { color: #3B82F6; }

    /* Photos Grid */
    .portal-photos {
      display: flex;
      gap: 8px;
    }

    .portal-photo {
      width: 70px;
      height: 55px;
      border-radius: 8px;
      object-fit: cover;
    }

    /* Documents List */
    .portal-docs {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .portal-doc {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .portal-doc-icon {
      width: 28px;
      height: 28px;
      background: #FEE2E2;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #DC2626;
    }

    .portal-doc-info {
      flex: 1;
      min-width: 0;
    }

    .portal-doc-name {
      font-size: 11px;
      font-weight: 600;
      color: #1E293B;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .portal-doc-meta {
      font-size: 10px;
      color: #94A3B8;
    }

    /* Decisions */
    .portal-decision {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 12px;
      background: white;
      border-radius: 10px;
      border: 1px solid #E2E8F0;
    }

    .portal-decision-text {
      flex: 1;
    }

    .portal-decision-title {
      font-size: 12px;
      font-weight: 600;
      color: #1E293B;
    }

    .portal-decision-date {
      font-size: 10px;
      color: #94A3B8;
    }

    .portal-decision-btn {
      background: #F97316;
      color: white;
      padding: 6px 14px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 600;
      border: none;
      cursor: pointer;
    }

    /* Payments */
    .portal-payment-amount {
      font-size: 24px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 4px;
    }

    .portal-payment-amount span {
      font-size: 14px;
      font-weight: 500;
      color: #64748B;
    }

    .portal-payment-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
    }

    .portal-payment-badge {
      background: #DCFCE7;
      color: #22C55E;
      padding: 3px 8px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: 600;
    }

    .portal-payment-label {
      font-size: 11px;
      color: #94A3B8;
    }

    .portal-payment-date {
      font-size: 11px;
      color: #64748B;
    }

    /* Messages */
    .portal-message {
      display: flex;
      gap: 10px;
    }

    .portal-message-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }

    .portal-message-content {
      flex: 1;
    }

    .portal-message-header {
      display: flex;
      align-items: baseline;
      gap: 8px;
      margin-bottom: 4px;
    }

    .portal-message-name {
      font-size: 12px;
      font-weight: 600;
      color: #1E293B;
    }

    .portal-message-time {
      font-size: 10px;
      color: #94A3B8;
    }

    .portal-message-text {
      font-size: 11px;
      color: #64748B;
      line-height: 1.4;
    }

    /* Next Step Box */
    .portal-next {
      background: white;
      border: 1px solid #E2E8F0;
    }

    .portal-next-item {
      display: flex;
      gap: 10px;
      padding: 10px 0;
    }

    .portal-next-icon {
      width: 32px;
      height: 32px;
      background: #FFEDD5;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #F97316;
      flex-shrink: 0;
    }

    .portal-next-title {
      font-size: 12px;
      font-weight: 600;
      color: #1E293B;
    }

    .portal-next-date {
      font-size: 11px;
      color: #64748B;
    }

    /* Bottom 3-column layout */
    .portal-bottom-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-top: 16px;
    }

    /* Features Bar */
    .portal-features {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 30px;
      margin-top: 50px;
      padding: 30px 40px;
      background: white;
      border-radius: 20px;
      border: 1px solid #F1F5F9;
    }

    .portal-feature {
      display: flex;
      align-items: flex-start;
      gap: 14px;
    }

    .portal-feature-icon {
      width: 48px;
      height: 48px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .portal-feature-icon.green { background: #DCFCE7; color: #22C55E; }
    .portal-feature-icon.blue { background: #DBEAFE; color: #3B82F6; }
    .portal-feature-icon.teal { background: #CCFBF1; color: #14B8A6; }
    .portal-feature-icon.orange { background: #FFEDD5; color: #F97316; }

    .portal-feature-text h5 {
      font-size: 15px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 4px;
    }

    .portal-feature-text p {
      font-size: 13px;
      color: #64748B;
      line-height: 1.4;
    }

    @media (max-width: 1200px) {
      .portal-main {
        grid-template-columns: 1fr;
        gap: 40px;
      }

      .portal-left {
        position: static;
      }

      .portal-grid {
        grid-template-columns: 1fr;
      }

      .portal-features {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 768px) {
      .portal-features {
        grid-template-columns: 1fr;
      }

      .portal-sidebar {
        display: none;
      }
    }

    /* ═══════════════════════════════════════════════════════════════════════════════
       COÛTS & MARGES - Section premium rentabilité
    ═══════════════════════════════════════════════════════════════════════════════ */
    .costs-section {
      padding: 100px 24px 80px;
      background: #FFFFFF;
    }

    .costs-container {
      max-width: 1400px;
      margin: 0 auto;
    }

    .costs-main {
      display: grid;
      grid-template-columns: 360px 1fr;
      gap: 60px;
      align-items: start;
    }

    /* Left Column */
    .costs-left {
      position: sticky;
      top: 140px;
    }

    .costs-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #FFF4ED;
      color: #F97316;
      padding: 8px 16px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 24px;
    }

    .costs-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 38px;
      font-weight: 800;
      color: #1E293B;
      line-height: 1.15;
      margin-bottom: 6px;
    }

    .costs-title-orange {
      color: #F97316;
    }

    .costs-desc {
      font-size: 16px;
      color: #64748B;
      line-height: 1.7;
      margin-bottom: 32px;
      margin-top: 20px;
    }

    /* Benefit Cards */
    .costs-benefits {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .costs-benefit {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      background: white;
      border-radius: 14px;
      padding: 16px;
      border: 1px solid #F1F5F9;
      transition: all 0.2s ease;
    }

    .costs-benefit:hover {
      box-shadow: 0 8px 24px rgba(0,0,0,0.06);
      border-color: #E2E8F0;
    }

    .costs-benefit-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .costs-benefit-icon.teal { background: #CCFBF1; color: #14B8A6; }
    .costs-benefit-icon.blue { background: #DBEAFE; color: #3B82F6; }
    .costs-benefit-icon.purple { background: #EDE9FE; color: #8B5CF6; }
    .costs-benefit-icon.green { background: #DCFCE7; color: #22C55E; }
    .costs-benefit-icon.orange { background: #FFEDD5; color: #F97316; }

    .costs-benefit-text h4 {
      font-size: 14px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 3px;
    }

    .costs-benefit-text p {
      font-size: 12px;
      color: #64748B;
      line-height: 1.4;
    }

    /* Right Column - Dashboard */
    .costs-right {
      background: white;
      border-radius: 20px;
      box-shadow: 0 25px 60px rgba(0,0,0,0.08);
      overflow: hidden;
      border: 1px solid #E2E8F0;
    }

    /* Dashboard Header */
    .costs-dash-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 24px;
      border-bottom: 1px solid #F1F5F9;
    }

    .costs-dash-title {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 16px;
      font-weight: 700;
      color: #1E293B;
    }

    .costs-dash-actions {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .costs-date-picker {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 14px;
      background: #F8FAFC;
      border-radius: 8px;
      font-size: 13px;
      color: #64748B;
    }

    .costs-export-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: white;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      font-size: 13px;
      color: #64748B;
      cursor: pointer;
    }

    /* Dashboard Body */
    .costs-dash-body {
      display: flex;
    }

    /* Dashboard Sidebar */
    .costs-dash-sidebar {
      width: 140px;
      border-right: 1px solid #F1F5F9;
      padding: 16px 12px;
      flex-shrink: 0;
    }

    .costs-dash-nav {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border-radius: 10px;
      font-size: 13px;
      color: #64748B;
      cursor: pointer;
      margin-bottom: 4px;
    }

    .costs-dash-nav:hover {
      background: #F8FAFC;
    }

    .costs-dash-nav.active {
      background: #1E3A5F;
      color: white;
    }

    .costs-dash-nav svg {
      width: 16px;
      height: 16px;
    }

    /* Dashboard Content */
    .costs-dash-content {
      flex: 1;
      padding: 20px;
      min-width: 0;
    }

    /* Project Header */
    .costs-project {
      display: flex;
      gap: 16px;
      margin-bottom: 20px;
    }

    .costs-project-img {
      width: 100px;
      height: 75px;
      border-radius: 12px;
      object-fit: cover;
    }

    .costs-project-info h3 {
      font-size: 18px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 4px;
    }

    .costs-project-address {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #64748B;
      margin-bottom: 8px;
    }

    .costs-project-meta {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .costs-project-badge {
      background: #DCFCE7;
      color: #22C55E;
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 600;
    }

    .costs-project-date {
      font-size: 11px;
      color: #94A3B8;
    }

    /* KPI Cards */
    .costs-kpis {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
      margin-bottom: 20px;
    }

    .costs-kpi {
      background: #F8FAFC;
      border-radius: 14px;
      padding: 16px;
      text-align: center;
    }

    .costs-kpi-icon {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 10px;
    }

    .costs-kpi-icon.green { background: #DCFCE7; color: #22C55E; }
    .costs-kpi-icon.blue { background: #DBEAFE; color: #3B82F6; }
    .costs-kpi-icon.purple { background: #EDE9FE; color: #8B5CF6; }
    .costs-kpi-icon.teal { background: #CCFBF1; color: #14B8A6; }

    .costs-kpi-label {
      font-size: 11px;
      color: #64748B;
      margin-bottom: 4px;
    }

    .costs-kpi-value {
      font-size: 22px;
      font-weight: 700;
      color: #1E293B;
    }

    .costs-kpi-value.green { color: #22C55E; }

    .costs-kpi-sub {
      font-size: 11px;
      color: #22C55E;
      margin-top: 2px;
    }

    .costs-kpi-percent {
      font-size: 11px;
      color: #94A3B8;
    }

    /* Grid 3 columns */
    .costs-grid-3 {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 16px;
      margin-bottom: 16px;
    }

    /* Box */
    .costs-box {
      background: #F8FAFC;
      border-radius: 14px;
      padding: 16px;
    }

    .costs-box-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .costs-box-title {
      font-size: 13px;
      font-weight: 700;
      color: #1E293B;
    }

    /* Team */
    .costs-team-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .costs-team-member {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .costs-team-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
    }

    .costs-team-info {
      flex: 1;
      min-width: 0;
    }

    .costs-team-name {
      font-size: 12px;
      font-weight: 600;
      color: #1E293B;
    }

    .costs-team-role {
      font-size: 10px;
      color: #94A3B8;
    }

    .costs-team-hours {
      font-size: 13px;
      font-weight: 700;
      color: #1E293B;
    }

    .costs-team-badge {
      background: #DCFCE7;
      color: #22C55E;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 10px;
      font-weight: 600;
    }

    .costs-team-total {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-top: 10px;
      margin-top: 10px;
      border-top: 1px solid #E2E8F0;
    }

    .costs-team-total-label {
      font-size: 12px;
      color: #64748B;
    }

    .costs-team-total-value {
      font-size: 18px;
      font-weight: 700;
      color: #1E3A5F;
    }

    /* Map */
    .costs-map {
      height: 120px;
      background: linear-gradient(135deg, #E0F2FE 0%, #DBEAFE 50%, #EDE9FE 100%);
      border-radius: 10px;
      position: relative;
      overflow: hidden;
      margin-bottom: 10px;
    }

    .costs-map::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 60px;
      height: 60px;
      border: 2px dashed #F97316;
      border-radius: 50%;
      opacity: 0.5;
    }

    .costs-map-pins {
      position: absolute;
      top: 40%;
      left: 45%;
    }

    .costs-map-pin {
      width: 20px;
      height: 20px;
      background: #22C55E;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      position: absolute;
    }

    .costs-map-pin:nth-child(2) { left: 30px; top: -10px; }
    .costs-map-pin:nth-child(3) { left: 15px; top: 25px; }

    .costs-map-label {
      font-size: 11px;
      color: #64748B;
      text-align: center;
    }

    /* Cost breakdown */
    .costs-breakdown {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .costs-breakdown-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 12px;
    }

    .costs-breakdown-label {
      color: #64748B;
    }

    .costs-breakdown-value {
      font-weight: 600;
      color: #1E293B;
    }

    .costs-breakdown-total {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-top: 10px;
      margin-top: 6px;
      border-top: 1px solid #E2E8F0;
    }

    .costs-breakdown-total-label {
      font-size: 13px;
      font-weight: 600;
      color: #1E293B;
    }

    .costs-breakdown-total-value {
      font-size: 18px;
      font-weight: 700;
      color: #1E3A5F;
    }

    /* Grid 2 + 1 */
    .costs-grid-2-1 {
      display: grid;
      grid-template-columns: 1fr 1fr 200px;
      gap: 16px;
    }

    /* Chart placeholder */
    .costs-chart {
      height: 140px;
      position: relative;
    }

    .costs-chart-legend {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 10px;
    }

    .costs-chart-legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      color: #64748B;
    }

    .costs-chart-legend-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    .costs-chart-legend-dot.red { background: #EF4444; }
    .costs-chart-legend-dot.blue { background: #3B82F6; }

    /* Line chart placeholder */
    .costs-line-chart {
      height: 100px;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 100'%3E%3Cpath d='M0 80 Q50 70 100 60 T200 40 T300 30 T400 25' stroke='%233B82F6' stroke-width='2' fill='none'/%3E%3Cpath d='M0 85 Q50 75 100 55 T200 50 T300 60 T400 65' stroke='%23EF4444' stroke-width='2' fill='none'/%3E%3C/svg%3E") no-repeat center;
      background-size: 100% 100%;
      border-bottom: 1px solid #E2E8F0;
      border-left: 1px solid #E2E8F0;
      margin-top: 10px;
    }

    .costs-chart-axis {
      display: flex;
      justify-content: space-between;
      font-size: 10px;
      color: #94A3B8;
      margin-top: 6px;
    }

    /* Donut chart */
    .costs-donut {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .costs-donut-chart {
      width: 100px;
      height: 100px;
      border-radius: 50%;
      background: conic-gradient(#22C55E 0deg 96deg, #3B82F6 96deg 260deg, #F97316 260deg 360deg);
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .costs-donut-chart::before {
      content: '';
      position: absolute;
      width: 60px;
      height: 60px;
      background: white;
      border-radius: 50%;
    }

    .costs-donut-center {
      position: relative;
      z-index: 1;
      text-align: center;
    }

    .costs-donut-value {
      font-size: 18px;
      font-weight: 700;
      color: #22C55E;
    }

    .costs-donut-label {
      font-size: 10px;
      color: #64748B;
    }

    .costs-donut-legend {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .costs-donut-legend-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 11px;
    }

    .costs-donut-legend-dot {
      width: 8px;
      height: 8px;
      border-radius: 2px;
    }

    .costs-donut-legend-dot.green { background: #22C55E; }
    .costs-donut-legend-dot.blue { background: #3B82F6; }
    .costs-donut-legend-dot.orange { background: #F97316; }

    .costs-donut-legend-label {
      color: #64748B;
      flex: 1;
    }

    .costs-donut-legend-value {
      font-weight: 600;
      color: #1E293B;
    }

    /* Alerts */
    .costs-alerts {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .costs-alert {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      background: white;
      border-left: 3px solid #F97316;
      padding: 12px;
      border-radius: 0 8px 8px 0;
    }

    .costs-alert-icon {
      width: 28px;
      height: 28px;
      background: #FFEDD5;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #F97316;
      flex-shrink: 0;
    }

    .costs-alert-content {
      flex: 1;
    }

    .costs-alert-title {
      font-size: 12px;
      font-weight: 600;
      color: #1E293B;
      margin-bottom: 2px;
    }

    .costs-alert-text {
      font-size: 11px;
      color: #64748B;
    }

    .costs-alert-link {
      font-size: 11px;
      color: #F97316;
      font-weight: 500;
    }

    /* Features Bar */
    .costs-features {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 24px;
      margin-top: 50px;
      padding: 28px 40px;
      background: #F8FAFC;
      border-radius: 20px;
    }

    .costs-feature {
      display: flex;
      align-items: flex-start;
      gap: 14px;
    }

    .costs-feature-icon {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .costs-feature-icon.orange { background: #FFEDD5; color: #F97316; }
    .costs-feature-icon.blue { background: #DBEAFE; color: #3B82F6; }
    .costs-feature-icon.green { background: #DCFCE7; color: #22C55E; }
    .costs-feature-icon.teal { background: #CCFBF1; color: #14B8A6; }
    .costs-feature-icon.purple { background: #EDE9FE; color: #8B5CF6; }

    .costs-feature-text h5 {
      font-size: 14px;
      font-weight: 700;
      color: #1E293B;
      margin-bottom: 4px;
    }

    .costs-feature-text p {
      font-size: 12px;
      color: #64748B;
      line-height: 1.4;
    }

    @media (max-width: 1200px) {
      .costs-main {
        grid-template-columns: 1fr;
        gap: 40px;
      }

      .costs-left {
        position: static;
      }

      .costs-grid-3 {
        grid-template-columns: 1fr;
      }

      .costs-grid-2-1 {
        grid-template-columns: 1fr;
      }

      .costs-kpis {
        grid-template-columns: repeat(2, 1fr);
      }

      .costs-features {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 768px) {
      .costs-features {
        grid-template-columns: 1fr;
      }

      .costs-dash-sidebar {
        display: none;
      }
    }
  `}</style>
);

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANTS
// ═══════════════════════════════════════════════════════════════════════════════

const CockpitHeroSection = () => {
  // Menu réel du Cockpit Pro (dans l'ordre exact)
  const menuItems = [
    { icon: Users, label: 'Collaboration' },
    { icon: TrendingUp, label: 'Commercial' },
    { icon: HardHat, label: 'Chantiers', active: true },
    { icon: FolderOpen, label: 'Catalogue' },
    { icon: Store, label: 'Ma boutique' },
    { icon: Megaphone, label: 'Marketing' },
    { icon: Share2, label: 'Réseau Social' },
    { icon: Users, label: 'Réseau Pro' },
    { icon: ShoppingCart, label: 'Centrale d\'achat' },
    { icon: Receipt, label: 'Comptabilité' },
  ];

  const stats = [
    { label: "Chiffre d'affaires", value: '128 450€', trend: '+24%', positive: true },
    { label: 'Devis envoyés', value: '45', trend: '+18%', positive: true },
    { label: 'Chantiers en cours', value: '12', trend: '+20%', positive: true },
    { label: 'Tâches à faire', value: '8', trend: '-12%', positive: false },
  ];

  const chantiers = [
    { name: 'Villa contemporaine', location: 'Saint-Tropez (83)', status: 'En cours', statusClass: 'en-cours' },
    { name: 'Rénovation complète', location: 'Lyon (69)', status: 'En cours', statusClass: 'en-cours' },
    { name: 'Extension maison', location: 'Aix-en-Provence (13)', status: 'Planning', statusClass: 'planning' },
    { name: 'Piscine & Terrasse', location: 'Marseille (13)', status: 'Terminé', statusClass: 'termine' },
  ];

  const activities = [
    { title: 'Nouveau devis signé', meta: 'Il y a 10 min • Devis n°2024-045' },
    { title: 'Compte rendu chantier', meta: 'Il y a 30 min • Villa contemporaine' },
    { title: 'Facture envoyée', meta: 'Il y a 2h • Facture n°2024-089' },
    { title: 'Nouveau client', meta: 'Il y a 2h • Dupont Rénovation' },
  ];

  const features = [
    { icon: Mic, title: 'Commande vocale', desc: 'Devis, tâches, comptes rendus... à la voix', color: 'blue' },
    { icon: MapPin, title: 'Suivi chantier GPS', desc: 'Temps réel, rentabilité, pointage équipe', color: 'green' },
    { icon: Bot, title: 'Équipe IA intégrée', desc: 'Assistant, commercial, chef de chantier...', color: 'purple' },
    { icon: BarChart3, title: 'Pilotage 360°', desc: 'Activité, finance, équipe, marketing, achats...', color: 'orange' },
    { icon: Shield, title: 'Zéro perte d\'info', desc: 'Tout est enregistré, classé, retrouvé', color: 'pink' },
  ];

  const trustItems = [
    { icon: Trophy, title: 'Conçu par un entrepreneur', desc: '+1500 piscines construites dans 7 pays' },
    { icon: Shield, title: 'Données 100% sécurisées', desc: 'Hébergées en France & cryptées' },
    { icon: Headphones, title: 'Support humain 7J/7', desc: 'Une équipe dispo quand tu en as besoin' },
    { icon: TrendingUp, title: 'Aucune commission', desc: 'Tu gardes 100% de tes revenus' },
  ];

  return (
    <section className="cockpit-hero">
      <div className="cockpit-container">
        {/* Header */}
        <div className="hero-header">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="hero-badge"
          >
            Cockpit Bâtiment · IA intégrée
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="hero-main">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="hero-content"
          >
            <h1>
              <span className="line1">Ton entreprise</span>
              <span className="line1">du bâtiment.</span>
              <span>Pilotée depuis un seul cockpit.</span>
            </h1>
            
            {/* Trait orange décoratif */}
            <div style={{
              width: 60,
              height: 4,
              background: '#FF7A00',
              borderRadius: 2,
              marginTop: 20,
              marginBottom: 24,
            }} />
            
            <p className="hero-subtitle">
              Chantiers, clients, devis, équipes, marketing, achats...<br />
              <strong>Tout est centralisé, automatisé et piloté par l'IA.</strong>
            </p>
            
            <div className="hero-points">
              <div className="hero-point">
                <CheckCircle size={22} />
                Devis en 1 minute à la voix
              </div>
              <div className="hero-point">
                <CheckCircle size={22} />
                Suivi chantier en temps réel
              </div>
              <div className="hero-point">
                <CheckCircle size={22} />
                IA + équipe = Zéro perte de temps
              </div>
            </div>
            
            <div className="hero-actions">
              <Link to="/pro/fondateur" className="cta-primary">
                ✨ Devenir Membre Fondateur →
              </Link>
              
              <Link to="/pro/tarifs" className="cta-secondary">
                Voir les tarifs
              </Link>
            </div>
          </motion.div>

          {/* Right: Cockpit Preview */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="cockpit-preview-wrapper"
          >
            <div className="cockpit-preview">
            {/* Sidebar - Menu réel */}
            <div className="cockpit-sidebar">
              {/* Logo */}
              <div className="cockpit-logo">
                <div className="cockpit-logo-icon">
                  <Briefcase size={14} color="white" />
                </div>
                <div className="cockpit-logo-text">
                  <strong>Cockpit Pro</strong>
                  <span>Assistant IA intégré</span>
                </div>
              </div>
              
              {/* Bouton nouvelle conversation */}
              <div className="new-conversation-btn">
                <span style={{ fontSize: 12, fontWeight: 800 }}>+</span>
                Nouvelle conversation
              </div>
              
              {/* Conversations */}
              <div className="sidebar-section">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MessageSquare size={12} />
                  Conversations
                </div>
                <ChevronRight size={10} style={{ opacity: 0.5 }} />
              </div>
              
              {/* Divider MES ESPACES */}
              <div className="sidebar-divider">MES ESPACES</div>
              
              {/* Menu items - tous avec flèches */}
              {menuItems.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className={`sidebar-item ${item.active ? 'active' : ''}`}>
                    {Icon && <Icon size={12} />}
                    {item.label}
                    <ChevronRight size={10} className="sidebar-item-chevron" />
                  </div>
                );
              })}
              
              {/* Bottom section */}
              <div className="sidebar-bottom">
                <div className="sidebar-bottom-item">
                  <Home size={10} />
                  Retour à l'accueil
                </div>
                <div className="sidebar-bottom-item">
                  <Settings size={10} />
                  Paramètres
                </div>
                <div className="sidebar-logout">
                  <LogOut size={10} />
                  Déconnexion
                </div>
              </div>
            </div>
            
            {/* Dashboard */}
            <div className="cockpit-dashboard">
              <div className="dash-header">
                <div>
                  <h3>Bonjour Romain !</h3>
                  <p>Voici un aperçu de ton activité aujourd'hui.</p>
                </div>
                <div className="period-selector">
                  Période <strong style={{ marginLeft: 8 }}>30 derniers jours ▾</strong>
                </div>
              </div>
              
              <div className="stats-row">
                {stats.map((stat, i) => (
                  <div key={i} className="stat-card">
                    <div className="stat-card-label">{stat.label}</div>
                    <div className="stat-card-value">{stat.value}</div>
                    <div className="stat-card-chart">
                      {[40, 60, 45, 80, 55, 70, 90].map((h, j) => (
                        <div key={j} className="stat-bar" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                    <div className={`stat-trend ${stat.positive ? 'positive' : 'negative'}`}>
                      {stat.trend}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="dash-panels">
                {/* Mes chantiers */}
                <div className="panel">
                  <h4>Mes chantiers</h4>
                  {chantiers.map((chantier, i) => (
                    <div key={i} className="chantier-item">
                      <div className="chantier-thumb">
                        <Home size={20} />
                      </div>
                      <div className="chantier-info">
                        <div className="chantier-name">{chantier.name}</div>
                        <div className="chantier-location">{chantier.location}</div>
                      </div>
                      <div className={`chantier-status ${chantier.statusClass}`}>
                        {chantier.status}
                      </div>
                    </div>
                  ))}
                  <a href="#" className="panel-link">Voir toute l'activité →</a>
                </div>
                
                {/* Activité récente */}
                <div className="panel">
                  <h4>Activité récente</h4>
                  {activities.map((activity, i) => (
                    <div key={i} className="activity-item">
                      <div className="activity-dot" />
                      <div className="activity-content">
                        <div className="activity-title">{activity.title}</div>
                        <div className="activity-meta">{activity.meta}</div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Assistant IA - Style réel du cockpit */}
                <div className="assistant-panel">
                  <div className="assistant-mic-btn">
                    <Mic size={22} />
                  </div>
                  <div className="assistant-greeting">Bonjour Romain !</div>
                  <div className="assistant-subtitle">
                    Appuie sur le micro pour parler, ou<br />écris ton message ci-dessous
                  </div>
                  
                  <div className="actions-divider">
                    <span>Actions rapides</span>
                  </div>
                  
                  <div className="actions-grid">
                    <div className="action-card peach">
                      <div className="action-card-icon peach">
                        <FileText size={12} color="#ea580c" />
                      </div>
                      <div className="action-card-title">Compte rendu</div>
                    </div>
                    <div className="action-card green">
                      <div className="action-card-icon green">
                        <FileText size={12} color="#059669" />
                      </div>
                      <div className="action-card-title">Créer un devis</div>
                    </div>
                    <div className="action-card green">
                      <div className="action-card-icon green">
                        <Users size={12} color="#059669" />
                      </div>
                      <div className="action-card-title">Prospects</div>
                    </div>
                    <div className="action-card pink">
                      <div className="action-card-icon pink">
                        <Mail size={12} color="#db2777" />
                      </div>
                      <div className="action-card-title">Boîte mail</div>
                    </div>
                  </div>
                  
                  <div className="chat-input-bar">
                    <div className="chat-input-field">Écris ton message...</div>
                    <div className="chat-send-btn">
                      <Send size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </motion.div>
        </div>

        {/* Feature Strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="feature-strip"
        >
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div key={i} className="feature-item">
                <div className={`feature-icon ${feature.color}`}>
                  <Icon />
                </div>
                <div className="feature-text">
                  <strong>{feature.title}</strong>
                  <span>{feature.desc}</span>
                </div>
              </div>
            );
          })}
        </motion.div>

        {/* Trust Strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="trust-strip"
        >
          {trustItems.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="trust-item">
                <div className="trust-icon">
                  <Icon />
                </div>
                <div className="trust-text">
                  <strong>{item.title}</strong>
                  <span>{item.desc}</span>
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// BLOC 2 - POURQUOI LE COCKPIT
// ═══════════════════════════════════════════════════════════════════════════════
const CockpitBlock2 = () => {
  return (
    <section className="cockpit-block-2">
      <div className="cb2-container">
        <div className="cb2-left">
          <div className="cb2-badge">
            <HardHat size={18} /> POUR LES PROS DU BÂTIMENT
          </div>

          <h2 className="cb2-title">
            Arrête de t'enfermer<br />
            dans l'opérationnel.<br />
            <span>Reprends ton entreprise en main.</span>
          </h2>

          <p className="cb2-text">
            Le Cockpit Bâtiment remplace tout ce qui te prend des heures chaque semaine.
            Tu te concentres sur ton métier.<br />
            <strong>On s'occupe du reste.</strong>
          </p>

          <div className="cb2-callout">
            <div className="cb2-callout-icon">
              <Calendar size={24} />
            </div>
            <div>
              <p>
                Fini les soirées à faire des devis, des comptes rendus ou à courir après les infos.
              </p>
              <strong>Gagne du temps. Gagne en sérénité. Gagne en liberté.</strong>
            </div>
          </div>

          <div className="cb2-stats">
            <div>
              <strong>2x à 3x</strong>
              <span>plus productif<br />chaque semaine</span>
            </div>
            <div>
              <strong>-70%</strong>
              <span>de charge<br />mentale</span>
            </div>
            <div>
              <strong>100%</strong>
              <span>de visibilité sur<br />ton activité</span>
            </div>
            <div>
              <strong>Des heures</strong>
              <span>récupérées<br />chaque semaine</span>
            </div>
          </div>

          <div className="cb2-bottom-left">
            <div>
              <strong><Star size={16} style={{display: 'inline', marginRight: '6px', color: '#f97316'}} />Un cockpit pensé pour les artisans et entreprises du bâtiment.</strong>
              <span>Simple, complet, puissant.</span>
            </div>
            <Link to="/pro/fonctionnalites">Découvrir toutes les fonctionnalités →</Link>
          </div>
        </div>

        <div className="cb2-right">
          <div className="cb2-right-header">
            <h3>Tout ce dont tu as besoin. Réuni en un seul endroit.</h3>
            <p>Ton cockpit te fait gagner du temps sur tout ce qui te faisait perdre du temps.</p>
          </div>

          <div className="cb2-features-grid">
            <article className="cb2-card">
              <div className="cb2-card-icon blue">
                <FileText size={24} color="#3b82f6" />
              </div>
              <h4>Devis en 1 minute</h4>
              <p>Parle à ton assistant, il crée ton devis complet prêt à envoyer.</p>
              <div className="cb2-mini-ui">
                <div className="cb2-ui-title">Nouveau devis</div>
                <div className="cb2-voice">Créer un devis pour une rénovation complète...</div>
                <div className="cb2-price">8 450 € HT <span>Prêt à envoyer</span></div>
              </div>
            </article>

            <article className="cb2-card">
              <div className="cb2-card-icon green">
                <CheckCircle size={24} color="#22c55e" />
              </div>
              <h4>Comptes rendus automatiques</h4>
              <p>Un compte rendu complet généré automatiquement dès que tu quittes un chantier.</p>
              <div className="cb2-mini-ui">
                <div className="cb2-ui-title">Compte rendu — Villa Contemporaine <span>Terminé</span></div>
                <ul>
                  <li>Avancement du chantier</li>
                  <li>Tâches réalisées</li>
                  <li>Photos & pièces jointes</li>
                  <li>Prochaines étapes</li>
                </ul>
              </div>
            </article>

            <article className="cb2-card">
              <div className="cb2-card-icon orange">
                <MapPin size={24} color="#f97316" />
              </div>
              <h4>Suivi chantier en temps réel</h4>
              <p>Avancement, photos, équipes, heures passées… tout est à jour en instantané.</p>
              <div className="cb2-mini-ui">
                <div className="cb2-ui-title">Villa Contemporaine — Lyon <span>En cours</span></div>
                <div className="cb2-progress"><i></i></div>
                <div className="cb2-thumbs">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </article>

            <article className="cb2-card">
              <div className="cb2-card-icon yellow">
                <Users size={24} color="#f59e0b" />
              </div>
              <h4>Gestion d'équipe simplifiée</h4>
              <p>Planning, présence, heures, tâches… tout est organisé et suivi.</p>
              <div className="cb2-mini-ui">
                <div className="cb2-team-row"><span>Thomas</span><strong>7h30</strong></div>
                <div className="cb2-team-row"><span>Lucas</span><strong>8h00</strong></div>
                <div className="cb2-team-row"><span>Julien</span><strong>7h00</strong></div>
              </div>
            </article>

            <article className="cb2-card">
              <div className="cb2-card-icon red">
                <Receipt size={24} color="#ef4444" />
              </div>
              <h4>Relances clients automatiques</h4>
              <p>Fini les impayés et les oublis. Ton cockpit relance pour toi.</p>
              <div className="cb2-mini-ui">
                <div className="cb2-reminder"><span>Maison Martin</span><em>Relance 1</em></div>
                <div className="cb2-reminder"><span>SCI Dupont</span><em>Relance 2</em></div>
                <div className="cb2-reminder"><span>Entreprise Durand</span><em>Relance 1</em></div>
              </div>
            </article>

            <article className="cb2-card">
              <div className="cb2-card-icon purple">
                <Bot size={24} color="#7c3aed" />
              </div>
              <h4>Assistant IA à ton service</h4>
              <p>Pose-lui une question, il t'aide, prend des actions et t'oriente.</p>
              <div className="cb2-ai-bubble">
                Peux-tu me faire un point sur le chantier Villa Contemporaine ?
              </div>
              <div className="cb2-ai-answer">
                ✓ Avancement : 68%<br />
                ✓ 3 tâches en retard<br />
                ✓ Prochaine étape : pose des menuiseries
              </div>
            </article>
          </div>

        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// FOUNDER BANNER - Bandeau premium fondateur
// ═══════════════════════════════════════════════════════════════════════════════
const FounderBanner = () => {
  return (
    <motion.section 
      className="founder-banner"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="founder-container">
        <div className="founder-left">
          <img 
            src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/l6ybtiux_14069.jpg" 
            alt="Romain Trallero" 
            className="founder-photo"
          />
          <div className="founder-trophy">
            <Trophy />
          </div>
        </div>

        <div className="founder-center">
          <h3 className="founder-title">
            Conçu par un entrepreneur<br />
            pour les entrepreneurs.
          </h3>
          <div className="founder-name">Romain Trallero</div>
          <div className="founder-sub">
            17 ans dans le bâtiment<br />
            +1500 piscines construites réparties sur 7 pays.
          </div>
        </div>

        <div className="founder-right">
          <Link to="/a-propos" className="founder-btn">
            En savoir plus sur son parcours →
          </Link>
        </div>
      </div>
    </motion.section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// COLLABORATION SECTION - Section premium Collaboration
// ═══════════════════════════════════════════════════════════════════════════════
const CollaborationSection = () => {
  return (
    <section className="collab-section">
      <div className="collab-container">
        {/* LEFT COLUMN */}
        <motion.div 
          className="collab-left"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="collab-badge">
            <Users size={16} />
            COLLABORATION
          </div>

          <h2 className="collab-title">
            Toute ton équipe<br />
            travaille au même endroit.
          </h2>
          <p className="collab-subtitle">Fini WhatsApp.</p>

          <p className="collab-desc">
            Crée des feeds par chantier ou par projet.<br />
            Invite tes équipes, clients, fournisseurs, sous-traitants...<br />
            Partage infos, photos, fichiers, tâches et événements.<br />
            <strong>Tout est centralisé, traçable et accessible.</strong>
          </p>

          {/* Comparison Cards */}
          <div className="collab-comparison">
            <div className="collab-card avant">
              <div className="collab-card-title avant">
                <XCircle size={18} />
                Avant
              </div>
              <ul>
                <li><X size={14} /> Messages dispersés</li>
                <li><X size={14} /> Informations perdues</li>
                <li><X size={14} /> Fichiers introuvables</li>
                <li><X size={14} /> Décisions pas tracées</li>
                <li><X size={14} /> Perte de temps</li>
              </ul>
            </div>

            <div className="collab-card avec">
              <div className="collab-card-title avec">
                <CheckCircle size={18} />
                Avec le Cockpit
              </div>
              <ul>
                <li><Check size={14} /> Un feed par projet</li>
                <li><Check size={14} /> Infos centralisées</li>
                <li><Check size={14} /> Tâches attribuées</li>
                <li><Check size={14} /> Historique complet</li>
                <li><Check size={14} /> Équipe alignée</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* RIGHT COLUMN - MOCKUP */}
        <motion.div 
          className="collab-right"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="collab-mockup">
            {/* Header */}
            <div className="collab-mockup-header">
              <div className="collab-mockup-title">
                <FileText size={18} />
                Villa Contemporaine – Lyon
                <ChevronDown size={14} />
              </div>
              <div className="collab-header-right">
                <div className="collab-avatars">
                  <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face" className="collab-avatar" alt="" />
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face" className="collab-avatar" alt="" />
                  <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face" className="collab-avatar" alt="" />
                  <div className="collab-avatar-more">+3</div>
                </div>
                <button className="collab-invite-btn">
                  <UserPlus size={14} />
                  Inviter
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="collab-tabs">
              <div className="collab-tab active">Feed</div>
              <div className="collab-tab">Tâches</div>
              <div className="collab-tab">Fichiers</div>
              <div className="collab-tab">Événements</div>
              <div className="collab-tab">Membres</div>
            </div>

            {/* Main Content */}
            <div className="collab-main">
              {/* Sidebar Left */}
              <div className="collab-sidebar-left">
                <div className="collab-sidebar-section">
                  <div className="collab-sidebar-title">
                    Mes feeds
                    <Plus size={14} />
                  </div>
                  <div className="collab-feed-item active">
                    <span className="collab-feed-dot orange"></span>
                    Villa Contemporaine
                  </div>
                  <div className="collab-feed-item">
                    <span className="collab-feed-dot blue"></span>
                    Rénovation Extension
                  </div>
                  <div className="collab-feed-item">
                    <span className="collab-feed-dot green"></span>
                    Piscine Carrelage
                  </div>
                  <div className="collab-feed-item">
                    <span className="collab-feed-dot purple"></span>
                    Villa d'Architecte
                  </div>
                </div>

                <div className="collab-sidebar-section">
                  <div className="collab-sidebar-title">Équipes</div>
                  <div className="collab-feed-item">
                    <Users size={14} />
                    Équipe Maçonnerie
                  </div>
                  <div className="collab-feed-item">
                    <Users size={14} />
                    Équipe Plomberie
                  </div>
                  <div className="collab-feed-item">
                    <Users size={14} />
                    Équipe Menuiserie
                  </div>
                </div>

                <div className="collab-sidebar-section">
                  <div className="collab-sidebar-title">Contacts</div>
                  <div className="collab-feed-item">
                    <User size={14} />
                    Clients
                  </div>
                  <div className="collab-feed-item">
                    <User size={14} />
                    Fournisseurs
                  </div>
                  <div className="collab-feed-item">
                    <User size={14} />
                    Sous-traitants
                  </div>
                </div>
              </div>

              {/* Feed Center */}
              <div className="collab-feed-center">
                {/* Input Box */}
                <div className="collab-input-box">
                  <div className="collab-input-placeholder">
                    <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face" alt="" />
                    Écrire un message, partager une photo, un document...
                  </div>
                  <div className="collab-input-actions">
                    <button className="collab-input-action"><Camera size={14} /> Photo</button>
                    <button className="collab-input-action"><FileText size={14} /> Fichier</button>
                    <button className="collab-input-action"><CheckSquare size={14} /> Tâche</button>
                    <button className="collab-input-action"><Calendar size={14} /> Événement</button>
                  </div>
                </div>

                {/* Post */}
                <div className="collab-post">
                  <div className="collab-post-header">
                    <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face" className="collab-post-avatar" alt="" />
                    <div className="collab-post-info">
                      <div className="collab-post-name">Thomas Martin <span className="collab-post-role">Conducteur de travaux</span></div>
                    </div>
                    <div className="collab-post-time">Aujourd'hui à 10:42</div>
                  </div>
                  <div className="collab-post-content">
                    Coulage de la dalle terminé ce matin.<br />
                    Voici quelques photos du chantier.
                  </div>
                  <div className="collab-post-images">
                    <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=200&h=150&fit=crop" className="collab-post-img" alt="" />
                    <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=200&h=150&fit=crop" className="collab-post-img" alt="" />
                    <img src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=200&h=150&fit=crop" className="collab-post-img" alt="" />
                  </div>
                  <div className="collab-post-reactions">
                    <span><Heart size={14} fill="#EF4444" color="#EF4444" /> 12</span>
                    <span><MessageCircle size={14} /> 4 commentaires</span>
                    <span><Eye size={14} /> Vu par 8 personnes</span>
                  </div>
                </div>

                {/* Comment */}
                <div className="collab-comment">
                  <div className="collab-post-header">
                    <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face" className="collab-post-avatar" alt="" />
                    <div className="collab-post-info">
                      <div className="collab-post-name">Client – M. Durand <span className="collab-comment-badge">Client</span></div>
                    </div>
                    <div className="collab-post-time">Aujourd'hui à 11:15</div>
                  </div>
                  <div className="collab-post-content">
                    Top 👍 très propre, bravo à l'équipe !
                  </div>
                </div>

                {/* Task */}
                <div className="collab-task">
                  <div className="collab-task-header">
                    <span className="collab-task-badge"><CheckSquare size={12} /> Tâche attribuée</span>
                    <span className="collab-task-time">Aujourd'hui à 11:20</span>
                  </div>
                  <div className="collab-task-title">Préparer la livraison des menuiseries</div>
                  <div className="collab-task-meta">
                    <span>Assignée à : Lucas Morel</span>
                    <span className="collab-task-deadline">Échéance : 24 mai</span>
                  </div>
                </div>

                {/* Event */}
                <div className="collab-event">
                  <div className="collab-event-header">
                    <span className="collab-event-badge"><Calendar size={12} /> Événement créé</span>
                    <span className="collab-task-time">Aujourd'hui à 11:30</span>
                  </div>
                  <div className="collab-event-title">Livraison menuiseries</div>
                  <div className="collab-event-date">Vendredi 24 mai à 8h00</div>
                  <span className="collab-event-btn">Voir l'événement</span>
                </div>

                {/* Fournisseur Comment */}
                <div className="collab-comment">
                  <div className="collab-post-header">
                    <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face" className="collab-post-avatar" alt="" />
                    <div className="collab-post-info">
                      <div className="collab-post-name">Fournisseur – Menuiseries Pro <span className="collab-fournisseur-badge">Fournisseur</span></div>
                    </div>
                    <div className="collab-post-time">Aujourd'hui à 11:45</div>
                  </div>
                  <div className="collab-post-content">
                    Nous confirmons la livraison vendredi matin.<br />
                    Bonne journée à tous.
                  </div>
                </div>
              </div>

              {/* Sidebar Right */}
              <div className="collab-sidebar-right">
                <div className="collab-about-box">
                  <div className="collab-about-title">À propos du feed</div>
                  <div className="collab-about-text">
                    Espace dédié au chantier<br />
                    Villa Contemporaine à Lyon.
                  </div>
                </div>

                <div className="collab-members-header">
                  <span className="collab-members-title">Membres (8)</span>
                  <span className="collab-members-link">Voir tout</span>
                </div>
                <div className="collab-members-grid">
                  <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face" className="collab-member-avatar" alt="" />
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face" className="collab-member-avatar" alt="" />
                  <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face" className="collab-member-avatar" alt="" />
                  <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face" className="collab-member-avatar" alt="" />
                  <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face" className="collab-member-avatar" alt="" />
                </div>
                <button className="collab-manage-btn">Gérer les membres</button>

                <div className="collab-sidebar-title">Derniers fichiers</div>
                <div className="collab-files-list">
                  <div className="collab-file-item">
                    <div className="collab-file-icon pdf"><FileText size={14} /></div>
                    <div className="collab-file-info">
                      <div className="collab-file-name">Plan étage.pdf</div>
                      <div className="collab-file-meta">Ajouté par Thomas M.</div>
                    </div>
                  </div>
                  <div className="collab-file-item">
                    <div className="collab-file-icon pdf"><FileText size={14} /></div>
                    <div className="collab-file-info">
                      <div className="collab-file-name">Devis menuiseries.pdf</div>
                      <div className="collab-file-meta">Ajouté par Lucas M.</div>
                    </div>
                  </div>
                  <div className="collab-file-item">
                    <div className="collab-file-icon img"><Image size={14} /></div>
                    <div className="collab-file-info">
                      <div className="collab-file-name">Photo_chantier_23.jpg</div>
                      <div className="collab-file-meta">Ajouté par Thomas M.</div>
                    </div>
                  </div>
                </div>
                <button className="collab-see-all-btn">Voir tous les fichiers</button>

                <div className="collab-next-event">
                  <div className="collab-next-event-title">Prochain événement</div>
                  <div className="collab-next-event-item">
                    <div className="collab-next-event-icon"><Calendar size={14} /></div>
                    <div>
                      <div className="collab-next-event-name">Livraison menuiseries</div>
                      <div className="collab-next-event-date">Vendredi 24 mai<br />à 08h00</div>
                    </div>
                  </div>
                  <button className="collab-see-all-btn" style={{ marginTop: '10px', marginBottom: 0 }}>Voir tous les événements</button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Features Bar - PLEINE LARGEUR */}
      <div className="collab-features-bar">
        <div className="collab-feature">
          <div className="collab-feature-icon"><MessageSquare size={20} /></div>
          <div className="collab-feature-text">
            <h4>Posts & discussions</h4>
            <p>Partage infos, photos et avancements.</p>
          </div>
        </div>
        <div className="collab-feature">
          <div className="collab-feature-icon"><CheckSquare size={20} /></div>
          <div className="collab-feature-text">
            <h4>Tâches & responsabilités</h4>
            <p>Attribue et valide les tâches.</p>
          </div>
        </div>
        <div className="collab-feature">
          <div className="collab-feature-icon"><AtSign size={20} /></div>
          <div className="collab-feature-text">
            <h4>Mentions & notifications</h4>
            <p>Tague et reste informé.</p>
          </div>
        </div>
        <div className="collab-feature">
          <div className="collab-feature-icon"><Calendar size={20} /></div>
          <div className="collab-feature-text">
            <h4>Événements</h4>
            <p>Réunions, livraisons, étapes clés.</p>
          </div>
        </div>
        <div className="collab-feature">
          <div className="collab-feature-icon"><Heart size={20} /></div>
          <div className="collab-feature-text">
            <h4>J'aime & réactions</h4>
            <p>Like, commente, réagis.</p>
          </div>
        </div>
        <div className="collab-feature">
          <div className="collab-feature-icon"><Shield size={20} /></div>
          <div className="collab-feature-text">
            <h4>Sécurisé & traçable</h4>
            <p>Tout est enregistré.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SYSTÈME INTELLIGENT - Section réseau connecté
// ═══════════════════════════════════════════════════════════════════════════════
const SystemSection = () => {
  return (
    <section className="system-section">
      <div className="system-container">
        {/* MAIN 3-COLUMN LAYOUT */}
        <div className="system-main">
          {/* LEFT COLUMN - Title + Side Blocks */}
          <div className="system-left">
            <div className="system-badge">
              <Briefcase size={14} />
              SYSTÈME INTELLIGENT
            </div>
            <h2 className="system-title">
              Ton entreprise<br />
              devient un système.
            </h2>
            <p className="system-desc">
              Équipes, IA, clients, chantiers...<br />
              tout est connecté et piloté depuis<br />
              un seul endroit.
            </p>

            <div className="system-side-blocks">
              <div className="system-side-block">
                <div className="system-side-icon blue">
                  <Share2 size={20} />
                </div>
                <div className="system-side-text">
                  <h4>Tout est connecté</h4>
                  <p>Chaque module échange ses données en temps réel.</p>
                </div>
              </div>
              <div className="system-side-block">
                <div className="system-side-icon green">
                  <CheckCircle size={20} />
                </div>
                <div className="system-side-text">
                  <h4>Tout est piloté</h4>
                  <p>Décisions, actions, suivi : vous gardez le contrôle.</p>
                </div>
              </div>
              <div className="system-side-block">
                <div className="system-side-icon purple">
                  <Shield size={20} />
                </div>
                <div className="system-side-text">
                  <h4>Tout est sécurisé</h4>
                  <p>Vos données sont protégées et hébergées en Europe.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CENTER - Network Visualization */}
          <div className="system-center">
            {/* SVG Connection Lines - Curved */}
            <svg className="system-svg" viewBox="0 0 700 680" preserveAspectRatio="xMidYMid meet">
              <path d="M350 340 Q350 200 350 80" />
              <path d="M350 340 Q250 280 140 180" />
              <path d="M350 340 Q450 280 560 180" />
              <path d="M350 340 Q200 340 80 340" />
              <path d="M350 340 Q500 340 620 340" />
              <path d="M350 340 Q250 450 160 530" />
              <path d="M350 340 Q350 480 350 580" />
              <path d="M350 340 Q450 450 540 530" />
            </svg>

            {/* Central Cockpit */}
            <div className="system-cockpit">
              <div className="system-cockpit-icon">
                <Briefcase size={22} />
              </div>
              <h3>Cockpit Bâtiment</h3>
              <span className="system-cockpit-badge">IA intégrée</span>
              <p className="system-cockpit-subtitle">Votre centre de pilotage<br />tout-en-un</p>
              
              <div className="system-cockpit-stats">
                <div className="system-cockpit-stat">
                  <div className="system-cockpit-stat-label">Chantiers</div>
                  <div className="system-cockpit-stat-value">24</div>
                  <div className="system-cockpit-stat-change">+12%</div>
                </div>
                <div className="system-cockpit-stat">
                  <div className="system-cockpit-stat-label">Devis</div>
                  <div className="system-cockpit-stat-value">18</div>
                  <div className="system-cockpit-stat-change">+8%</div>
                </div>
                <div className="system-cockpit-stat">
                  <div className="system-cockpit-stat-label">Factures</div>
                  <div className="system-cockpit-stat-value">32</div>
                  <div className="system-cockpit-stat-change">+15%</div>
                </div>
                <div className="system-cockpit-stat">
                  <div className="system-cockpit-stat-label">Tâches</div>
                  <div className="system-cockpit-stat-value">56</div>
                  <div className="system-cockpit-stat-change">+22%</div>
                </div>
              </div>

              <div className="system-cockpit-sync">
                <span className="system-cockpit-sync-dot"></span>
                Données synchronisées en temps réel
              </div>
            </div>

            {/* Module: Client (TOP) */}
            <div className="system-module client">
              <div className="system-module-header">
                <div className="system-module-icon teal"><User size={16} /></div>
                <h4>Client</h4>
              </div>
              <p className="system-module-desc">Suivi, échanges, validation</p>
              <div className="system-module-avatars">
                <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=60&h=60&fit=crop&crop=face" className="system-module-avatar" alt="" />
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face" className="system-module-avatar" alt="" />
                <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face" className="system-module-avatar" alt="" />
                <div className="system-module-badge">+12</div>
              </div>
            </div>

            {/* Module: Devis IA (TOP LEFT) */}
            <div className="system-module devis">
              <div className="system-module-header">
                <div className="system-module-icon blue"><FileText size={16} /></div>
                <h4>Devis IA</h4>
              </div>
              <p className="system-module-desc">Devis rapides, précis et automatisés</p>
              <div className="system-module-stats">
                <div>
                  <div className="system-module-stat-label">En cours</div>
                  <div className="system-module-stat-value">12</div>
                </div>
                <div>
                  <div className="system-module-stat-label">Acceptés</div>
                  <div className="system-module-stat-value">7</div>
                </div>
              </div>
            </div>

            {/* Module: Équipe (TOP RIGHT) */}
            <div className="system-module equipe">
              <div className="system-module-header">
                <div className="system-module-icon indigo"><Users size={16} /></div>
                <h4>Équipe</h4>
              </div>
              <p className="system-module-desc">Collaboration, tâches et communication</p>
              <div className="system-module-avatars">
                <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&h=60&fit=crop&crop=face" className="system-module-avatar" alt="" />
                <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face" className="system-module-avatar" alt="" />
                <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=60&h=60&fit=crop&crop=face" className="system-module-avatar" alt="" />
                <div className="system-module-badge">+8</div>
              </div>
            </div>

            {/* Module: Assistants IA (LEFT) */}
            <div className="system-module assistants">
              <div className="system-module-header">
                <div className="system-module-icon purple"><Bot size={16} /></div>
                <h4>Assistants IA</h4>
              </div>
              <p className="system-module-desc">Agents spécialisés pour chaque métier</p>
              <div className="system-module-list">
                <div className="system-module-list-item">Assistant Devis</div>
                <div className="system-module-list-item">Assistant Planning</div>
                <div className="system-module-list-item">Assistant Achats</div>
                <div className="system-module-list-item muted">+ 6 autres assistants</div>
              </div>
            </div>

            {/* Module: Super Assistant (RIGHT) */}
            <div className="system-module super-assistant">
              <div className="system-module-header">
                <div className="system-module-icon orange"><Rocket size={16} /></div>
                <h4>Super Assistant</h4>
              </div>
              <p className="system-module-desc">Votre copilote IA pour décider et agir</p>
              <div className="system-module-list">
                <div className="system-module-list-item">✓ Analyse et recommandations</div>
                <div className="system-module-list-item">✓ Alertes et priorités</div>
                <div className="system-module-list-item">✓ Aide à la décision</div>
              </div>
            </div>

            {/* Module: Chantiers (BOTTOM LEFT) */}
            <div className="system-module chantiers">
              <div className="system-module-header">
                <div className="system-module-icon yellow"><HardHat size={16} /></div>
                <h4>Chantiers</h4>
              </div>
              <p className="system-module-desc">Suivi en temps réel et traçabilité</p>
              <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=200&h=80&fit=crop" className="system-module-img" alt="" />
              <div className="system-module-progress">
                <span>Avancement global</span>
                <strong>68%</strong>
              </div>
            </div>

            {/* Module: Paiement (BOTTOM CENTER) */}
            <div className="system-module paiement">
              <div className="system-module-header">
                <div className="system-module-icon green"><Receipt size={16} /></div>
                <h4>Paiement</h4>
              </div>
              <p className="system-module-desc">Facturation, encaissements et relances</p>
              <div className="system-module-meta">Encaissements (mois)</div>
              <div className="system-module-amount">128 450 € <span>+18%</span></div>
            </div>

            {/* Module: Marketing (BOTTOM RIGHT) */}
            <div className="system-module marketing">
              <div className="system-module-header">
                <div className="system-module-icon rose"><Megaphone size={16} /></div>
                <h4>Marketing / Relances</h4>
              </div>
              <p className="system-module-desc">Relances automatiques et acquisition</p>
              <div className="system-module-stats">
                <div>
                  <div className="system-module-stat-label">Relances en cours</div>
                  <div className="system-module-stat-value">23</div>
                </div>
                <div>
                  <div className="system-module-stat-label">Nouveaux leads</div>
                  <div className="system-module-stat-value">18</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Side Blocks */}
          <div className="system-right">
            <div className="system-side-blocks">
              <div className="system-side-block">
                <div className="system-side-icon blue">
                  <Briefcase size={20} />
                </div>
                <div className="system-side-text">
                  <h4>Intégré</h4>
                  <p>Toutes vos activités réunies au même endroit.</p>
                </div>
              </div>
              <div className="system-side-block">
                <div className="system-side-icon orange">
                  <Bot size={20} />
                </div>
                <div className="system-side-text">
                  <h4>Intelligent</h4>
                  <p>L'IA analyse, anticipe et vous fait gagner du temps.</p>
                </div>
              </div>
              <div className="system-side-block">
                <div className="system-side-icon teal">
                  <TrendingUp size={20} />
                </div>
                <div className="system-side-text">
                  <h4>Évolutif</h4>
                  <p>Un système qui grandit avec votre entreprise.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Banner */}
        <div className="system-banner">
          <div className="system-banner-left">
            <div className="system-banner-icon">
              <CheckCircle size={24} />
            </div>
            <div>
              <h4 className="system-banner-title">Un seul cockpit. Zéro perte d'information.</h4>
              <p className="system-banner-desc">Fini les outils déconnectés, les fichiers éparpillés et les erreurs.</p>
            </div>
          </div>
          <div className="system-banner-stats">
            <div className="system-banner-stat">
              <Check size={20} />
              <div className="system-banner-stat-text">
                <h5>Gain de temps</h5>
                <p>jusqu'à 30%</p>
              </div>
            </div>
            <div className="system-banner-stat">
              <Check size={20} />
              <div className="system-banner-stat-text">
                <h5>Moins d'erreurs</h5>
                <p>plus de sérénité</p>
              </div>
            </div>
            <div className="system-banner-stat">
              <Check size={20} />
              <div className="system-banner-stat-text">
                <h5>Plus de rentabilité</h5>
                <p>plus de croissance</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMMANDE VOCALE - Section premium
// ═══════════════════════════════════════════════════════════════════════════════
const VoiceSection = () => {
  return (
    <section className="voice-section">
      <div className="voice-container">
        {/* Process Steps */}
        <div className="voice-steps">
          <div className="voice-step">
            <div className="voice-step-icon blue"><Mic size={24} /></div>
            <div className="voice-step-label">Tu parles</div>
          </div>
          <div className="voice-step">
            <div className="voice-step-icon purple"><Bot size={24} /></div>
            <div className="voice-step-label">L'IA comprend</div>
          </div>
          <div className="voice-step">
            <div className="voice-step-icon teal"><FolderOpen size={24} /></div>
            <div className="voice-step-label">Structure</div>
          </div>
          <div className="voice-step">
            <div className="voice-step-icon orange"><Zap size={24} /></div>
            <div className="voice-step-label">Agit</div>
          </div>
          <div className="voice-step">
            <div className="voice-step-icon green"><CheckCircle size={24} /></div>
            <div className="voice-step-label">Vous êtes à jour</div>
          </div>
        </div>

        {/* Main Layout */}
        <div className="voice-main">
          {/* Left Column */}
          <div className="voice-left">
            <div className="voice-badge">
              <Mic size={14} />
              COMMANDE VOCALE
            </div>
            <h2 className="voice-title">Tu parles.</h2>
            <h2 className="voice-title voice-title-orange">Le cockpit exécute.</h2>
            
            <p className="voice-subtitle">
              Devis, tâches, comptes rendus, réunions :<br />
              <span className="voice-subtitle-light">tout peut partir de ta voix.</span>
            </p>

            <p className="voice-desc">
              Sur un chantier, en voiture, entre deux rendez-vous,<br />
              tu n'as plus besoin d'écrire.<br />
              Tu parles à ton Super Assistant,<br />
              il comprend, classe, crée, résume<br />
              et déclenche les bonnes actions.
            </p>

            {/* Voice Command Card */}
            <div className="voice-card">
              <div className="voice-card-content">
                <img 
                  src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/l6ybtiux_14069.jpg"
                  alt="User" 
                  className="voice-card-avatar"
                />
                <div className="voice-card-text">
                  <div className="voice-card-quote">"</div>
                  <p className="voice-card-message">
                    Fais-moi le compte rendu du chantier Villa Contemporaine, les tâches à faire et informe le client de l'avancement.
                  </p>
                </div>
              </div>
              <div className="voice-card-wave">
                <span></span><span></span><span></span><span></span><span></span>
                <span></span><span></span><span></span><span></span><span></span>
              </div>
              <div className="voice-card-mic">
                <Mic size={22} />
              </div>
            </div>

            {/* Super Assistant Listening */}
            <div className="voice-listening">
              <div className="voice-listening-icon">
                <Bot size={18} />
              </div>
              <div className="voice-listening-text">
                <h5>Super Assistant à l'écoute...</h5>
                <p>Analyse de la demande en cours</p>
              </div>
              <div className="voice-listening-wave">
                <span></span><span></span><span></span><span></span>
                <span></span><span></span><span></span><span></span>
              </div>
            </div>
          </div>

          {/* Center Flow */}
          <div className="voice-center">
            <div className="voice-flow">
              <span className="voice-flow-arrow">→</span>
              <div className="voice-ai-icon">
                <Bot size={36} />
                <span className="voice-ai-badge">IA</span>
              </div>
              <span className="voice-flow-arrow">→</span>
            </div>
            <div className="voice-ai-labels">
              <p>Comprend</p>
              <p>Organise</p>
              <p>Exécute</p>
            </div>
            <div className="voice-ai-check">
              <Check size={16} />
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="voice-right">
            <div className="voice-right-header">
              <h3 className="voice-right-title">Résultat généré automatiquement</h3>
              <span className="voice-right-badge">
                <CheckCircle size={14} />
                Terminé
              </span>
            </div>

            {/* Result: Compte rendu */}
            <div className="voice-result">
              <div className="voice-result-header">
                <div className="voice-result-icon orange"><FileText size={18} /></div>
                <span className="voice-result-title">Compte rendu généré</span>
              </div>
              <p className="voice-result-content">
                Avancement général : 68%<br />
                Points réalisés : Dalle terminée, ferraillage en cours<br />
                Points à suivre : Livraison menuiseries vendredi
              </p>
              <p className="voice-result-meta">Document PDF généré • 2 pages</p>
            </div>

            {/* Result: Tâches */}
            <div className="voice-result">
              <div className="voice-result-header">
                <div className="voice-result-icon blue"><CheckSquare size={18} /></div>
                <span className="voice-result-title">Tâches créées</span>
              </div>
              <div className="voice-result-tasks">
                <div className="voice-result-task">Finaliser ferraillage → Lucas</div>
                <div className="voice-result-task">Préparer réception menuiseries → Thomas</div>
                <div className="voice-result-task">Valider planning semaine 22 → Équipe</div>
              </div>
            </div>

            {/* Result: Équipe */}
            <div className="voice-result">
              <div className="voice-result-header">
                <div className="voice-result-icon purple"><Users size={18} /></div>
                <span className="voice-result-title">Équipe notifiée</span>
              </div>
              <div className="voice-result-avatars">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face" className="voice-result-avatar" alt="" />
                <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&h=60&fit=crop&crop=face" className="voice-result-avatar" alt="" />
                <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face" className="voice-result-avatar" alt="" />
                <div className="voice-result-avatar-more">+4</div>
              </div>
            </div>

            {/* Result: Client */}
            <div className="voice-result">
              <div className="voice-result-header">
                <div className="voice-result-icon teal"><Mail size={18} /></div>
                <span className="voice-result-title">Client informé</span>
              </div>
              <p className="voice-result-content">
                Email envoyé à M. Durand avec résumé + photos
              </p>
              <span className="voice-result-sent">
                <Check size={12} /> Envoyé
              </span>
            </div>

            {/* Result: Photos */}
            <div className="voice-result">
              <div className="voice-result-header">
                <div className="voice-result-icon green"><Image size={18} /></div>
                <span className="voice-result-title">Photos associées</span>
              </div>
              <div className="voice-result-photos">
                <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=120&h=90&fit=crop" className="voice-result-photo" alt="" />
                <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=120&h=90&fit=crop" className="voice-result-photo" alt="" />
                <img src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=120&h=90&fit=crop" className="voice-result-photo" alt="" />
                <div className="voice-result-photo-more">+5</div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Bar */}
        <div className="voice-features">
          <div className="voice-feature">
            <div className="voice-feature-icon orange"><FileText size={18} /></div>
            <div className="voice-feature-text">
              <h5>Devis à la voix</h5>
              <p>Dicte, l'IA génère</p>
            </div>
          </div>
          <div className="voice-feature">
            <div className="voice-feature-icon blue"><Volume2 size={18} /></div>
            <div className="voice-feature-text">
              <h5>Réunions enregistrées</h5>
              <p>Transcription auto</p>
            </div>
          </div>
          <div className="voice-feature">
            <div className="voice-feature-icon teal"><FileText size={18} /></div>
            <div className="voice-feature-text">
              <h5>Comptes rendus auto</h5>
              <p>Générés en 1 clic</p>
            </div>
          </div>
          <div className="voice-feature">
            <div className="voice-feature-icon green"><CheckSquare size={18} /></div>
            <div className="voice-feature-text">
              <h5>Tâches créées auto</h5>
              <p>Attribuées à l'équipe</p>
            </div>
          </div>
          <div className="voice-feature">
            <div className="voice-feature-icon purple"><Bell size={18} /></div>
            <div className="voice-feature-text">
              <h5>Équipes informées</h5>
              <p>Notifications ciblées</p>
            </div>
          </div>
          <div className="voice-feature">
            <div className="voice-feature-icon blue"><Clock size={18} /></div>
            <div className="voice-feature-text">
              <h5>Historique conservé</h5>
              <p>Tout est tracé</p>
            </div>
          </div>
        </div>

        {/* Bottom Banner */}
        <div className="voice-banner">
          <div className="voice-banner-left">
            <div className="voice-banner-icon">
              <Mic size={24} />
            </div>
            <div>
              <h4 className="voice-banner-title">Fini les notes perdues. Fini les oublis.</h4>
              <p className="voice-banner-desc">Tu parles, tout est structuré.</p>
            </div>
          </div>
          <div className="voice-banner-stats">
            <div className="voice-banner-stat">
              <div className="voice-banner-stat-icon blue"><Clock size={18} /></div>
              <div className="voice-banner-stat-text">
                <h5>Jusqu'à 3h</h5>
                <p>gagnées / semaine</p>
              </div>
            </div>
            <div className="voice-banner-stat">
              <div className="voice-banner-stat-icon green"><CheckCircle size={18} /></div>
              <div className="voice-banner-stat-text">
                <h5>Moins d'erreurs</h5>
                <p>plus de précision</p>
              </div>
            </div>
            <div className="voice-banner-stat">
              <div className="voice-banner-stat-icon purple"><Zap size={18} /></div>
              <div className="voice-banner-stat-text">
                <h5>Plus de réactivité</h5>
                <p>équipe synchronisée</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PORTAIL CLIENT - Section premium
// ═══════════════════════════════════════════════════════════════════════════════
const PortalSection = () => {
  return (
    <section className="portal-section">
      <div className="portal-container">
        <div className="portal-main">
          {/* Left Column */}
          <div className="portal-left">
            <div className="portal-badge">
              <User size={14} />
              PORTAIL CLIENT
            </div>
            <h2 className="portal-title">Tes clients savent<br />où ça en est.</h2>
            <h2 className="portal-title portal-title-orange">Sans te déranger<br />toute la journée.</h2>
            
            <p className="portal-desc">
              Avancement, photos, documents, décisions, paiements : tout est accessible depuis leur espace client.
            </p>

            {/* Benefit Cards */}
            <div className="portal-benefits">
              <div className="portal-benefit">
                <div className="portal-benefit-icon teal"><Headphones size={20} /></div>
                <div className="portal-benefit-text">
                  <h4>Moins d'appels, plus de sérénité</h4>
                  <p>Tes clients trouvent les réponses seuls.</p>
                </div>
              </div>
              <div className="portal-benefit">
                <div className="portal-benefit-icon green"><Eye size={20} /></div>
                <div className="portal-benefit-text">
                  <h4>Transparence totale</h4>
                  <p>Chaque étape visible en temps réel.</p>
                </div>
              </div>
              <div className="portal-benefit">
                <div className="portal-benefit-icon yellow"><Star size={20} /></div>
                <div className="portal-benefit-text">
                  <h4>Image professionnelle renforcée</h4>
                  <p>Un portail moderne, à ton image.</p>
                </div>
              </div>
              <div className="portal-benefit">
                <div className="portal-benefit-icon orange"><Heart size={20} /></div>
                <div className="portal-benefit-text">
                  <h4>Clients plus satisfaits</h4>
                  <p>La confiance se construit dans la clarté.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Portal Mockup */}
          <div className="portal-right">
            {/* Header */}
            <div className="portal-header">
              <div className="portal-header-left">
                <Home size={20} />
                Portail client
              </div>
              <div className="portal-header-right">
                <div className="portal-header-icons">
                  <div className="portal-header-icon">
                    <Bell size={18} />
                    <span className="portal-header-icon-badge">3</span>
                  </div>
                </div>
                <div className="portal-header-user">
                  <div className="portal-header-user-info">
                    <div className="portal-header-user-greeting">Bonjour,</div>
                    <div className="portal-header-user-name">Jean Martin</div>
                  </div>
                  <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face" className="portal-header-avatar" alt="" />
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="portal-body">
              {/* Sidebar */}
              <div className="portal-sidebar">
                <div className="portal-nav-item active">
                  <Home size={16} />
                  Vue d'ensemble
                </div>
                <div className="portal-nav-item">
                  <TrendingUp size={16} />
                  Avancement
                </div>
                <div className="portal-nav-item">
                  <Image size={16} />
                  Photos
                </div>
                <div className="portal-nav-item">
                  <FileText size={16} />
                  Documents
                </div>
                <div className="portal-nav-item">
                  <CheckSquare size={16} />
                  Décisions
                </div>
                <div className="portal-nav-item">
                  <Receipt size={16} />
                  Paiements
                </div>
                <div className="portal-nav-item">
                  <MessageSquare size={16} />
                  Messages
                </div>
                <div className="portal-nav-item">
                  <Calendar size={16} />
                  Calendrier
                </div>
              </div>

              {/* Content */}
              <div className="portal-content">
                {/* Project Card */}
                <div className="portal-project">
                  <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=200&h=150&fit=crop" className="portal-project-img" alt="" />
                  <div className="portal-project-info">
                    <h3>Villa Contemporaine</h3>
                    <div className="portal-project-address">
                      <MapPin size={12} />
                      12 rue des Lilas, 69003 Lyon
                    </div>
                    <div className="portal-project-meta">
                      <span className="portal-project-badge">En cours</span>
                      <span className="portal-project-date">Livraison prévue : Août 2025</span>
                    </div>
                  </div>
                </div>

                {/* Grid Layout */}
                <div className="portal-grid">
                  <div className="portal-grid-left">
                    {/* Timeline */}
                    <div className="portal-box">
                      <div className="portal-box-header">
                        <span className="portal-box-title">Avancement du chantier</span>
                        <span className="portal-box-link">Voir détails</span>
                      </div>
                      <div className="portal-timeline">
                        <div className="portal-timeline-step">
                          <div className="portal-timeline-dot done"><Check size={12} /></div>
                          <div className="portal-timeline-label">Préparation</div>
                          <div className="portal-timeline-status done">Terminé</div>
                        </div>
                        <div className="portal-timeline-step">
                          <div className="portal-timeline-dot done"><Check size={12} /></div>
                          <div className="portal-timeline-label">Gros œuvre</div>
                          <div className="portal-timeline-status done">Terminé</div>
                        </div>
                        <div className="portal-timeline-step">
                          <div className="portal-timeline-dot current">3</div>
                          <div className="portal-timeline-label">Couverture</div>
                          <div className="portal-timeline-status current">En cours</div>
                        </div>
                        <div className="portal-timeline-step">
                          <div className="portal-timeline-dot pending">4</div>
                          <div className="portal-timeline-label">Cloisons</div>
                          <div className="portal-timeline-status">À venir</div>
                        </div>
                        <div className="portal-timeline-step">
                          <div className="portal-timeline-dot pending">5</div>
                          <div className="portal-timeline-label">Finitions</div>
                          <div className="portal-timeline-status">À venir</div>
                        </div>
                      </div>
                    </div>

                    {/* Photos */}
                    <div className="portal-box">
                      <div className="portal-box-header">
                        <span className="portal-box-title">Dernières photos</span>
                        <span className="portal-box-link">Voir tout</span>
                      </div>
                      <div className="portal-photos">
                        <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=140&h=100&fit=crop" className="portal-photo" alt="" />
                        <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=140&h=100&fit=crop" className="portal-photo" alt="" />
                        <img src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=140&h=100&fit=crop" className="portal-photo" alt="" />
                        <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=140&h=100&fit=crop" className="portal-photo" alt="" />
                      </div>
                    </div>

                    {/* Decisions */}
                    <div className="portal-box">
                      <div className="portal-box-header">
                        <span className="portal-box-title">Décisions en attente</span>
                      </div>
                      <div className="portal-decision">
                        <div className="portal-decision-text">
                          <div className="portal-decision-title">Choix du carrelage salle de bain</div>
                          <div className="portal-decision-date">À valider avant le 20 mai</div>
                        </div>
                        <button className="portal-decision-btn">Valider</button>
                      </div>
                    </div>
                  </div>

                  <div className="portal-grid-right">
                    {/* Documents */}
                    <div className="portal-box">
                      <div className="portal-box-header">
                        <span className="portal-box-title">Documents</span>
                      </div>
                      <div className="portal-docs">
                        <div className="portal-doc">
                          <div className="portal-doc-icon"><FileText size={14} /></div>
                          <div className="portal-doc-info">
                            <div className="portal-doc-name">Devis final.pdf</div>
                            <div className="portal-doc-meta">2.4 MB</div>
                          </div>
                        </div>
                        <div className="portal-doc">
                          <div className="portal-doc-icon"><FileText size={14} /></div>
                          <div className="portal-doc-info">
                            <div className="portal-doc-name">Plan étage.pdf</div>
                            <div className="portal-doc-meta">1.8 MB</div>
                          </div>
                        </div>
                        <div className="portal-doc">
                          <div className="portal-doc-icon"><FileText size={14} /></div>
                          <div className="portal-doc-info">
                            <div className="portal-doc-name">Facture acompte.pdf</div>
                            <div className="portal-doc-meta">0.5 MB</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Payments */}
                    <div className="portal-box">
                      <div className="portal-box-header">
                        <span className="portal-box-title">Paiements</span>
                      </div>
                      <div className="portal-payment-amount">12 450 € <span>TTC</span></div>
                      <div className="portal-payment-meta">
                        <span className="portal-payment-badge">Payé</span>
                        <span className="portal-payment-label">Acompte 30%</span>
                      </div>
                      <div className="portal-payment-date">Règlement effectué le 12 avril</div>
                    </div>

                    {/* Messages */}
                    <div className="portal-box">
                      <div className="portal-box-header">
                        <span className="portal-box-title">Messages</span>
                      </div>
                      <div className="portal-message">
                        <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face" className="portal-message-avatar" alt="" />
                        <div className="portal-message-content">
                          <div className="portal-message-header">
                            <span className="portal-message-name">Thomas</span>
                            <span className="portal-message-time">Il y a 2h</span>
                          </div>
                          <p className="portal-message-text">La dalle est terminée, voici les photos du jour.</p>
                        </div>
                      </div>
                    </div>

                    {/* Next Step */}
                    <div className="portal-box portal-next">
                      <div className="portal-box-header">
                        <span className="portal-box-title">Prochaine étape</span>
                      </div>
                      <div className="portal-next-item">
                        <div className="portal-next-icon"><Calendar size={16} /></div>
                        <div>
                          <div className="portal-next-title">Livraison menuiseries</div>
                          <div className="portal-next-date">Vendredi 24 mai à 8h00</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Bar */}
        <div className="portal-features">
          <div className="portal-feature">
            <div className="portal-feature-icon green"><TrendingUp size={22} /></div>
            <div className="portal-feature-text">
              <h5>Suivi en temps réel</h5>
              <p>Chaque étape du chantier accessible instantanément.</p>
            </div>
          </div>
          <div className="portal-feature">
            <div className="portal-feature-icon blue"><Image size={22} /></div>
            <div className="portal-feature-text">
              <h5>Photos et documents</h5>
              <p>Tout est centralisé et téléchargeable.</p>
            </div>
          </div>
          <div className="portal-feature">
            <div className="portal-feature-icon teal"><CheckSquare size={22} /></div>
            <div className="portal-feature-text">
              <h5>Décisions simplifiées</h5>
              <p>Validations en un clic depuis le portail.</p>
            </div>
          </div>
          <div className="portal-feature">
            <div className="portal-feature-icon orange"><Shield size={22} /></div>
            <div className="portal-feature-text">
              <h5>Données sécurisées</h5>
              <p>Accès protégé et hébergement en Europe.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION COÛTS & MARGES - RENTABILITÉ CHANTIER
// ═══════════════════════════════════════════════════════════════════════════════
const CoutsMargesSection = () => {
  return (
    <section className="couts-section">
      <style>{`
        .couts-section {
          width: 100%;
          background: #FFFFFF;
          padding: 120px 40px;
          font-family: 'Inter', 'DM Sans', sans-serif;
        }
        .couts-container {
          max-width: 1300px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1.5fr;
          gap: 80px;
          align-items: flex-start;
        }
        
        /* LEFT COLUMN */
        .couts-left {
          display: flex;
          flex-direction: column;
          gap: 32px;
        }
        .couts-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #FFF4ED;
          color: #F97316;
          border-radius: 999px;
          padding: 6px 14px;
          font-size: 13px;
          font-weight: 600;
          width: fit-content;
        }
        .couts-badge svg {
          width: 16px;
          height: 16px;
        }
        .couts-title {
          font-size: 42px;
          font-weight: 700;
          line-height: 1.2;
          color: #1E293B;
          margin: 0;
        }
        .couts-title span {
          color: #F97316;
          display: block;
        }
        .couts-subtitle {
          font-size: 18px;
          color: #64748B;
          line-height: 1.6;
          margin: 0;
        }
        .couts-paragraph {
          font-size: 16px;
          color: #64748B;
          line-height: 1.7;
          margin: 0;
        }
        
        /* BENEFITS STACK */
        .couts-benefits {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 8px;
        }
        .couts-benefit {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 16px;
          display: flex;
          gap: 12px;
          align-items: flex-start;
          transition: box-shadow 0.2s ease;
        }
        .couts-benefit:hover {
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        }
        .couts-benefit-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .couts-benefit-icon.orange { background: #FFF4ED; color: #F97316; }
        .couts-benefit-icon.blue { background: #EEF2FF; color: #6366F1; }
        .couts-benefit-icon.green { background: #ECFDF5; color: #22C55E; }
        .couts-benefit-icon.teal { background: #F0FDFA; color: #14B8A6; }
        .couts-benefit-icon.red { background: #FEF2F2; color: #EF4444; }
        .couts-benefit-text h4 {
          font-size: 15px;
          font-weight: 600;
          color: #1E293B;
          margin: 0 0 4px 0;
        }
        .couts-benefit-text p {
          font-size: 13px;
          color: #64748B;
          margin: 0;
          line-height: 1.4;
        }
        
        /* RIGHT COLUMN - DASHBOARD */
        .couts-right {
          position: relative;
        }
        .couts-dashboard {
          background: #FFFFFF;
          border-radius: 20px;
          padding: 24px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.08);
        }
        
        /* DASHBOARD HEADER */
        .couts-dash-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #F1F5F9;
        }
        .couts-dash-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 18px;
          font-weight: 600;
          color: #1E293B;
        }
        .couts-dash-title svg {
          color: #64748B;
        }
        .couts-dash-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .couts-dash-date {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #64748B;
          padding: 8px 12px;
          background: #F8FAFC;
          border-radius: 8px;
        }
        .couts-dash-export {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #64748B;
          padding: 8px 12px;
          background: #F8FAFC;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .couts-dash-export:hover {
          background: #E2E8F0;
        }
        
        /* MAIN LAYOUT */
        .couts-dash-main {
          display: flex;
          gap: 20px;
        }
        
        /* SIDEBAR NAV */
        .couts-dash-nav {
          width: 140px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .couts-nav-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 13px;
          color: #64748B;
          cursor: pointer;
          transition: all 0.2s;
        }
        .couts-nav-item:hover {
          background: #F8FAFC;
        }
        .couts-nav-item.active {
          background: #1E3A5F;
          color: #FFFFFF;
        }
        .couts-nav-item svg {
          width: 16px;
          height: 16px;
        }
        
        /* CONTENT AREA */
        .couts-dash-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        /* PROJECT CARD */
        .couts-project {
          display: flex;
          gap: 16px;
          padding: 16px;
          background: #F8FAFC;
          border-radius: 12px;
        }
        .couts-project-img {
          width: 80px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
        }
        .couts-project-info {
          flex: 1;
        }
        .couts-project-name {
          font-size: 16px;
          font-weight: 600;
          color: #1E293B;
          margin: 0 0 4px 0;
        }
        .couts-project-address {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #64748B;
          margin-bottom: 8px;
        }
        .couts-project-address svg {
          width: 12px;
          height: 12px;
        }
        .couts-project-meta {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .couts-project-status {
          background: #ECFDF5;
          color: #22C55E;
          font-size: 11px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 999px;
        }
        .couts-project-date {
          font-size: 11px;
          color: #94A3B8;
        }
        
        /* KPI CARDS */
        .couts-kpis {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }
        .couts-kpi {
          background: #F8FAFC;
          border-radius: 12px;
          padding: 16px;
        }
        .couts-kpi-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }
        .couts-kpi-label {
          font-size: 12px;
          color: #64748B;
          font-weight: 500;
        }
        .couts-kpi-icon {
          width: 28px;
          height: 28px;
          background: #FFFFFF;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .couts-kpi-icon svg {
          width: 14px;
          height: 14px;
          color: #94A3B8;
        }
        .couts-kpi-value {
          font-size: 24px;
          font-weight: 700;
          color: #1E293B;
          margin-bottom: 4px;
        }
        .couts-kpi-value.green { color: #22C55E; }
        .couts-kpi-sub {
          font-size: 11px;
          color: #22C55E;
          font-weight: 500;
        }
        .couts-kpi-sub.muted {
          color: #94A3B8;
        }
        
        /* GRID SECTIONS */
        .couts-grid {
          display: grid;
          grid-template-columns: 1fr 1.2fr 1fr;
          gap: 16px;
        }
        
        /* TEAM BOX */
        .couts-box {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 16px;
        }
        .couts-box-title {
          font-size: 13px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 12px;
        }
        .couts-team-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .couts-team-member {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .couts-team-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          object-fit: cover;
        }
        .couts-team-info {
          flex: 1;
        }
        .couts-team-name {
          font-size: 13px;
          font-weight: 500;
          color: #1E293B;
        }
        .couts-team-role {
          font-size: 11px;
          color: #94A3B8;
        }
        .couts-team-hours {
          font-size: 13px;
          font-weight: 600;
          color: #1E293B;
          margin-right: 8px;
        }
        .couts-team-status {
          background: #ECFDF5;
          color: #22C55E;
          font-size: 10px;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 999px;
        }
        .couts-team-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 10px;
          border-top: 1px solid #F1F5F9;
          margin-top: 10px;
        }
        .couts-team-total-label {
          font-size: 12px;
          color: #64748B;
        }
        .couts-team-total-value {
          font-size: 16px;
          font-weight: 700;
          color: #1E293B;
        }
        
        /* MAP BOX */
        .couts-map {
          background: linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 50%, #F8FAFC 100%);
          border-radius: 10px;
          height: 120px;
          position: relative;
          overflow: hidden;
        }
        .couts-map-roads {
          position: absolute;
          inset: 0;
          opacity: 0.3;
        }
        .couts-map-roads::before {
          content: '';
          position: absolute;
          left: 20%;
          top: 0;
          bottom: 0;
          width: 3px;
          background: #CBD5E1;
        }
        .couts-map-roads::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          top: 40%;
          height: 3px;
          background: #CBD5E1;
        }
        .couts-map-zone {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 80px;
          height: 60px;
          border: 2px dashed #F97316;
          border-radius: 8px;
          background: rgba(249, 115, 22, 0.08);
        }
        .couts-map-pins {
          position: absolute;
          inset: 0;
        }
        .couts-map-pin {
          position: absolute;
          width: 24px;
          height: 24px;
          background: #22C55E;
          border-radius: 50%;
          border: 2px solid #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        .couts-map-pin svg {
          width: 12px;
          height: 12px;
          color: #FFFFFF;
        }
        .couts-map-pin:nth-child(1) { left: 45%; top: 35%; }
        .couts-map-pin:nth-child(2) { left: 55%; top: 50%; }
        .couts-map-pin:nth-child(3) { left: 48%; top: 62%; }
        .couts-map-pin:nth-child(4) { left: 60%; top: 40%; }
        .couts-map-label {
          position: absolute;
          bottom: 8px;
          left: 8px;
          background: #FFFFFF;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 11px;
          color: #64748B;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .couts-map-label strong {
          color: #1E293B;
        }
        
        /* COST BOX */
        .couts-cost-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .couts-cost-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
        }
        .couts-cost-row span:first-child {
          color: #64748B;
        }
        .couts-cost-row span:last-child {
          color: #1E293B;
          font-weight: 500;
        }
        .couts-cost-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 10px;
          border-top: 1px solid #F1F5F9;
          margin-top: 8px;
        }
        .couts-cost-total span:first-child {
          font-size: 12px;
          color: #64748B;
          font-weight: 500;
        }
        .couts-cost-total span:last-child {
          font-size: 16px;
          font-weight: 700;
          color: #1E3A5F;
        }
        
        /* CHARTS ROW */
        .couts-charts {
          display: grid;
          grid-template-columns: 1.2fr 1fr 1fr;
          gap: 16px;
        }
        
        /* LINE CHART */
        .couts-chart-line {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 16px;
        }
        .couts-chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .couts-chart-title {
          font-size: 13px;
          font-weight: 600;
          color: #1E293B;
        }
        .couts-chart-legend {
          display: flex;
          gap: 16px;
        }
        .couts-legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: #64748B;
        }
        .couts-legend-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
        .couts-legend-dot.red { background: #F97316; }
        .couts-legend-dot.blue { background: #3B82F6; }
        .couts-chart-visual {
          height: 120px;
          position: relative;
          display: flex;
          align-items: flex-end;
        }
        .couts-chart-y {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 20px;
          width: 50px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          font-size: 10px;
          color: #94A3B8;
        }
        .couts-chart-area {
          position: absolute;
          left: 55px;
          right: 0;
          top: 0;
          bottom: 20px;
        }
        .couts-chart-grid {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .couts-chart-gridline {
          height: 1px;
          background: #F1F5F9;
        }
        .couts-chart-lines {
          position: absolute;
          inset: 0;
        }
        .couts-chart-lines svg {
          width: 100%;
          height: 100%;
        }
        .couts-chart-x {
          position: absolute;
          bottom: 0;
          left: 55px;
          right: 0;
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: #94A3B8;
        }
        
        /* DONUT CHART */
        .couts-chart-donut {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 16px;
        }
        .couts-donut-wrap {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .couts-donut-visual {
          width: 100px;
          height: 100px;
          position: relative;
          flex-shrink: 0;
        }
        .couts-donut-visual svg {
          width: 100%;
          height: 100%;
          transform: rotate(-90deg);
        }
        .couts-donut-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
        }
        .couts-donut-percent {
          font-size: 20px;
          font-weight: 700;
          color: #22C55E;
        }
        .couts-donut-label {
          font-size: 10px;
          color: #64748B;
        }
        .couts-donut-legend {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .couts-donut-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .couts-donut-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }
        .couts-donut-dot.green { background: #22C55E; }
        .couts-donut-dot.red { background: #EF4444; }
        .couts-donut-dot.blue { background: #3B82F6; }
        .couts-donut-text {
          flex: 1;
        }
        .couts-donut-text span:first-child {
          font-size: 11px;
          color: #64748B;
          display: block;
        }
        .couts-donut-text span:last-child {
          font-size: 12px;
          color: #1E293B;
          font-weight: 600;
        }
        
        /* ALERTS BOX */
        .couts-alerts {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 16px;
        }
        .couts-alert-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .couts-alert {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px;
          background: #FFFBEB;
          border-left: 3px solid #F97316;
          border-radius: 6px;
        }
        .couts-alert-icon {
          width: 28px;
          height: 28px;
          background: #FFF4ED;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .couts-alert-icon svg {
          width: 14px;
          height: 14px;
          color: #F97316;
        }
        .couts-alert-content {
          flex: 1;
        }
        .couts-alert-title {
          font-size: 12px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 2px;
        }
        .couts-alert-desc {
          font-size: 11px;
          color: #64748B;
        }
        .couts-alert-link {
          font-size: 11px;
          color: #F97316;
          font-weight: 500;
          cursor: pointer;
          margin-top: 4px;
          display: inline-block;
        }
        
        /* BOTTOM FEATURES */
        .couts-features {
          display: flex;
          gap: 20px;
          background: #F8FAFC;
          border-radius: 16px;
          padding: 20px;
          margin-top: 20px;
        }
        .couts-feature {
          flex: 1;
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .couts-feature-icon {
          width: 44px;
          height: 44px;
          background: #FFFFFF;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .couts-feature-icon.orange { color: #F97316; }
        .couts-feature-icon.blue { color: #3B82F6; }
        .couts-feature-icon.green { color: #22C55E; }
        .couts-feature-icon.teal { color: #14B8A6; }
        .couts-feature-text h5 {
          font-size: 14px;
          font-weight: 600;
          color: #1E293B;
          margin: 0 0 4px 0;
        }
        .couts-feature-text p {
          font-size: 12px;
          color: #64748B;
          margin: 0;
          line-height: 1.4;
        }
        
        /* RESPONSIVE */
        @media (max-width: 1200px) {
          .couts-container {
            grid-template-columns: 1fr;
            gap: 48px;
          }
          .couts-right {
            order: -1;
          }
        }
        @media (max-width: 900px) {
          .couts-section {
            padding: 80px 24px;
          }
          .couts-title {
            font-size: 32px;
          }
          .couts-dash-main {
            flex-direction: column;
          }
          .couts-dash-nav {
            flex-direction: row;
            width: 100%;
            overflow-x: auto;
          }
          .couts-kpis {
            grid-template-columns: repeat(2, 1fr);
          }
          .couts-grid {
            grid-template-columns: 1fr;
          }
          .couts-charts {
            grid-template-columns: 1fr;
          }
          .couts-features {
            flex-direction: column;
            gap: 16px;
          }
        }
        @media (max-width: 600px) {
          .couts-kpis {
            grid-template-columns: 1fr;
          }
          .couts-donut-wrap {
            flex-direction: column;
          }
        }
      `}</style>
      
      <div className="couts-container">
        {/* LEFT COLUMN */}
        <div className="couts-left">
          <div className="couts-badge">
            <PiggyBank size={16} />
            COÛTS & MARGES
          </div>
          
          <h2 className="couts-title">
            Tu sais vraiment combien te rapporte un chantier.<br />
            <span>Pas une estimation. La réalité.</span>
          </h2>
          
          <p className="couts-subtitle">
            Grâce à la géolocalisation et au suivi du temps réel, tu connais précisément les heures passées sur chaque chantier.
          </p>
          
          <p className="couts-paragraph">
            Chaque membre de ton équipe est pointé automatiquement à l'arrivée et au départ du chantier. Tu connais le coût réel de la main d'œuvre. Tu sais enfin si tu gagnes… ou si tu perds.
          </p>
          
          <div className="couts-benefits">
            <div className="couts-benefit">
              <div className="couts-benefit-icon orange">
                <MapPin size={18} />
              </div>
              <div className="couts-benefit-text">
                <h4>Pointage automatique par géolocalisation</h4>
                <p>Arrivée et départ détectés sur le chantier.</p>
              </div>
            </div>
            
            <div className="couts-benefit">
              <div className="couts-benefit-icon blue">
                <Clock size={18} />
              </div>
              <div className="couts-benefit-text">
                <h4>Temps réel par chantier et par employé</h4>
                <p>Heures travaillées fiables et actualisées.</p>
              </div>
            </div>
            
            <div className="couts-benefit">
              <div className="couts-benefit-icon green">
                <Euro size={18} />
              </div>
              <div className="couts-benefit-text">
                <h4>Calcul automatique des coûts horaires</h4>
                <p>Main d'œuvre, matériel, sous-traitance.</p>
              </div>
            </div>
            
            <div className="couts-benefit">
              <div className="couts-benefit-icon teal">
                <BarChart3 size={18} />
              </div>
              <div className="couts-benefit-text">
                <h4>Rentabilité réelle chantier par chantier</h4>
                <p>Marge nette calculée automatiquement.</p>
              </div>
            </div>
            
            <div className="couts-benefit">
              <div className="couts-benefit-icon red">
                <AlertTriangle size={18} />
              </div>
              <div className="couts-benefit-text">
                <h4>Alertes en cas de dérive</h4>
                <p>Dépassements de temps ou de budget.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* RIGHT COLUMN - DASHBOARD */}
        <div className="couts-right">
          <div className="couts-dashboard">
            {/* Header */}
            <div className="couts-dash-header">
              <div className="couts-dash-title">
                <Building size={20} />
                Rentabilité chantier
              </div>
              <div className="couts-dash-actions">
                <div className="couts-dash-date">
                  <Calendar size={14} />
                  01 - 30 mai 2024
                </div>
                <div className="couts-dash-export">
                  <Download size={14} />
                  Exporter
                  <ChevronDown size={14} />
                </div>
              </div>
            </div>
            
            {/* Main Layout */}
            <div className="couts-dash-main">
              {/* Sidebar Nav */}
              <div className="couts-dash-nav">
                <div className="couts-nav-item active">
                  <BarChart3 size={16} />
                  Vue d'ensemble
                </div>
                <div className="couts-nav-item">
                  <HardHat size={16} />
                  Chantiers
                </div>
                <div className="couts-nav-item">
                  <Users size={16} />
                  Équipes
                </div>
                <div className="couts-nav-item">
                  <Clock size={16} />
                  Pointages
                </div>
                <div className="couts-nav-item">
                  <Euro size={16} />
                  Coûts
                </div>
                <div className="couts-nav-item">
                  <TrendingUp size={16} />
                  Marges
                </div>
                <div className="couts-nav-item">
                  <Bell size={16} />
                  Alertes
                </div>
                <div className="couts-nav-item">
                  <FileText size={16} />
                  Rapports
                </div>
                <div className="couts-nav-item">
                  <Settings size={16} />
                  Paramètres
                </div>
              </div>
              
              {/* Content */}
              <div className="couts-dash-content">
                {/* Project Card */}
                <div className="couts-project">
                  <img 
                    src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=200&h=150&fit=crop" 
                    className="couts-project-img" 
                    alt="" 
                  />
                  <div className="couts-project-info">
                    <h3 className="couts-project-name">Villa Contemporaine</h3>
                    <div className="couts-project-address">
                      <MapPin size={12} />
                      12 Allée des Pins, 83700 Saint-Raphaël
                    </div>
                    <div className="couts-project-meta">
                      <span className="couts-project-status">En cours</span>
                      <span className="couts-project-date">Démarré le 12 mars 2024</span>
                    </div>
                  </div>
                </div>
                
                {/* KPI Cards */}
                <div className="couts-kpis">
                  <div className="couts-kpi">
                    <div className="couts-kpi-header">
                      <span className="couts-kpi-label">Marge nette</span>
                      <div className="couts-kpi-icon">
                        <TrendingUp size={14} />
                      </div>
                    </div>
                    <div className="couts-kpi-value">26,6%</div>
                    <div className="couts-kpi-sub">+2 250 €</div>
                  </div>
                  
                  <div className="couts-kpi">
                    <div className="couts-kpi-header">
                      <span className="couts-kpi-label">Devis total</span>
                      <div className="couts-kpi-icon">
                        <FileText size={14} />
                      </div>
                    </div>
                    <div className="couts-kpi-value">8 450 € <span style={{fontSize: '12px', fontWeight: 400, color: '#94A3B8'}}>HT</span></div>
                    <div className="couts-kpi-sub muted">100%</div>
                  </div>
                  
                  <div className="couts-kpi">
                    <div className="couts-kpi-header">
                      <span className="couts-kpi-label">Coût total réel</span>
                      <div className="couts-kpi-icon">
                        <Euro size={14} />
                      </div>
                    </div>
                    <div className="couts-kpi-value">6 200 € <span style={{fontSize: '12px', fontWeight: 400, color: '#94A3B8'}}>HT</span></div>
                    <div className="couts-kpi-sub muted">73,4%</div>
                  </div>
                  
                  <div className="couts-kpi">
                    <div className="couts-kpi-header">
                      <span className="couts-kpi-label">Résultat net</span>
                      <div className="couts-kpi-icon">
                        <Target size={14} />
                      </div>
                    </div>
                    <div className="couts-kpi-value green">+2 250 €</div>
                    <div className="couts-kpi-sub">Bénéfice</div>
                  </div>
                </div>
                
                {/* Grid: Team / Map / Costs */}
                <div className="couts-grid">
                  {/* Team */}
                  <div className="couts-box">
                    <div className="couts-box-title">Équipe sur le chantier</div>
                    <div className="couts-team-list">
                      <div className="couts-team-member">
                        <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face" className="couts-team-avatar" alt="" />
                        <div className="couts-team-info">
                          <div className="couts-team-name">Thomas L.</div>
                          <div className="couts-team-role">Chef d'équipe</div>
                        </div>
                        <span className="couts-team-hours">7h30</span>
                        <span className="couts-team-status">Présent</span>
                      </div>
                      <div className="couts-team-member">
                        <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&h=60&fit=crop&crop=face" className="couts-team-avatar" alt="" />
                        <div className="couts-team-info">
                          <div className="couts-team-name">Lucas D.</div>
                          <div className="couts-team-role">Maçon</div>
                        </div>
                        <span className="couts-team-hours">8h00</span>
                        <span className="couts-team-status">Présent</span>
                      </div>
                      <div className="couts-team-member">
                        <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face" className="couts-team-avatar" alt="" />
                        <div className="couts-team-info">
                          <div className="couts-team-name">Julien M.</div>
                          <div className="couts-team-role">Charpentier</div>
                        </div>
                        <span className="couts-team-hours">6h45</span>
                        <span className="couts-team-status">Présent</span>
                      </div>
                      <div className="couts-team-member">
                        <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face" className="couts-team-avatar" alt="" />
                        <div className="couts-team-info">
                          <div className="couts-team-name">Sofiane B.</div>
                          <div className="couts-team-role">Plaquiste</div>
                        </div>
                        <span className="couts-team-hours">6h00</span>
                        <span className="couts-team-status">Présent</span>
                      </div>
                    </div>
                    <div className="couts-team-total">
                      <span className="couts-team-total-label">Total heures</span>
                      <span className="couts-team-total-value">22h15</span>
                    </div>
                  </div>
                  
                  {/* Map */}
                  <div className="couts-box">
                    <div className="couts-box-title">Géolocalisation en temps réel</div>
                    <div className="couts-map">
                      <div className="couts-map-roads"></div>
                      <div className="couts-map-zone"></div>
                      <div className="couts-map-pins">
                        <div className="couts-map-pin"><User size={12} /></div>
                        <div className="couts-map-pin"><User size={12} /></div>
                        <div className="couts-map-pin"><User size={12} /></div>
                        <div className="couts-map-pin"><User size={12} /></div>
                      </div>
                      <div className="couts-map-label">
                        <strong>Zone chantier</strong> — Villa Contemporaine
                      </div>
                    </div>
                  </div>
                  
                  {/* Costs */}
                  <div className="couts-box">
                    <div className="couts-box-title">Coût main d'œuvre</div>
                    <div className="couts-cost-list">
                      <div className="couts-cost-row">
                        <span>Taux horaire moyen</span>
                        <span>45,00 € /h</span>
                      </div>
                      <div className="couts-cost-row">
                        <span>Total heures</span>
                        <span>22h15</span>
                      </div>
                      <div className="couts-cost-row">
                        <span>Coût total main d'œuvre</span>
                        <span>1 001,25 €</span>
                      </div>
                      <div className="couts-cost-row">
                        <span>Autres coûts (matériaux, etc.)</span>
                        <span>5 198,75 €</span>
                      </div>
                    </div>
                    <div className="couts-cost-total">
                      <span>Coût total réel</span>
                      <span>6 200,00 €</span>
                    </div>
                  </div>
                </div>
                
                {/* Charts Row */}
                <div className="couts-charts">
                  {/* Line Chart */}
                  <div className="couts-chart-line">
                    <div className="couts-chart-header">
                      <span className="couts-chart-title">Évolution des coûts vs devis</span>
                      <div className="couts-chart-legend">
                        <div className="couts-legend-item">
                          <div className="couts-legend-dot red"></div>
                          Coût réel
                        </div>
                        <div className="couts-legend-item">
                          <div className="couts-legend-dot blue"></div>
                          Coût prévu
                        </div>
                      </div>
                    </div>
                    <div className="couts-chart-visual">
                      <div className="couts-chart-y">
                        <span>8 000 €</span>
                        <span>6 000 €</span>
                        <span>4 000 €</span>
                        <span>2 000 €</span>
                        <span>0 €</span>
                      </div>
                      <div className="couts-chart-area">
                        <div className="couts-chart-grid">
                          <div className="couts-chart-gridline"></div>
                          <div className="couts-chart-gridline"></div>
                          <div className="couts-chart-gridline"></div>
                          <div className="couts-chart-gridline"></div>
                          <div className="couts-chart-gridline"></div>
                        </div>
                        <div className="couts-chart-lines">
                          <svg viewBox="0 0 200 80" preserveAspectRatio="none">
                            <path d="M0 70 Q50 65 80 50 T140 35 T200 20" fill="none" stroke="#3B82F6" strokeWidth="2" strokeDasharray="4 2" />
                            <path d="M0 72 Q50 68 80 55 T140 40 T200 25" fill="none" stroke="#F97316" strokeWidth="2" />
                          </svg>
                        </div>
                      </div>
                      <div className="couts-chart-x">
                        <span>1 mai</span>
                        <span>8 mai</span>
                        <span>15 mai</span>
                        <span>22 mai</span>
                        <span>30 mai</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Donut Chart */}
                  <div className="couts-chart-donut">
                    <div className="couts-chart-title" style={{marginBottom: '12px'}}>Rentabilité par rapport au devis</div>
                    <div className="couts-donut-wrap">
                      <div className="couts-donut-visual">
                        <svg viewBox="0 0 36 36">
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#22C55E" strokeWidth="3" strokeDasharray="26.6 73.4" />
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#3B82F6" strokeWidth="3" strokeDasharray="73.4 26.6" strokeDashoffset="-26.6" />
                        </svg>
                        <div className="couts-donut-center">
                          <div className="couts-donut-percent">26,6%</div>
                          <div className="couts-donut-label">Marge nette</div>
                        </div>
                      </div>
                      <div className="couts-donut-legend">
                        <div className="couts-donut-item">
                          <div className="couts-donut-dot green"></div>
                          <div className="couts-donut-text">
                            <span>Marge nette</span>
                            <span>2 250,00 €</span>
                          </div>
                        </div>
                        <div className="couts-donut-item">
                          <div className="couts-donut-dot red"></div>
                          <div className="couts-donut-text">
                            <span>Coût total</span>
                            <span>6 200,00 €</span>
                          </div>
                        </div>
                        <div className="couts-donut-item">
                          <div className="couts-donut-dot blue"></div>
                          <div className="couts-donut-text">
                            <span>Devis total</span>
                            <span>8 450,00 €</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Alerts */}
                  <div className="couts-alerts">
                    <div className="couts-chart-title" style={{marginBottom: '12px'}}>Alertes</div>
                    <div className="couts-alert-list">
                      <div className="couts-alert">
                        <div className="couts-alert-icon">
                          <AlertTriangle size={14} />
                        </div>
                        <div className="couts-alert-content">
                          <div className="couts-alert-title">Dépassement temps main d'œuvre</div>
                          <div className="couts-alert-desc">+2h15 sur ce chantier</div>
                          <span className="couts-alert-link">Voir détails</span>
                        </div>
                      </div>
                      <div className="couts-alert">
                        <div className="couts-alert-icon">
                          <AlertTriangle size={14} />
                        </div>
                        <div className="couts-alert-content">
                          <div className="couts-alert-title">Coût matériaux en hausse</div>
                          <div className="couts-alert-desc">+12% vs estimation</div>
                          <span className="couts-alert-link">Voir détails</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Bottom Features */}
                <div className="couts-features">
                  <div className="couts-feature">
                    <div className="couts-feature-icon orange">
                      <Euro size={22} />
                    </div>
                    <div className="couts-feature-text">
                      <h5>Zéro estimation</h5>
                      <p>Des données réelles, pas des approximations.</p>
                    </div>
                  </div>
                  <div className="couts-feature">
                    <div className="couts-feature-icon blue">
                      <Clock size={22} />
                    </div>
                    <div className="couts-feature-text">
                      <h5>Maîtrise totale</h5>
                      <p>Tu sais où va ton argent, à chaque instant.</p>
                    </div>
                  </div>
                  <div className="couts-feature">
                    <div className="couts-feature-icon green">
                      <TrendingUp size={22} />
                    </div>
                    <div className="couts-feature-text">
                      <h5>Plus de marge</h5>
                      <p>Tu corriges les dérives avant qu'il ne soit trop tard.</p>
                    </div>
                  </div>
                  <div className="couts-feature">
                    <div className="couts-feature-icon teal">
                      <Shield size={22} />
                    </div>
                    <div className="couts-feature-text">
                      <h5>Décisions justes</h5>
                      <p>Des chiffres fiables pour des décisions rentables.</p>
                    </div>
                  </div>
                  <div className="couts-feature">
                    <div className="couts-feature-icon orange">
                      <Target size={22} />
                    </div>
                    <div className="couts-feature-text">
                      <h5>Rentabilité maximale</h5>
                      <p>Chaque chantier devient plus performant.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION AUTOMATISATION TOTALE
// ═══════════════════════════════════════════════════════════════════════════════
const AutomationSection = () => {
  return (
    <section className="automation-section">
      <style>{`
        .automation-section {
          width: 100%;
          background: #FFFFFF;
          padding: 120px 40px;
          font-family: 'Inter', 'DM Sans', sans-serif;
        }
        .automation-container {
          max-width: 1300px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1.5fr;
          gap: 80px;
          align-items: flex-start;
        }
        
        /* LEFT COLUMN */
        .automation-left {
          display: flex;
          flex-direction: column;
          gap: 28px;
        }
        .automation-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #FFF4ED;
          color: #F97316;
          border-radius: 999px;
          padding: 6px 14px;
          font-size: 13px;
          font-weight: 600;
          width: fit-content;
        }
        .automation-badge svg {
          width: 16px;
          height: 16px;
        }
        .automation-title {
          font-size: 42px;
          font-weight: 700;
          line-height: 1.2;
          color: #1E293B;
          margin: 0;
        }
        .automation-title span {
          color: #F97316;
          display: block;
        }
        .automation-subtitle {
          font-size: 18px;
          color: #64748B;
          line-height: 1.6;
          margin: 0;
        }
        
        /* BENEFITS STACK */
        .automation-benefits {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 8px;
        }
        .automation-benefit {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 16px;
          display: flex;
          gap: 12px;
          align-items: flex-start;
          transition: box-shadow 0.2s ease;
        }
        .automation-benefit:hover {
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        }
        .automation-benefit-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .automation-benefit-icon.blue { background: #EEF2FF; color: #3B82F6; }
        .automation-benefit-icon.green { background: #ECFDF5; color: #22C55E; }
        .automation-benefit-icon.purple { background: #F3E8FF; color: #A855F7; }
        .automation-benefit-icon.orange { background: #FFF4ED; color: #F97316; }
        .automation-benefit-icon.pink { background: #FDF2F8; color: #EC4899; }
        .automation-benefit-text h4 {
          font-size: 15px;
          font-weight: 600;
          color: #1E293B;
          margin: 0 0 4px 0;
        }
        .automation-benefit-text p {
          font-size: 13px;
          color: #64748B;
          margin: 0;
          line-height: 1.4;
        }
        
        /* RIGHT COLUMN - DASHBOARD */
        .automation-right {
          position: relative;
        }
        .automation-dashboard {
          background: #FFFFFF;
          border-radius: 20px;
          padding: 24px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.08);
        }
        
        /* DASHBOARD HEADER */
        .automation-dash-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #F1F5F9;
        }
        .automation-dash-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 18px;
          font-weight: 600;
          color: #1E293B;
        }
        .automation-dash-title svg {
          color: #64748B;
        }
        .automation-dash-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .automation-dash-status {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #22C55E;
          font-weight: 500;
        }
        .automation-dash-status::before {
          content: '';
          width: 8px;
          height: 8px;
          background: #22C55E;
          border-radius: 50%;
        }
        .automation-dash-settings {
          width: 32px;
          height: 32px;
          background: #F8FAFC;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #64748B;
        }
        
        /* KPI SECTION */
        .automation-kpi-title {
          font-size: 13px;
          font-weight: 600;
          color: #64748B;
          margin-bottom: 12px;
        }
        .automation-kpis {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }
        .automation-kpi {
          background: #F8FAFC;
          border-radius: 12px;
          padding: 16px;
          text-align: center;
        }
        .automation-kpi-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 10px;
        }
        .automation-kpi-icon.blue { background: #EEF2FF; color: #3B82F6; }
        .automation-kpi-icon.green { background: #ECFDF5; color: #22C55E; }
        .automation-kpi-icon.purple { background: #F3E8FF; color: #A855F7; }
        .automation-kpi-icon.orange { background: #FFF4ED; color: #F97316; }
        .automation-kpi-value {
          font-size: 28px;
          font-weight: 700;
          color: #1E293B;
          margin-bottom: 4px;
        }
        .automation-kpi-label {
          font-size: 12px;
          color: #64748B;
          margin-bottom: 4px;
        }
        .automation-kpi-sub {
          font-size: 11px;
          color: #22C55E;
          font-weight: 500;
        }
        .automation-kpi-sub.muted {
          color: #94A3B8;
        }
        
        /* MAIN GRID */
        .automation-main-grid {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 20px;
        }
        
        /* AUTOMATIONS LIST */
        .automation-list-box {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 16px;
        }
        .automation-list-title {
          font-size: 14px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 16px;
        }
        .automation-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .automation-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #FAFAFA;
          border-radius: 10px;
          transition: background 0.2s;
        }
        .automation-item:hover {
          background: #F1F5F9;
        }
        .automation-item-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .automation-item-icon.blue { background: #EEF2FF; color: #3B82F6; }
        .automation-item-icon.green { background: #ECFDF5; color: #22C55E; }
        .automation-item-icon.purple { background: #F3E8FF; color: #A855F7; }
        .automation-item-icon.orange { background: #FFF4ED; color: #F97316; }
        .automation-item-icon.pink { background: #FDF2F8; color: #EC4899; }
        .automation-item-content {
          flex: 1;
          min-width: 0;
        }
        .automation-item-title {
          font-size: 13px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 2px;
        }
        .automation-item-desc {
          font-size: 11px;
          color: #94A3B8;
        }
        .automation-item-status {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }
        .automation-item-badge {
          font-size: 11px;
          font-weight: 600;
          color: #22C55E;
        }
        .automation-item-freq {
          font-size: 10px;
          color: #94A3B8;
        }
        .automation-item-arrow {
          color: #CBD5E1;
        }
        .automation-list-link {
          display: block;
          text-align: center;
          font-size: 13px;
          color: #3B82F6;
          font-weight: 500;
          margin-top: 16px;
          cursor: pointer;
        }
        .automation-list-link:hover {
          text-decoration: underline;
        }
        
        /* ACTIONS TIMELINE */
        .automation-actions-box {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 16px;
        }
        .automation-actions-title {
          font-size: 14px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 16px;
        }
        .automation-timeline {
          display: flex;
          flex-direction: column;
          gap: 0;
          position: relative;
        }
        .automation-timeline::before {
          content: '';
          position: absolute;
          left: 17px;
          top: 24px;
          bottom: 24px;
          width: 2px;
          background: #E5E7EB;
        }
        .automation-timeline-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 0;
          position: relative;
        }
        .automation-timeline-check {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #22C55E;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          z-index: 1;
        }
        .automation-timeline-check svg {
          width: 14px;
          height: 14px;
          color: #FFFFFF;
        }
        .automation-timeline-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .automation-timeline-icon.blue { background: #EEF2FF; color: #3B82F6; }
        .automation-timeline-icon.green { background: #ECFDF5; color: #22C55E; }
        .automation-timeline-icon.purple { background: #F3E8FF; color: #A855F7; }
        .automation-timeline-icon.pink { background: #FDF2F8; color: #EC4899; }
        .automation-timeline-content {
          flex: 1;
          min-width: 0;
        }
        .automation-timeline-title {
          font-size: 13px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 2px;
        }
        .automation-timeline-desc {
          font-size: 11px;
          color: #94A3B8;
          line-height: 1.4;
        }
        .automation-timeline-time {
          font-size: 12px;
          font-weight: 500;
          color: #64748B;
          flex-shrink: 0;
        }
        .automation-actions-link {
          display: block;
          text-align: center;
          font-size: 13px;
          color: #F97316;
          font-weight: 500;
          margin-top: 16px;
          cursor: pointer;
        }
        .automation-actions-link:hover {
          text-decoration: underline;
        }
        
        /* BOTTOM FEATURES */
        .automation-features {
          display: flex;
          gap: 20px;
          background: #F8FAFC;
          border-radius: 16px;
          padding: 20px;
          margin-top: 20px;
        }
        .automation-feature {
          flex: 1;
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .automation-feature-icon {
          width: 44px;
          height: 44px;
          background: #FFFFFF;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .automation-feature-icon.blue { color: #3B82F6; }
        .automation-feature-icon.green { color: #22C55E; }
        .automation-feature-icon.orange { color: #F97316; }
        .automation-feature-icon.purple { color: #A855F7; }
        .automation-feature-icon.pink { color: #EC4899; }
        .automation-feature-text h5 {
          font-size: 14px;
          font-weight: 600;
          color: #1E293B;
          margin: 0 0 4px 0;
        }
        .automation-feature-text p {
          font-size: 12px;
          color: #64748B;
          margin: 0;
          line-height: 1.4;
        }
        
        /* RESPONSIVE */
        @media (max-width: 1200px) {
          .automation-container {
            grid-template-columns: 1fr;
            gap: 48px;
          }
          .automation-right {
            order: -1;
          }
        }
        @media (max-width: 900px) {
          .automation-section {
            padding: 80px 24px;
          }
          .automation-title {
            font-size: 32px;
          }
          .automation-kpis {
            grid-template-columns: repeat(2, 1fr);
          }
          .automation-main-grid {
            grid-template-columns: 1fr;
          }
          .automation-features {
            flex-direction: column;
            gap: 16px;
          }
        }
        @media (max-width: 600px) {
          .automation-kpis {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      
      <div className="automation-container">
        {/* LEFT COLUMN */}
        <div className="automation-left">
          <div className="automation-badge">
            <Zap size={16} />
            AUTOMATISATION TOTALE
          </div>
          
          <h2 className="automation-title">
            Tu ne fais plus.<br />
            <span>Le cockpit le fait pour toi.</span>
          </h2>
          
          <p className="automation-subtitle">
            Tâches, relances, comptes rendus, suivis, rapports, notifications : tout est automatisé. Tu te concentres sur l'essentiel.
          </p>
          
          <div className="automation-benefits">
            <div className="automation-benefit">
              <div className="automation-benefit-icon blue">
                <RefreshCw size={18} />
              </div>
              <div className="automation-benefit-text">
                <h4>Relances clients automatiques</h4>
                <p>Factures, devis, validations : plus rien n'est oublié.</p>
              </div>
            </div>
            
            <div className="automation-benefit">
              <div className="automation-benefit-icon green">
                <FileText size={18} />
              </div>
              <div className="automation-benefit-text">
                <h4>Comptes rendus générés automatiquement</h4>
                <p>Réunions, visites, avancements : tout est rédigé.</p>
              </div>
            </div>
            
            <div className="automation-benefit">
              <div className="automation-benefit-icon purple">
                <Bell size={18} />
              </div>
              <div className="automation-benefit-text">
                <h4>Suivi chantier en arrière-plan</h4>
                <p>Le cockpit suit, contrôle et t'alerte si besoin.</p>
              </div>
            </div>
            
            <div className="automation-benefit">
              <div className="automation-benefit-icon orange">
                <Zap size={18} />
              </div>
              <div className="automation-benefit-text">
                <h4>Notifications intelligentes</h4>
                <p>Toi et ton équipe recevez l'essentiel, au bon moment.</p>
              </div>
            </div>
            
            <div className="automation-benefit">
              <div className="automation-benefit-icon pink">
                <Bot size={18} />
              </div>
              <div className="automation-benefit-text">
                <h4>IA qui travaille pendant que tu avances</h4>
                <p>Analyse, tri, création, résumés : ton assistant 24/7.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* RIGHT COLUMN - DASHBOARD */}
        <div className="automation-right">
          <div className="automation-dashboard">
            {/* Header */}
            <div className="automation-dash-header">
              <div className="automation-dash-title">
                <Settings size={20} />
                Automatisation active
              </div>
              <div className="automation-dash-actions">
                <div className="automation-dash-status">
                  Tout fonctionne
                </div>
                <div className="automation-dash-settings">
                  <Settings size={16} />
                </div>
              </div>
            </div>
            
            {/* KPIs */}
            <div className="automation-kpi-title">Automatisations en cours</div>
            <div className="automation-kpis">
              <div className="automation-kpi">
                <div className="automation-kpi-icon blue">
                  <Bot size={20} />
                </div>
                <div className="automation-kpi-value">24</div>
                <div className="automation-kpi-label">Automatisations actives</div>
                <div className="automation-kpi-sub">+3 aujourd'hui</div>
              </div>
              
              <div className="automation-kpi">
                <div className="automation-kpi-icon green">
                  <RefreshCw size={20} />
                </div>
                <div className="automation-kpi-value">156</div>
                <div className="automation-kpi-label">Tâches automatisées</div>
                <div className="automation-kpi-sub muted">Cette semaine</div>
              </div>
              
              <div className="automation-kpi">
                <div className="automation-kpi-icon purple">
                  <Send size={20} />
                </div>
                <div className="automation-kpi-value">98%</div>
                <div className="automation-kpi-label">Taux de réussite</div>
                <div className="automation-kpi-sub">Excellent</div>
              </div>
              
              <div className="automation-kpi">
                <div className="automation-kpi-icon orange">
                  <Clock size={20} />
                </div>
                <div className="automation-kpi-value">32h</div>
                <div className="automation-kpi-label">Temps gagné</div>
                <div className="automation-kpi-sub muted">Cette semaine</div>
              </div>
            </div>
            
            {/* Main Grid */}
            <div className="automation-main-grid">
              {/* Automations List */}
              <div className="automation-list-box">
                <div className="automation-list-title">Automatisations actives</div>
                <div className="automation-list">
                  <div className="automation-item">
                    <div className="automation-item-icon blue">
                      <Mail size={16} />
                    </div>
                    <div className="automation-item-content">
                      <div className="automation-item-title">Relance devis en attente</div>
                      <div className="automation-item-desc">Envoie automatique des relances aux clients</div>
                    </div>
                    <div className="automation-item-status">
                      <span className="automation-item-badge">Actif</span>
                      <span className="automation-item-freq">Toutes les 48h</span>
                    </div>
                    <ChevronRight size={16} className="automation-item-arrow" />
                  </div>
                  
                  <div className="automation-item">
                    <div className="automation-item-icon green">
                      <FileText size={16} />
                    </div>
                    <div className="automation-item-content">
                      <div className="automation-item-title">Compte rendu de chantier</div>
                      <div className="automation-item-desc">Génération automatique après chaque visite</div>
                    </div>
                    <div className="automation-item-status">
                      <span className="automation-item-badge">Actif</span>
                      <span className="automation-item-freq">À chaque visite</span>
                    </div>
                    <ChevronRight size={16} className="automation-item-arrow" />
                  </div>
                  
                  <div className="automation-item">
                    <div className="automation-item-icon purple">
                      <ListChecks size={16} />
                    </div>
                    <div className="automation-item-content">
                      <div className="automation-item-title">Suivi des tâches en retard</div>
                      <div className="automation-item-desc">Détection et notification automatique des retards</div>
                    </div>
                    <div className="automation-item-status">
                      <span className="automation-item-badge">Actif</span>
                      <span className="automation-item-freq">Tous les jours</span>
                    </div>
                    <ChevronRight size={16} className="automation-item-arrow" />
                  </div>
                  
                  <div className="automation-item">
                    <div className="automation-item-icon orange">
                      <Receipt size={16} />
                    </div>
                    <div className="automation-item-content">
                      <div className="automation-item-title">Relance factures impayées</div>
                      <div className="automation-item-desc">Envoi automatique des rappels de paiement</div>
                    </div>
                    <div className="automation-item-status">
                      <span className="automation-item-badge">Actif</span>
                      <span className="automation-item-freq">Toutes les 72h</span>
                    </div>
                    <ChevronRight size={16} className="automation-item-arrow" />
                  </div>
                  
                  <div className="automation-item">
                    <div className="automation-item-icon pink">
                      <Cloud size={16} />
                    </div>
                    <div className="automation-item-content">
                      <div className="automation-item-title">Sauvegarde & archivage</div>
                      <div className="automation-item-desc">Sauvegarde automatique de tous les documents</div>
                    </div>
                    <div className="automation-item-status">
                      <span className="automation-item-badge">Actif</span>
                      <span className="automation-item-freq">En continu</span>
                    </div>
                    <ChevronRight size={16} className="automation-item-arrow" />
                  </div>
                </div>
                <span className="automation-list-link">Voir toutes les automatisations</span>
              </div>
              
              {/* Actions Timeline */}
              <div className="automation-actions-box">
                <div className="automation-actions-title">Exemples d'actions automatisées</div>
                <div className="automation-timeline">
                  <div className="automation-timeline-item">
                    <div className="automation-timeline-check">
                      <Check size={14} />
                    </div>
                    <div className="automation-timeline-icon blue">
                      <Mail size={14} />
                    </div>
                    <div className="automation-timeline-content">
                      <div className="automation-timeline-title">Relance envoyée</div>
                      <div className="automation-timeline-desc">Relance automatique envoyée à M. Dubois pour le devis</div>
                    </div>
                    <div className="automation-timeline-time">09:15</div>
                  </div>
                  
                  <div className="automation-timeline-item">
                    <div className="automation-timeline-check">
                      <Check size={14} />
                    </div>
                    <div className="automation-timeline-icon green">
                      <FileText size={14} />
                    </div>
                    <div className="automation-timeline-content">
                      <div className="automation-timeline-title">Compte rendu généré</div>
                      <div className="automation-timeline-desc">CR de visite généré automatiquement pour Villa Contemporaine</div>
                    </div>
                    <div className="automation-timeline-time">11:30</div>
                  </div>
                  
                  <div className="automation-timeline-item">
                    <div className="automation-timeline-check">
                      <Check size={14} />
                    </div>
                    <div className="automation-timeline-icon purple">
                      <Bell size={14} />
                    </div>
                    <div className="automation-timeline-content">
                      <div className="automation-timeline-title">Équipe notifiée</div>
                      <div className="automation-timeline-desc">L'équipe a été notifiée des nouvelles tâches assignées</div>
                    </div>
                    <div className="automation-timeline-time">14:45</div>
                  </div>
                  
                  <div className="automation-timeline-item">
                    <div className="automation-timeline-check">
                      <Check size={14} />
                    </div>
                    <div className="automation-timeline-icon pink">
                      <Cloud size={14} />
                    </div>
                    <div className="automation-timeline-content">
                      <div className="automation-timeline-title">Sauvegarde effectuée</div>
                      <div className="automation-timeline-desc">Tous les documents du chantier ont été sauvegardés</div>
                    </div>
                    <div className="automation-timeline-time">16:20</div>
                  </div>
                </div>
                <span className="automation-actions-link">Voir l'historique complet</span>
              </div>
            </div>
            
            {/* Bottom Features */}
            <div className="automation-features">
              <div className="automation-feature">
                <div className="automation-feature-icon blue">
                  <Clock size={22} />
                </div>
                <div className="automation-feature-text">
                  <h5>Jusqu'à 32h gagnées / semaine</h5>
                  <p>Fini les tâches répétitives qui te prennent du temps.</p>
                </div>
              </div>
              <div className="automation-feature">
                <div className="automation-feature-icon green">
                  <RefreshCw size={22} />
                </div>
                <div className="automation-feature-text">
                  <h5>Zéro oubli</h5>
                  <p>Le cockpit suit tout à ta place.</p>
                </div>
              </div>
              <div className="automation-feature">
                <div className="automation-feature-icon orange">
                  <Rocket size={22} />
                </div>
                <div className="automation-feature-text">
                  <h5>Plus de réactivité</h5>
                  <p>Les actions sont faites immédiatement.</p>
                </div>
              </div>
              <div className="automation-feature">
                <div className="automation-feature-icon purple">
                  <Shield size={22} />
                </div>
                <div className="automation-feature-text">
                  <h5>Moins d'erreurs</h5>
                  <p>Automatisation + IA = fiabilité maximale.</p>
                </div>
              </div>
              <div className="automation-feature">
                <div className="automation-feature-icon pink">
                  <Smile size={22} />
                </div>
                <div className="automation-feature-text">
                  <h5>Moins de stress</h5>
                  <p>Tu te concentres sur ton métier, pas sur l'administratif.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION OFFRE & TARIFS COCKPIT (DUPLICATA 1:1 DE BOUTIQUE)
// ═══════════════════════════════════════════════════════════════════════════════
const CockpitPricingSection = () => {
  return (
    <section 
      id="tarifs-cockpit" 
      style={{
        background: 'linear-gradient(180deg, #f4f9ff 0%, #ffffff 100%)',
        padding: '80px 20px',
        fontFamily: "'DM Sans', sans-serif",
        color: '#1a2a4a',
      }}
    >
      {/* CSS Responsive */}
      <style>{`
        .cockpit-main-card {
          display: grid;
          grid-template-columns: 1.1fr 1fr;
          gap: 38px;
        }
        .cockpit-offers-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 22px;
        }
        .cockpit-benefits-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .cockpit-final-banner-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }
        .cockpit-info-row {
          display: flex;
          justify-content: center;
          gap: 60px;
        }
        @media (max-width: 1024px) {
          .cockpit-main-card { grid-template-columns: 1fr; }
          .cockpit-offers-grid { grid-template-columns: 1fr; }
          .cockpit-benefits-grid { grid-template-columns: 1fr; }
          .cockpit-final-banner-grid { grid-template-columns: 1fr; text-align: center; }
          .cockpit-info-row { flex-direction: column; gap: 16px; align-items: center; }
        }
      `}</style>

      <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
        
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 20px',
            borderRadius: 999,
            background: '#eaf3ff',
            color: '#1264e8',
            fontWeight: 700,
            marginBottom: 20,
            border: '1px solid #bfd8ff',
            fontSize: 13,
          }}
        >
          🚀 SERVICE CLÉ EN MAIN
        </motion.div>

        {/* Titre */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 'clamp(36px, 5vw, 56px)',
            lineHeight: 1.1,
            margin: 0,
            fontWeight: 800,
            letterSpacing: '-0.02em',
          }}
        >
          Tu arrives. <span style={{ color: '#1264e8' }}>Ton entreprise tourne déjà.</span>
        </motion.h2>

        {/* Sous-titre */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          style={{
            fontSize: 'clamp(16px, 2vw, 18px)',
            lineHeight: 1.5,
            margin: '16px auto 40px',
            maxWidth: 650,
            color: 'rgba(26, 42, 74, 0.7)',
            fontWeight: 500,
          }}
        >
          On s'occupe de tout.<br />
          Ton cockpit est prêt à piloter.
        </motion.p>

        {/* Grande carte centrale */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="cockpit-main-card"
          style={{
            background: 'white',
            border: '1px solid #cfe2ff',
            borderRadius: 28,
            boxShadow: '0 30px 90px rgba(18, 100, 232, 0.14)',
            padding: 38,
            alignItems: 'center',
            textAlign: 'left',
            marginBottom: 28,
          }}
        >
          {/* Dashboard Mockup */}
          <div style={{
            background: 'linear-gradient(135deg, #eef6ff, #ffffff)',
            borderRadius: 24,
            padding: 28,
            border: '1px solid #d8e9ff',
            minHeight: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              width: '100%',
              maxWidth: 520,
              background: 'white',
              borderRadius: 22,
              boxShadow: '0 20px 60px rgba(26, 42, 74, 0.12)',
              padding: 26,
            }}>
              <div style={{ fontWeight: 800, color: '#1a2a4a', marginBottom: 14, fontSize: 18 }}>
                Mon cockpit
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 14,
                marginBottom: 18,
              }}>
                <div style={{ background: '#f4f9ff', borderRadius: 14, padding: 16, fontSize: 14 }}>
                  Chiffre d'affaires<br /><strong style={{ fontSize: 20 }}>128 450€</strong>
                </div>
                <div style={{ background: '#f4f9ff', borderRadius: 14, padding: 16, fontSize: 14 }}>
                  Chantiers actifs<br /><strong style={{ fontSize: 20 }}>12</strong>
                </div>
                <div style={{ background: '#f4f9ff', borderRadius: 14, padding: 16, fontSize: 14 }}>
                  Clients<br /><strong style={{ fontSize: 20 }}>356</strong>
                </div>
                <div style={{ background: '#f4f9ff', borderRadius: 14, padding: 16, fontSize: 14 }}>
                  Rentabilité<br /><strong style={{ fontSize: 20 }}>26,6%</strong>
                </div>
              </div>
              <div style={{
                background: '#e9fff3',
                color: '#15803d',
                display: 'inline-block',
                padding: '6px 12px',
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 13,
              }}>
                Actif
              </div>
            </div>
          </div>

          {/* Installation info */}
          <div>
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(26px, 3vw, 34px)',
              lineHeight: 1.1,
              margin: '0 0 22px',
              fontWeight: 800,
            }}>
              Installation clé en main
            </h3>

            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 28px',
              fontSize: 18,
              lineHeight: 1.9,
              fontWeight: 600,
            }}>
              <li>✅ Cockpit configuré et personnalisé</li>
              <li>✅ Modules activés (chantier, client, gestion)</li>
              <li>✅ Agents IA opérationnels</li>
              <li>✅ Automatisations actives</li>
              <li>✅ Paiements et suivi connectés</li>
            </ul>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              background: '#eaf3ff',
              color: '#1264e8',
              border: '1px solid #bfd8ff',
              borderRadius: 16,
              padding: '16px 24px',
              fontSize: 24,
              fontWeight: 900,
            }}>
              📅 LIVRÉ EN 7 JOURS
            </div>
          </div>
        </motion.div>

        {/* 3 Cartes Offres */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="cockpit-offers-grid"
          style={{ marginBottom: 26, textAlign: 'left' }}
        >
          {/* PRO */}
          <div style={{
            background: 'white',
            border: '1px solid #cfe2ff',
            borderRadius: 22,
            padding: 28,
            boxShadow: '0 18px 50px rgba(18, 100, 232, 0.10)',
          }}>
            <h3 style={{ color: '#1264e8', fontSize: 30, margin: 0, fontWeight: 900 }}>PRO</h3>
            <p style={{ margin: '4px 0 20px', fontWeight: 700 }}>Installation clé en main</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', lineHeight: 1.8, fontWeight: 600, fontSize: 15 }}>
              <li>✅ Cockpit configuré</li>
              <li>✅ Modules essentiels</li>
              <li>✅ Assistant IA inclus</li>
              <li>✅ Suivi chantier</li>
              <li>✅ Espace client</li>
            </ul>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderLeft: '1px solid #d8e9ff',
              paddingLeft: 20,
              margin: '20px 0',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Paiement unique</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ textDecoration: 'line-through', color: '#7a8495', fontSize: 24 }}>500€</div>
                <div style={{ color: '#1264e8', fontSize: 48, fontWeight: 900 }}>250€</div>
              </div>
            </div>
            <div style={{
              background: '#eaf3ff',
              color: '#1264e8',
              borderRadius: 999,
              display: 'inline-block',
              padding: '6px 14px',
              fontWeight: 800,
              marginBottom: 18,
              fontSize: 12,
            }}>
              OFFRE FONDATEUR
            </div>
            <button style={{
              width: '100%',
              border: 'none',
              borderRadius: 14,
              background: '#1264e8',
              color: 'white',
              padding: 16,
              fontSize: 18,
              fontWeight: 900,
              cursor: 'pointer',
            }}>
              Choisir Pro
            </button>
          </div>

          {/* BUSINESS - Mise en avant */}
          <div style={{
            background: 'white',
            border: '2px solid #E8650A',
            borderRadius: 22,
            padding: 28,
            boxShadow: '0 26px 70px rgba(232, 101, 10, 0.18)',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              top: -18,
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#E8650A',
              color: 'white',
              borderRadius: 999,
              padding: '9px 22px',
              fontWeight: 900,
              fontSize: 13,
              whiteSpace: 'nowrap',
            }}>
              ⭐ LE PLUS CHOISI
            </div>
            <h3 style={{ color: '#E8650A', fontSize: 30, margin: 0, fontWeight: 900, marginTop: 8 }}>BUSINESS</h3>
            <p style={{ margin: '4px 0 20px', fontWeight: 700 }}>Système complet</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', lineHeight: 1.8, fontWeight: 600, fontSize: 15 }}>
              <li>✅ Tout PRO</li>
              <li>✅ 4 agents IA</li>
              <li>✅ Automatisation avancée</li>
              <li>✅ Gestion complète</li>
              <li>✅ Optimisation</li>
            </ul>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderLeft: '1px solid #fce4d4',
              paddingLeft: 20,
              margin: '20px 0',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Paiement unique</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ textDecoration: 'line-through', color: '#7a8495', fontSize: 24 }}>1000€</div>
                <div style={{ color: '#E8650A', fontSize: 48, fontWeight: 900 }}>500€</div>
              </div>
            </div>
            <div style={{
              background: '#fce4d4',
              color: '#E8650A',
              borderRadius: 999,
              display: 'inline-block',
              padding: '6px 14px',
              fontWeight: 800,
              marginBottom: 18,
              fontSize: 12,
            }}>
              OFFRE FONDATEUR
            </div>
            <button style={{
              width: '100%',
              border: 'none',
              borderRadius: 14,
              background: '#E8650A',
              color: 'white',
              padding: 16,
              fontSize: 18,
              fontWeight: 900,
              cursor: 'pointer',
            }}>
              Choisir Business
            </button>
          </div>

          {/* PREMIUM */}
          <div style={{
            background: 'white',
            border: '1px solid #dec8ff',
            borderRadius: 22,
            padding: 28,
            boxShadow: '0 18px 50px rgba(124, 58, 237, 0.12)',
          }}>
            <h3 style={{ color: '#7c3aed', fontSize: 30, margin: 0, fontWeight: 900 }}>PREMIUM</h3>
            <p style={{ margin: '4px 0 20px', fontWeight: 700 }}>Machine complète</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', lineHeight: 1.8, fontWeight: 600, fontSize: 15 }}>
              <li>✅ Tout BUSINESS</li>
              <li>✅ 12 agents IA</li>
              <li>✅ Équipe complète</li>
              <li>✅ Pilotage total</li>
              <li>✅ Accompagnement prioritaire</li>
            </ul>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderLeft: '1px solid #eadcff',
              paddingLeft: 20,
              margin: '20px 0',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Paiement unique</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ textDecoration: 'line-through', color: '#7a8495', fontSize: 24 }}>1500€</div>
                <div style={{ color: '#7c3aed', fontSize: 48, fontWeight: 900 }}>750€</div>
              </div>
            </div>
            <div style={{
              background: '#eadcff',
              color: '#7c3aed',
              borderRadius: 999,
              display: 'inline-block',
              padding: '6px 14px',
              fontWeight: 800,
              marginBottom: 18,
              fontSize: 12,
            }}>
              OFFRE FONDATEUR
            </div>
            <button style={{
              width: '100%',
              border: 'none',
              borderRadius: 14,
              background: '#7c3aed',
              color: 'white',
              padding: 16,
              fontSize: 18,
              fontWeight: 900,
              cursor: 'pointer',
            }}>
              Choisir Premium
            </button>
          </div>
        </motion.div>

        {/* Bandeau 3 bénéfices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="cockpit-benefits-grid"
          style={{
            background: 'white',
            border: '1px solid #cfe2ff',
            borderRadius: 22,
            padding: 20,
            marginBottom: 20,
            boxShadow: '0 18px 50px rgba(18, 100, 232, 0.08)',
            textAlign: 'center',
          }}
        >
          <div style={{ padding: '10px 0' }}>
            <strong style={{ fontSize: 16 }}>🕒 Livré en 7 jours</strong><br />
            <span style={{ color: 'rgba(26, 42, 74, 0.7)', fontSize: 14 }}>Ton cockpit prêt à l'emploi</span>
          </div>
          <div style={{ padding: '10px 0' }}>
            <strong style={{ fontSize: 16 }}>🛡️ Zéro prise de tête</strong><br />
            <span style={{ color: 'rgba(26, 42, 74, 0.7)', fontSize: 14 }}>On s'occupe de tout</span>
          </div>
          <div style={{ padding: '10px 0' }}>
            <strong style={{ fontSize: 16 }}>🎯 Prêt à piloter</strong><br />
            <span style={{ color: 'rgba(26, 42, 74, 0.7)', fontSize: 14 }}>Tu n'as plus qu'à te concentrer sur tes chantiers</span>
          </div>
        </motion.div>

        {/* Bandeau vert fondateur final */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="cockpit-final-banner-grid"
          style={{
            background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
            color: 'white',
            borderRadius: 20,
            padding: '24px 32px',
            alignItems: 'center',
            textAlign: 'left',
            boxShadow: '0 22px 70px rgba(21, 128, 61, 0.25)',
          }}
        >
          <div style={{ fontSize: 'clamp(18px, 2vw, 24px)', fontWeight: 900 }}>
            🏆 OFFRE RÉSERVÉE AUX 100 PREMIERS<br />
            <span style={{ color: '#fef08a' }}>MEMBRES FONDATEURS</span>
          </div>
          <div style={{ fontSize: 'clamp(16px, 2vw, 22px)', fontWeight: 900 }}>
            🔒 TARIF FONDATEUR BLOQUÉ À VIE<br />
            <span style={{ fontSize: 15, fontWeight: 600, color: 'rgba(255, 255, 255, 0.78)' }}>
              Ton tarif ne changera jamais tant que ton abonnement est actif.
            </span>
          </div>
        </motion.div>

        {/* Ligne d'info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
          className="cockpit-info-row"
          style={{
            marginTop: 24,
            fontWeight: 700,
            color: '#1a2a4a',
            fontSize: 15,
          }}
        >
          <div>🛡️ Paiement sécurisé</div>
          <div>🎧 Support humain 7j/7</div>
          <div>∞ 0% commission — tu gardes 100%</div>
        </motion.div>

      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION CLOSING - CONVERSION FINALE
// ═══════════════════════════════════════════════════════════════════════════════
const ClosingSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email.trim()) {
      setError('Entre ton email pour commencer');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Email invalide');
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1200));
    
    setIsSubmitting(false);
    setIsSuccess(true);
    
    // Reset after 3 seconds
    setTimeout(() => {
      setIsSuccess(false);
      setEmail('');
    }, 3000);
  };

  return (
    <section className="closing-section">
      <style>{`
        .closing-section {
          width: 100%;
          background: #FFFFFF;
          padding: 120px 40px;
          font-family: 'Inter', 'DM Sans', sans-serif;
        }
        .closing-container {
          max-width: 1300px;
          margin: 0 auto;
        }
        
        /* TOP SECTION - 2 COLUMNS */
        .closing-top {
          display: grid;
          grid-template-columns: 1fr 1.5fr;
          gap: 80px;
          align-items: flex-start;
          margin-bottom: 60px;
        }
        
        /* LEFT COLUMN */
        .closing-left {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .closing-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #FFF4ED;
          color: #F97316;
          border-radius: 999px;
          padding: 6px 14px;
          font-size: 13px;
          font-weight: 600;
          width: fit-content;
        }
        .closing-badge svg {
          width: 16px;
          height: 16px;
        }
        .closing-title {
          font-size: 42px;
          font-weight: 700;
          line-height: 1.2;
          color: #1E293B;
          margin: 0;
        }
        .closing-title span {
          color: #F97316;
          display: block;
        }
        .closing-subtitle {
          font-size: 18px;
          color: #64748B;
          line-height: 1.6;
          margin: 0;
        }
        
        /* BENEFITS */
        .closing-benefits {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 8px;
        }
        .closing-benefit {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 16px;
          display: flex;
          gap: 12px;
          align-items: flex-start;
          transition: box-shadow 0.2s ease;
        }
        .closing-benefit:hover {
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        }
        .closing-benefit-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .closing-benefit-icon.blue { background: #EEF2FF; color: #3B82F6; }
        .closing-benefit-icon.green { background: #ECFDF5; color: #22C55E; }
        .closing-benefit-icon.purple { background: #F3E8FF; color: #A855F7; }
        .closing-benefit-icon.orange { background: #FFF4ED; color: #F97316; }
        .closing-benefit-icon.teal { background: #F0FDFA; color: #14B8A6; }
        .closing-benefit-text h4 {
          font-size: 15px;
          font-weight: 600;
          color: #1E293B;
          margin: 0 0 4px 0;
        }
        .closing-benefit-text p {
          font-size: 13px;
          color: #64748B;
          margin: 0;
          line-height: 1.4;
        }
        
        /* RIGHT COLUMN - DASHBOARD + MOBILE */
        .closing-right {
          position: relative;
          display: flex;
          gap: 20px;
        }
        
        /* DASHBOARD MOCKUP */
        .closing-dashboard {
          flex: 1;
          background: #FFFFFF;
          border-radius: 20px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.08);
          overflow: hidden;
        }
        .closing-dash-header {
          display: flex;
          background: #1E3A5F;
          color: #FFFFFF;
        }
        .closing-dash-sidebar {
          width: 140px;
          background: #1E3A5F;
          padding: 16px 12px;
          flex-shrink: 0;
        }
        .closing-dash-logo {
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 4px;
        }
        .closing-dash-logo span {
          font-size: 10px;
          font-weight: 400;
          color: rgba(255,255,255,0.6);
          display: block;
        }
        .closing-dash-nav {
          display: flex;
          flex-direction: column;
          gap: 2px;
          margin-top: 16px;
        }
        .closing-nav-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          border-radius: 6px;
          font-size: 12px;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          transition: all 0.2s;
        }
        .closing-nav-item:hover {
          background: rgba(255,255,255,0.1);
        }
        .closing-nav-item.active {
          background: rgba(255,255,255,0.15);
          color: #FFFFFF;
        }
        .closing-nav-item svg {
          width: 14px;
          height: 14px;
        }
        
        /* DASHBOARD CONTENT */
        .closing-dash-content {
          flex: 1;
          background: #F8FAFC;
          padding: 16px;
        }
        .closing-dash-topbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .closing-dash-greeting {
          display: flex;
          flex-direction: column;
        }
        .closing-dash-greeting span {
          font-size: 12px;
          color: #64748B;
        }
        .closing-dash-greeting strong {
          font-size: 16px;
          color: #1E293B;
        }
        .closing-dash-greeting small {
          font-size: 11px;
          color: #94A3B8;
        }
        .closing-dash-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .closing-dash-date {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: #64748B;
          background: #FFFFFF;
          padding: 6px 10px;
          border-radius: 6px;
        }
        .closing-dash-export {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #64748B;
          background: #FFFFFF;
          padding: 6px 10px;
          border-radius: 6px;
        }
        .closing-dash-user {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .closing-dash-user-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          object-fit: cover;
        }
        .closing-dash-user-info {
          font-size: 11px;
        }
        .closing-dash-user-info strong {
          display: block;
          color: #1E293B;
        }
        .closing-dash-user-info span {
          color: #94A3B8;
        }
        
        /* KPIs */
        .closing-dash-kpis {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
          margin-bottom: 14px;
        }
        .closing-dash-kpi {
          background: #FFFFFF;
          border-radius: 10px;
          padding: 12px;
        }
        .closing-dash-kpi-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }
        .closing-dash-kpi-label {
          font-size: 10px;
          color: #64748B;
        }
        .closing-dash-kpi-icon {
          width: 24px;
          height: 24px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .closing-dash-kpi-icon.blue { background: #EEF2FF; color: #3B82F6; }
        .closing-dash-kpi-icon.green { background: #ECFDF5; color: #22C55E; }
        .closing-dash-kpi-icon.purple { background: #F3E8FF; color: #A855F7; }
        .closing-dash-kpi-icon.orange { background: #FFF4ED; color: #F97316; }
        .closing-dash-kpi-icon svg {
          width: 12px;
          height: 12px;
        }
        .closing-dash-kpi-value {
          font-size: 18px;
          font-weight: 700;
          color: #1E293B;
          margin-bottom: 2px;
        }
        .closing-dash-kpi-sub {
          font-size: 9px;
          color: #22C55E;
          font-weight: 500;
        }
        .closing-dash-kpi-sub.muted {
          color: #94A3B8;
        }
        
        /* CHARTS ROW */
        .closing-dash-charts {
          display: grid;
          grid-template-columns: 1.3fr 1fr;
          gap: 10px;
          margin-bottom: 14px;
        }
        .closing-dash-chart {
          background: #FFFFFF;
          border-radius: 10px;
          padding: 12px;
        }
        .closing-dash-chart-title {
          font-size: 11px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .closing-dash-chart-legend {
          display: flex;
          gap: 10px;
        }
        .closing-legend-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 9px;
          color: #64748B;
        }
        .closing-legend-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }
        .closing-legend-dot.blue { background: #3B82F6; }
        .closing-legend-dot.green { background: #22C55E; }
        .closing-chart-visual {
          height: 70px;
          position: relative;
        }
        .closing-chart-lines {
          position: absolute;
          inset: 0;
        }
        .closing-chart-lines svg {
          width: 100%;
          height: 100%;
        }
        .closing-chart-x {
          display: flex;
          justify-content: space-between;
          font-size: 8px;
          color: #94A3B8;
          margin-top: 4px;
        }
        
        /* DONUT CHART */
        .closing-donut-wrap {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .closing-donut-visual {
          width: 70px;
          height: 70px;
          position: relative;
        }
        .closing-donut-visual svg {
          width: 100%;
          height: 100%;
          transform: rotate(-90deg);
        }
        .closing-donut-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
        }
        .closing-donut-value {
          font-size: 16px;
          font-weight: 700;
          color: #1E293B;
        }
        .closing-donut-label {
          font-size: 8px;
          color: #64748B;
        }
        .closing-donut-legend {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .closing-donut-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 9px;
        }
        .closing-donut-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
        .closing-donut-dot.green { background: #22C55E; }
        .closing-donut-dot.blue { background: #3B82F6; }
        .closing-donut-dot.orange { background: #F97316; }
        .closing-donut-text span {
          color: #64748B;
        }
        .closing-donut-text strong {
          color: #1E293B;
          margin-left: 4px;
        }
        
        /* BOTTOM ROW - ACTIVITY + TESTIMONIAL */
        .closing-dash-bottom {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .closing-activity {
          background: #FFFFFF;
          border-radius: 10px;
          padding: 12px;
        }
        .closing-activity-title {
          font-size: 11px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 10px;
        }
        .closing-activity-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .closing-activity-item {
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }
        .closing-activity-icon {
          width: 24px;
          height: 24px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .closing-activity-icon.green { background: #ECFDF5; color: #22C55E; }
        .closing-activity-icon.blue { background: #EEF2FF; color: #3B82F6; }
        .closing-activity-icon.purple { background: #F3E8FF; color: #A855F7; }
        .closing-activity-icon.orange { background: #FFF4ED; color: #F97316; }
        .closing-activity-icon svg {
          width: 12px;
          height: 12px;
        }
        .closing-activity-content {
          flex: 1;
        }
        .closing-activity-name {
          font-size: 10px;
          font-weight: 600;
          color: #1E293B;
        }
        .closing-activity-desc {
          font-size: 9px;
          color: #94A3B8;
        }
        .closing-activity-time {
          font-size: 9px;
          color: #94A3B8;
          flex-shrink: 0;
        }
        
        /* TESTIMONIAL */
        .closing-testimonial {
          background: #FFFFFF;
          border-radius: 10px;
          padding: 12px;
        }
        .closing-testimonial-stars {
          display: flex;
          gap: 2px;
          margin-bottom: 8px;
        }
        .closing-testimonial-stars svg {
          width: 12px;
          height: 12px;
          color: #F97316;
          fill: #F97316;
        }
        .closing-testimonial-text {
          font-size: 10px;
          color: #64748B;
          line-height: 1.5;
          font-style: italic;
          margin-bottom: 10px;
        }
        .closing-testimonial-author {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .closing-testimonial-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          object-fit: cover;
        }
        .closing-testimonial-info {
          flex: 1;
        }
        .closing-testimonial-name {
          font-size: 11px;
          font-weight: 600;
          color: #1E293B;
        }
        .closing-testimonial-role {
          font-size: 9px;
          color: #94A3B8;
        }
        
        /* MOBILE MOCKUP */
        .closing-mobile {
          width: 160px;
          background: #FFFFFF;
          border-radius: 24px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.1);
          overflow: hidden;
          flex-shrink: 0;
        }
        .closing-mobile-header {
          background: #1E3A5F;
          padding: 8px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .closing-mobile-time {
          font-size: 10px;
          color: #FFFFFF;
          font-weight: 600;
        }
        .closing-mobile-icons {
          display: flex;
          gap: 4px;
        }
        .closing-mobile-icons svg {
          width: 12px;
          height: 12px;
          color: #FFFFFF;
        }
        .closing-mobile-topbar {
          background: #1E3A5F;
          padding: 8px 12px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .closing-mobile-logo {
          font-size: 12px;
          font-weight: 700;
          color: #FFFFFF;
        }
        .closing-mobile-logo span {
          font-size: 8px;
          font-weight: 400;
          color: rgba(255,255,255,0.6);
          display: block;
        }
        .closing-mobile-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          object-fit: cover;
        }
        .closing-mobile-content {
          padding: 12px;
          background: #F8FAFC;
        }
        .closing-mobile-title {
          font-size: 11px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 10px;
        }
        .closing-mobile-kpis {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .closing-mobile-kpi {
          background: #FFFFFF;
          border-radius: 8px;
          padding: 10px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .closing-mobile-kpi-left {
          display: flex;
          flex-direction: column;
        }
        .closing-mobile-kpi-label {
          font-size: 9px;
          color: #64748B;
        }
        .closing-mobile-kpi-value {
          font-size: 14px;
          font-weight: 700;
          color: #1E293B;
        }
        .closing-mobile-kpi-sub {
          font-size: 8px;
          color: #22C55E;
        }
        .closing-mobile-kpi-icon {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .closing-mobile-kpi-icon.blue { background: #EEF2FF; color: #3B82F6; }
        .closing-mobile-kpi-icon.green { background: #ECFDF5; color: #22C55E; }
        .closing-mobile-kpi-icon.purple { background: #F3E8FF; color: #A855F7; }
        .closing-mobile-kpi-icon.orange { background: #FFF4ED; color: #F97316; }
        .closing-mobile-kpi-icon svg {
          width: 14px;
          height: 14px;
        }
        .closing-mobile-nav {
          display: flex;
          justify-content: space-around;
          padding: 10px 8px;
          background: #FFFFFF;
          border-top: 1px solid #E5E7EB;
        }
        .closing-mobile-nav-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          font-size: 8px;
          color: #94A3B8;
        }
        .closing-mobile-nav-item.active {
          color: #F97316;
        }
        .closing-mobile-nav-item svg {
          width: 16px;
          height: 16px;
        }
        
        /* CTA ZONE */
        .closing-cta-zone {
          background: #F8FAFC;
          border-radius: 20px;
          padding: 40px;
          display: grid;
          grid-template-columns: 1.2fr 1fr auto;
          gap: 40px;
          align-items: center;
        }
        .closing-cta-left {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .closing-cta-title {
          font-size: 24px;
          font-weight: 700;
          color: #1E293B;
          margin: 0;
        }
        .closing-cta-title span {
          color: #F97316;
          display: block;
        }
        .closing-cta-bullets {
          display: flex;
          gap: 32px;
        }
        .closing-cta-bullet {
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }
        .closing-cta-bullet-check {
          width: 20px;
          height: 20px;
          background: #22C55E;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .closing-cta-bullet-check svg {
          width: 12px;
          height: 12px;
          color: #FFFFFF;
        }
        .closing-cta-bullet-text h5 {
          font-size: 14px;
          font-weight: 600;
          color: #1E293B;
          margin: 0;
        }
        .closing-cta-bullet-text p {
          font-size: 12px;
          color: #64748B;
          margin: 0;
        }
        
        /* CTA CENTER */
        .closing-cta-center {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }
        .closing-cta-headline {
          font-size: 18px;
          font-weight: 700;
          color: #1E293B;
          text-align: center;
          margin: 0;
        }
        .closing-cta-subline {
          font-size: 13px;
          color: #64748B;
          text-align: center;
          margin: 0;
        }
        .closing-cta-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #F97316;
          color: #FFFFFF;
          padding: 16px 28px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 16px;
          border: none;
          cursor: pointer;
          box-shadow: 0 10px 30px rgba(249,115,22,0.3);
          transition: all 0.2s;
        }
        .closing-cta-button:hover {
          background: #EA580C;
          transform: translateY(-2px);
          box-shadow: 0 14px 35px rgba(249,115,22,0.4);
        }
        
        /* EMAIL FORM */
        .closing-email-form {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          width: 100%;
          max-width: 400px;
        }
        .closing-email-input-wrap {
          display: flex;
          width: 100%;
          background: #FFFFFF;
          border: 2px solid #E5E7EB;
          border-radius: 14px;
          overflow: hidden;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
        }
        .closing-email-input-wrap:focus-within {
          border-color: #F97316;
          box-shadow: 0 4px 25px rgba(249,115,22,0.15);
        }
        .closing-email-input-wrap.error {
          border-color: #EF4444;
          animation: shake 0.4s ease;
        }
        .closing-email-input-wrap.success {
          border-color: #22C55E;
          background: #F0FDF4;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-5px); }
          40%, 80% { transform: translateX(5px); }
        }
        .closing-email-input {
          flex: 1;
          padding: 16px 20px;
          border: none;
          outline: none;
          font-size: 15px;
          color: #1E293B;
          background: transparent;
        }
        .closing-email-input::placeholder {
          color: #94A3B8;
        }
        .closing-email-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: #F97316;
          color: #FFFFFF;
          padding: 16px 24px;
          border: none;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        .closing-email-btn:hover:not(:disabled) {
          background: #EA580C;
        }
        .closing-email-btn:disabled {
          cursor: not-allowed;
          opacity: 0.9;
        }
        .closing-email-btn svg {
          transition: transform 0.2s ease;
        }
        .closing-email-btn:hover:not(:disabled) svg {
          transform: translateX(3px);
        }
        .closing-email-error {
          font-size: 13px;
          color: #EF4444;
          font-weight: 500;
        }
        
        /* SUCCESS STATE */
        .closing-email-success {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 24px;
          background: #F0FDF4;
          border: 2px solid #22C55E;
          border-radius: 14px;
          animation: successPop 0.4s ease;
        }
        @keyframes successPop {
          0% { transform: scale(0.9); opacity: 0; }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); opacity: 1; }
        }
        .closing-email-success-icon {
          width: 40px;
          height: 40px;
          background: #22C55E;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: checkBounce 0.5s ease 0.2s;
        }
        @keyframes checkBounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }
        .closing-email-success-icon svg {
          color: #FFFFFF;
        }
        .closing-email-success-text {
          display: flex;
          flex-direction: column;
        }
        .closing-email-success-text strong {
          font-size: 16px;
          color: #166534;
        }
        .closing-email-success-text span {
          font-size: 13px;
          color: #15803D;
        }
        
        /* LOADING SPINNER */
        .closing-spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #FFFFFF;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .closing-cta-social {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .closing-cta-avatars {
          display: flex;
        }
        .closing-cta-avatars img {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 2px solid #FFFFFF;
          margin-left: -8px;
          object-fit: cover;
        }
        .closing-cta-avatars img:first-child {
          margin-left: 0;
        }
        .closing-cta-stars {
          display: flex;
          gap: 2px;
        }
        .closing-cta-stars svg {
          width: 14px;
          height: 14px;
          color: #F97316;
          fill: #F97316;
        }
        .closing-cta-count {
          font-size: 12px;
          color: #64748B;
        }
        
        /* CTA RIGHT - BADGES */
        .closing-cta-badges {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .closing-cta-badge {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 12px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .closing-cta-badge-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .closing-cta-badge-icon.blue { background: #EEF2FF; color: #3B82F6; }
        .closing-cta-badge-icon.green { background: #ECFDF5; color: #22C55E; }
        .closing-cta-badge-icon.purple { background: #F3E8FF; color: #A855F7; }
        .closing-cta-badge-text h5 {
          font-size: 12px;
          font-weight: 600;
          color: #1E293B;
          margin: 0;
          text-transform: uppercase;
        }
        .closing-cta-badge-text p {
          font-size: 11px;
          color: #64748B;
          margin: 0;
        }
        
        /* RESPONSIVE */
        @media (max-width: 1200px) {
          .closing-top {
            grid-template-columns: 1fr;
            gap: 48px;
          }
          .closing-right {
            order: -1;
            flex-direction: column;
            align-items: center;
          }
          .closing-mobile {
            display: none;
          }
          .closing-cta-zone {
            grid-template-columns: 1fr;
            gap: 32px;
          }
          .closing-cta-bullets {
            flex-wrap: wrap;
            gap: 20px;
          }
          .closing-cta-badges {
            flex-direction: row;
            justify-content: center;
          }
        }
        @media (max-width: 900px) {
          .closing-section {
            padding: 80px 24px;
          }
          .closing-title {
            font-size: 32px;
          }
          .closing-dash-kpis {
            grid-template-columns: repeat(2, 1fr);
          }
          .closing-dash-charts {
            grid-template-columns: 1fr;
          }
          .closing-dash-bottom {
            grid-template-columns: 1fr;
          }
          .closing-cta-bullets {
            flex-direction: column;
          }
          .closing-cta-badges {
            flex-direction: column;
          }
        }
        @media (max-width: 600px) {
          .closing-cta-zone {
            padding: 24px;
          }
          .closing-cta-title {
            font-size: 20px;
          }
        }
      `}</style>
      
      <div className="closing-container">
        {/* TOP SECTION */}
        <div className="closing-top">
          {/* LEFT COLUMN */}
          <div className="closing-left">
            <div className="closing-badge">
              <Rocket size={16} />
              PRÊT À PASSER AU NIVEAU SUPÉRIEUR ?
            </div>
            
            <h2 className="closing-title">
              Le cockpit qui transforme ton entreprise.<br />
              <span>Et ta liberté.</span>
            </h2>
            
            <p className="closing-subtitle">
              Plus de clarté. Plus de rentabilité. Moins de stress.<br />
              Rejoins les entrepreneurs du bâtiment qui passent à la vitesse supérieure.
            </p>
            
            <div className="closing-benefits">
              <div className="closing-benefit">
                <div className="closing-benefit-icon blue">
                  <TrendingUp size={18} />
                </div>
                <div className="closing-benefit-text">
                  <h4>+30% de rentabilité en moyenne</h4>
                  <p>Des chiffres réels, constatés par nos clients.</p>
                </div>
              </div>
              
              <div className="closing-benefit">
                <div className="closing-benefit-icon orange">
                  <Clock size={18} />
                </div>
                <div className="closing-benefit-text">
                  <h4>Jusqu'à 32h gagnées chaque semaine</h4>
                  <p>Fini les tâches répétitives et chronophages.</p>
                </div>
              </div>
              
              <div className="closing-benefit">
                <div className="closing-benefit-icon purple">
                  <Heart size={18} />
                </div>
                <div className="closing-benefit-text">
                  <h4>Moins de stress, plus de sérénité</h4>
                  <p>Tout est sous contrôle, même à distance.</p>
                </div>
              </div>
              
              <div className="closing-benefit">
                <div className="closing-benefit-icon green">
                  <Users size={18} />
                </div>
                <div className="closing-benefit-text">
                  <h4>Des clients plus satisfaits</h4>
                  <p>Suivi transparent, communications fluides.</p>
                </div>
              </div>
              
              <div className="closing-benefit">
                <div className="closing-benefit-icon teal">
                  <TrendingUp size={18} />
                </div>
                <div className="closing-benefit-text">
                  <h4>Un business qui grandit durablement</h4>
                  <p>Des processus solides pour scaler sereinement.</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* RIGHT COLUMN - DASHBOARD + MOBILE */}
          <div className="closing-right">
            {/* DASHBOARD MOCKUP */}
            <div className="closing-dashboard">
              <div className="closing-dash-header">
                {/* SIDEBAR */}
                <div className="closing-dash-sidebar">
                  <div className="closing-dash-logo">
                    cockpit<span>BÂTIMENT</span>
                  </div>
                  <div className="closing-dash-nav">
                    <div className="closing-nav-item active"><Home size={14} /> Vue d'ensemble</div>
                    <div className="closing-nav-item"><HardHat size={14} /> Chantiers</div>
                    <div className="closing-nav-item"><CheckSquare size={14} /> Tâches</div>
                    <div className="closing-nav-item"><Users size={14} /> Équipes</div>
                    <div className="closing-nav-item"><Calendar size={14} /> Planning</div>
                    <div className="closing-nav-item"><FileText size={14} /> Documents</div>
                    <div className="closing-nav-item"><Euro size={14} /> Finances</div>
                    <div className="closing-nav-item"><Zap size={14} /> Automatisation</div>
                    <div className="closing-nav-item"><BarChart3 size={14} /> Rapports</div>
                    <div className="closing-nav-item"><Settings size={14} /> Paramètres</div>
                  </div>
                </div>
                
                {/* CONTENT */}
                <div className="closing-dash-content">
                  {/* TOPBAR */}
                  <div className="closing-dash-topbar">
                    <div className="closing-dash-greeting">
                      <span>Bonjour, 👋</span>
                      <strong>Jean Martin</strong>
                      <small>Voici l'activité de ton entreprise aujourd'hui.</small>
                    </div>
                    <div className="closing-dash-actions">
                      <div className="closing-dash-date">
                        <Calendar size={10} />
                        1 - 31 mai 2024
                      </div>
                      <div className="closing-dash-export">
                        <Download size={10} />
                        Exporter
                      </div>
                      <div className="closing-dash-user">
                        <Bell size={14} style={{color: '#64748B'}} />
                        <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face" className="closing-dash-user-avatar" alt="" />
                        <div className="closing-dash-user-info">
                          <strong>Jean Martin</strong>
                          <span>Admin</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* KPIs */}
                  <div className="closing-dash-kpis">
                    <div className="closing-dash-kpi">
                      <div className="closing-dash-kpi-header">
                        <span className="closing-dash-kpi-label">Chiffre d'affaires</span>
                        <div className="closing-dash-kpi-icon blue"><TrendingUp size={12} /></div>
                      </div>
                      <div className="closing-dash-kpi-value">128 450 €</div>
                      <div className="closing-dash-kpi-sub">+18% vs avril</div>
                    </div>
                    <div className="closing-dash-kpi">
                      <div className="closing-dash-kpi-header">
                        <span className="closing-dash-kpi-label">Marge moyenne</span>
                        <div className="closing-dash-kpi-icon green"><TrendingUp size={12} /></div>
                      </div>
                      <div className="closing-dash-kpi-value">26,6%</div>
                      <div className="closing-dash-kpi-sub">+3,2% vs avril</div>
                    </div>
                    <div className="closing-dash-kpi">
                      <div className="closing-dash-kpi-header">
                        <span className="closing-dash-kpi-label">Heures gagnées</span>
                        <div className="closing-dash-kpi-icon purple"><Clock size={12} /></div>
                      </div>
                      <div className="closing-dash-kpi-value">32h</div>
                      <div className="closing-dash-kpi-sub">+28h vs avril</div>
                    </div>
                    <div className="closing-dash-kpi">
                      <div className="closing-dash-kpi-header">
                        <span className="closing-dash-kpi-label">Chantiers actifs</span>
                        <div className="closing-dash-kpi-icon orange"><HardHat size={12} /></div>
                      </div>
                      <div className="closing-dash-kpi-value">12</div>
                      <div className="closing-dash-kpi-sub muted">En cours</div>
                    </div>
                  </div>
                  
                  {/* CHARTS */}
                  <div className="closing-dash-charts">
                    <div className="closing-dash-chart">
                      <div className="closing-dash-chart-title">
                        Évolution du CA
                        <div className="closing-dash-chart-legend">
                          <div className="closing-legend-item"><div className="closing-legend-dot blue"></div> CA</div>
                          <div className="closing-legend-item"><div className="closing-legend-dot green"></div> Marge</div>
                        </div>
                      </div>
                      <div className="closing-chart-visual">
                        <div className="closing-chart-lines">
                          <svg viewBox="0 0 200 60" preserveAspectRatio="none">
                            <path d="M0 50 Q30 45 50 40 T100 30 T150 25 T200 15" fill="none" stroke="#3B82F6" strokeWidth="2" />
                            <path d="M0 55 Q30 52 50 48 T100 42 T150 38 T200 32" fill="none" stroke="#22C55E" strokeWidth="2" />
                          </svg>
                        </div>
                      </div>
                      <div className="closing-chart-x">
                        <span>1 mai</span>
                        <span>8 mai</span>
                        <span>15 mai</span>
                        <span>22 mai</span>
                        <span>31 mai</span>
                      </div>
                    </div>
                    <div className="closing-dash-chart">
                      <div className="closing-dash-chart-title">Répartition des chantiers</div>
                      <div className="closing-donut-wrap">
                        <div className="closing-donut-visual">
                          <svg viewBox="0 0 36 36">
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#22C55E" strokeWidth="3" strokeDasharray="59 41" />
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#3B82F6" strokeWidth="3" strokeDasharray="25 75" strokeDashoffset="-59" />
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#F97316" strokeWidth="3" strokeDasharray="16 84" strokeDashoffset="-84" />
                          </svg>
                          <div className="closing-donut-center">
                            <div className="closing-donut-value">12</div>
                            <div className="closing-donut-label">chantiers</div>
                          </div>
                        </div>
                        <div className="closing-donut-legend">
                          <div className="closing-donut-item">
                            <div className="closing-donut-dot green"></div>
                            <div className="closing-donut-text"><span>En cours</span> <strong>7 (59%)</strong></div>
                          </div>
                          <div className="closing-donut-item">
                            <div className="closing-donut-dot blue"></div>
                            <div className="closing-donut-text"><span>En préparation</span> <strong>3 (25%)</strong></div>
                          </div>
                          <div className="closing-donut-item">
                            <div className="closing-donut-dot orange"></div>
                            <div className="closing-donut-text"><span>En attente</span> <strong>2 (17%)</strong></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* BOTTOM - ACTIVITY + TESTIMONIAL */}
                  <div className="closing-dash-bottom">
                    <div className="closing-activity">
                      <div className="closing-activity-title">Activité récente</div>
                      <div className="closing-activity-list">
                        <div className="closing-activity-item">
                          <div className="closing-activity-icon green"><FileText size={12} /></div>
                          <div className="closing-activity-content">
                            <div className="closing-activity-name">Compte rendu généré automatiquement</div>
                            <div className="closing-activity-desc">Villa Contemporaine</div>
                          </div>
                          <div className="closing-activity-time">Il y a 15 min</div>
                        </div>
                        <div className="closing-activity-item">
                          <div className="closing-activity-icon blue"><Mail size={12} /></div>
                          <div className="closing-activity-content">
                            <div className="closing-activity-name">Relance devis envoyée</div>
                            <div className="closing-activity-desc">Client Martin</div>
                          </div>
                          <div className="closing-activity-time">Il y a 45 min</div>
                        </div>
                        <div className="closing-activity-item">
                          <div className="closing-activity-icon purple"><CheckSquare size={12} /></div>
                          <div className="closing-activity-content">
                            <div className="closing-activity-name">Tâche terminée</div>
                            <div className="closing-activity-desc">Préparation dalle béton</div>
                          </div>
                          <div className="closing-activity-time">Il y a 1h</div>
                        </div>
                        <div className="closing-activity-item">
                          <div className="closing-activity-icon orange"><Receipt size={12} /></div>
                          <div className="closing-activity-content">
                            <div className="closing-activity-name">Facture payée</div>
                            <div className="closing-activity-desc">Facture FA-2024-057</div>
                          </div>
                          <div className="closing-activity-time">Il y a 2h</div>
                        </div>
                      </div>
                    </div>
                    <div className="closing-testimonial">
                      <div className="closing-testimonial-stars">
                        <Star size={12} />
                        <Star size={12} />
                        <Star size={12} />
                        <Star size={12} />
                        <Star size={12} />
                      </div>
                      <p className="closing-testimonial-text">
                        « Cockpit a changé ma façon de gérer mes chantiers et mon entreprise. Plus de visibilité, plus de marge, et enfin du temps pour moi. »
                      </p>
                      <div className="closing-testimonial-author">
                        <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face" className="closing-testimonial-avatar" alt="" />
                        <div className="closing-testimonial-info">
                          <div className="closing-testimonial-name">Thomas L.</div>
                          <div className="closing-testimonial-role">Entrepreneur de maçonnerie • 18 chantiers actifs</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* MOBILE MOCKUP */}
            <div className="closing-mobile">
              <div className="closing-mobile-header">
                <span className="closing-mobile-time">9:41</span>
                <div className="closing-mobile-icons">
                  <Activity size={12} />
                  <Bell size={12} />
                </div>
              </div>
              <div className="closing-mobile-topbar">
                <div className="closing-mobile-logo">
                  Cockpit<span>BÂTIMENT</span>
                </div>
                <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face" className="closing-mobile-avatar" alt="" />
              </div>
              <div className="closing-mobile-content">
                <div className="closing-mobile-title">Vue d'ensemble</div>
                <div className="closing-mobile-kpis">
                  <div className="closing-mobile-kpi">
                    <div className="closing-mobile-kpi-left">
                      <span className="closing-mobile-kpi-label">Chiffre d'affaires</span>
                      <span className="closing-mobile-kpi-value">128 450 €</span>
                      <span className="closing-mobile-kpi-sub">+18% vs avril</span>
                    </div>
                    <div className="closing-mobile-kpi-icon blue"><TrendingUp size={14} /></div>
                  </div>
                  <div className="closing-mobile-kpi">
                    <div className="closing-mobile-kpi-left">
                      <span className="closing-mobile-kpi-label">Marge moyenne</span>
                      <span className="closing-mobile-kpi-value">26,6%</span>
                      <span className="closing-mobile-kpi-sub">+3,2% vs avril</span>
                    </div>
                    <div className="closing-mobile-kpi-icon green"><TrendingUp size={14} /></div>
                  </div>
                  <div className="closing-mobile-kpi">
                    <div className="closing-mobile-kpi-left">
                      <span className="closing-mobile-kpi-label">Heures gagnées</span>
                      <span className="closing-mobile-kpi-value">32h</span>
                      <span className="closing-mobile-kpi-sub">+28h vs avril</span>
                    </div>
                    <div className="closing-mobile-kpi-icon purple"><Clock size={14} /></div>
                  </div>
                  <div className="closing-mobile-kpi">
                    <div className="closing-mobile-kpi-left">
                      <span className="closing-mobile-kpi-label">Chantiers actifs</span>
                      <span className="closing-mobile-kpi-value">12</span>
                      <span className="closing-mobile-kpi-sub" style={{color: '#94A3B8'}}>En cours</span>
                    </div>
                    <div className="closing-mobile-kpi-icon orange"><HardHat size={14} /></div>
                  </div>
                </div>
              </div>
              <div className="closing-mobile-nav">
                <div className="closing-mobile-nav-item active"><Home size={16} /> Accueil</div>
                <div className="closing-mobile-nav-item"><HardHat size={16} /> Chantiers</div>
                <div className="closing-mobile-nav-item"><CheckSquare size={16} /> Tâches</div>
                <div className="closing-mobile-nav-item"><Settings size={16} /> Plus</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* CTA ZONE */}
        <div className="closing-cta-zone">
          {/* LEFT */}
          <div className="closing-cta-left">
            <h3 className="closing-cta-title">
              Commence aujourd'hui.<br />
              <span>Transforme ton business dès maintenant.</span>
            </h3>
            <div className="closing-cta-bullets">
              <div className="closing-cta-bullet">
                <div className="closing-cta-bullet-check"><Check size={12} /></div>
                <div className="closing-cta-bullet-text">
                  <h5>Mise en route rapide</h5>
                  <p>Accès en 24h</p>
                </div>
              </div>
              <div className="closing-cta-bullet">
                <div className="closing-cta-bullet-check"><Check size={12} /></div>
                <div className="closing-cta-bullet-text">
                  <h5>Formation & accompagnement</h5>
                  <p>On t'aide à chaque étape</p>
                </div>
              </div>
              <div className="closing-cta-bullet">
                <div className="closing-cta-bullet-check"><Check size={12} /></div>
                <div className="closing-cta-bullet-text">
                  <h5>Sans engagement</h5>
                  <p>Annule quand tu veux</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* CENTER */}
          <div className="closing-cta-center">
            <h4 className="closing-cta-headline">Teste Cockpit gratuitement pendant 14 jours.</h4>
            <p className="closing-cta-subline">Sans carte bancaire. Sans engagement.</p>
            
            {isSuccess ? (
              <div className="closing-email-success">
                <div className="closing-email-success-icon">
                  <Check size={24} />
                </div>
                <div className="closing-email-success-text">
                  <strong>C'est parti !</strong>
                  <span>Vérifie ta boîte mail pour accéder à ton cockpit</span>
                </div>
              </div>
            ) : (
              <form className="closing-email-form" onSubmit={handleSubmit}>
                <div className={`closing-email-input-wrap ${error ? 'error' : ''}`}>
                  <input
                    type="email"
                    className="closing-email-input"
                    placeholder="Ton adresse email pro"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    disabled={isSubmitting}
                  />
                  <button 
                    type="submit" 
                    className="closing-email-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="closing-spinner"></div>
                        Envoi...
                      </>
                    ) : (
                      <>
                        DÉMARRER
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
                {error && <span className="closing-email-error">{error}</span>}
              </form>
            )}
            
            <div className="closing-cta-social">
              <div className="closing-cta-avatars">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face" alt="" />
                <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face" alt="" />
                <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&h=60&fit=crop&crop=face" alt="" />
                <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face" alt="" />
              </div>
              <div className="closing-cta-stars">
                <Star size={14} />
                <Star size={14} />
                <Star size={14} />
                <Star size={14} />
                <Star size={14} />
              </div>
            </div>
            <span className="closing-cta-count">Rejoint par +1 200 entrepreneurs du bâtiment</span>
          </div>
          
          {/* RIGHT - BADGES */}
          <div className="closing-cta-badges">
            <div className="closing-cta-badge">
              <div className="closing-cta-badge-icon blue">
                <MapPin size={18} />
              </div>
              <div className="closing-cta-badge-text">
                <h5>HÉBERGÉ EN FRANCE</h5>
                <p>Données sécurisées</p>
              </div>
            </div>
            <div className="closing-cta-badge">
              <div className="closing-cta-badge-icon green">
                <Shield size={18} />
              </div>
              <div className="closing-cta-badge-text">
                <h5>SÉCURITÉ GARANTIE</h5>
                <p>RGPD COMPLIANT</p>
              </div>
            </div>
            <div className="closing-cta-badge">
              <div className="closing-cta-badge-icon purple">
                <Headphones size={18} />
              </div>
              <div className="closing-cta-badge-text">
                <h5>SUPPORT 5 JOURS / 7</h5>
                <p>Réponse sous 24h</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════════════
export default function EspaceProCRMPage() {
  return (
    <ImmersiveEspaceProWrapper>
      <GlobalStyles />
      <UnifiedHeader />
      <EspaceProNav />
      <EspaceProSectionsNav />
      <EspaceProSectionsNavSpacer />
      
      <main>
        <div id="hero">
          <CockpitHeroSection />
        </div>
        <div id="features">
          <CockpitBlock2 />
        </div>
        <FounderBanner />
        <div id="collaboration">
          <CollaborationSection />
        </div>
        <div id="system">
          <SystemSection />
        </div>
        <div id="voice">
          <VoiceSection />
        </div>
        <div id="portal">
          <PortalSection />
        </div>
        <div id="couts-marges">
          <CoutsMargesSection />
        </div>
        <div id="automation">
          <AutomationSection />
        </div>
        <div id="closing">
          <ClosingSection />
        </div>
        <div id="tarifs-cockpit-wrapper">
          <CockpitPricingSection />
        </div>
      </main>
      
      
    </ImmersiveEspaceProWrapper>
  );
}
